{
  "agent": {
    "title": "AI Agent",
    "common_intents": {
      "tooltip": "Based on your recently received messages.",
      "loading": "Analyzing recent messages…"
    },
    "active_status": {
      "all_messages": "Agent active for all messages",
      "specific_messages": "Agent filtered by intent",
      "automation_only": "Agent active only in automations",
      "never_reply": "Agent disabled",
      "chat_with_agent": "Chat with Agent"
    },
    "messages_modal": {
      "button": "Message history",
      "title": "Agent message history",
      "description": "Recent messages sent by the AI agent to your contacts.",
      "empty": "No messages sent by the agent yet."
    },
    "welcome": {
      "title": "Convert leads automatically with AI-powered conversations",
      "subtitle": "Get started in minutes and start turning conversations into sales automatically.",
      "voice_title": "Define a tone of voice",
      "voice_description": "Set the tone — let the agent learn from your voice and speak with your style.",
      "train_title": "Train your agent",
      "train_description": "Provide clear goals and instructions then upload your knowledge to build the smartest agent.",
      "actions_title": "Set up actions",
      "actions_description": "Allow your agent to send links, organise folders and trigger any other automation autonomously.",
      "setup_button": "Set up your AI Agent",
      "no_additional_cost": "No additional cost. Included in the Pro plan.",
      "mcp_prompt_title": "Using Claude or ChatGPT?",
      "mcp_prompt_description": "Connect our MCP server and let it set up your agent."
    },
    "test": {
      "title": "Test your AI Agent during configuration",
      "description": "As you go along, you'll get a sneak peek of your changes and can chat with your agent anytime!"
    },
    "info": {
      "what": {
        "title": "What is an AI Agent?",
        "subtitle": "Learn about your AI Agent and how it can help you.",
        "description": [
          "An AI Agent is a virtual assistant that can automatically **reply to your Instagram DMs** using artificial intelligence.",
          "With an AI Agent, you can provide instant responses to your contacts, answer common questions, and engage with your audience 24/7.",
          "It can help you engage with your audience, **provide customer support, and drive conversions.**"
        ]
      },
      "how": {
        "title": "How does it work?",
        "subtitle": "Learn how your AI Agent knows what to say and how to respond.",
        "description": [
          "Your Agent uses AI models to understand and respond to messages **in a natural way.**",
          "It can be customized with your **personality and tone of voice**.",
          "You can **train your Agent with data**, such as FAQs, documents, or any relevant content to improve its replies.",
          "You can also **connect actions** to your Agent, allowing it to send links, add contacts to folders, or trigger any automation"
        ]
      },
      "automations": {
        "title": "What about automations?",
        "subtitle": "Learn how your AI Agent can work alongside your existing automations.",
        "description": [
          "Your Agent works alongside your **existing automations seamlessly.**",
          "When you receive a message, the Agent first **checks if any of your automation scenarios is set up to reply.**",
          "If no automation is triggered, the Agent will step in to reply based on its training and personality.",
          "It will also step in **if contacts leave the automation flow** and will reply to any follow-up messages."
        ]
      }
    },
    "feedback": {
      "title": "Improve your agent",
      "label": "What should change?",
      "placeholder": "e.g. Shipping takes a week now, and don't use exclamation marks",
      "suggest": "Suggest changes",
      "applied": "Agent updated",
      "before": "Before",
      "after": "After",
      "improve": "Improve reply",
      "ops": {
        "add_rule": "New {{kind}}",
        "edit_rule": "Edit rule",
        "delete_rule": "Delete rule",
        "set_about": "Change About",
        "set_goal": "Change Goal",
        "add_example": "Add message example",
        "remove_example": "Remove message example"
      },
      "kinds": {
        "voice": "voice rule",
        "instruction": "instruction"
      },
      "apply": "Apply changes",
      "empty_value": "Can't be empty"
    },
    "setup": {
      "title": "Set up your AI Agent",
      "description": "Follow these steps to configure your AI Agent and get it ready to reply to your contacts."
    },
    "categories": {
      "behaviour": "Behaviour and personality",
      "capabilities": "Capabilities and actions",
      "knowledge": "Knowledge"
    },
    "personality": {
      "title": "Voice and personality",
      "description": "Let the agent speak with your own voice and style.",
      "analyzing_personality": "Analyzing personality…",
      "personality_analyzed": "Personality analyzed",
      "message_length": {
        "label": "Message length",
        "options": {
          "10": "Short",
          "25": "Normal",
          "50": "Long"
        },
        "words_one": "~{{count}} word",
        "words_other": "~{{count}} words"
      },
      "message_examples": {
        "label": "Message examples",
        "description": "Real messages your agent should sound like.",
        "placeholder": "e.g. Thanks for reaching out! How can I help you today? 😊",
        "add_button": "Add a message example"
      },
      "auto_like": "Auto-like positive messages ❤️",
      "analysis_error": "Error analyzing personality",
      "about": {
        "label": "About",
        "description": "Who you are, what you offer and who you talk to.",
        "placeholder": "e.g. We're a small workshop in Mérida making custom tote bags for brands, hotels and events across Mexico."
      },
      "voice_rules": {
        "label": "Voice rules",
        "description": "How your agent writes, one rule each.",
        "placeholder": "e.g. Never use exclamation marks",
        "add_button": "Add a rule"
      },
      "sections": {
        "profile": {
          "title": "Profile",
          "description": "Who your agent speaks for and how much it writes."
        }
      }
    },
    "account_data": {
      "title": "Goal and instructions",
      "description": "Increase conversion with clearly defined goals.",
      "analyzing_account": "Analyzing account…",
      "account_analyzed": "Account analyzed",
      "analysis_error": "Error analyzing account",
      "goal": {
        "label": "Goal",
        "description": "What your agent works toward. An automation can give it a different task.",
        "placeholder": "e.g. Answer questions, then ask for the size and number of pieces to send a quote."
      },
      "instructions": {
        "label": "Instructions",
        "description": "Followed everywhere, including automations and comment replies.",
        "placeholder": "e.g. Never promise a delivery date",
        "add_button": "Add an instruction",
        "count_one": "{{count}} instruction",
        "count_other": "{{count}} instructions"
      }
    },
    "trigger_mode": {
      "title": "Activation rules",
      "description": "Define when your agent should reply to messages.",
      "automation_notice": "Your agent will not reply to a message if an automation scenario has replied already.",
      "label": "Activation rules",
      "modes": {
        "all_messages": {
          "label": "All pending messages",
          "description": "The agent will reply to every message that is not handled by an automation."
        },
        "specific_messages": {
          "label": "Specific intents only",
          "description": "The agent will only reply to messages that match specific intents."
        },
        "automation_only": {
          "label": "Automation only",
          "description": "The agent can only be triggered from automation scenarios using AI actions: handover, auto-DM, etc."
        },
        "never_reply": {
          "label": "Disable all replies",
          "description": "The agent will never send any messages, even from automations."
        }
      },
      "trigger_intents": {
        "label": "AI-detected intents",
        "input_placeholder": "e.g. refund request, book a meeting, product inquiry, etc.",
        "add_intent": "Add an intent",
        "automation_notice": "You can also detect intents in your automation scenarios.",
        "empty_notice": "**Just testing things out?** Leave these empty and the agent won't reply to any message",
        "empty_confirm": {
          "title": "No intents added",
          "body": "Your agent won't reply to any message until you add at least one intent. Continue anyway?",
          "confirm": "Continue anyway",
          "cancel": "Add an intent"
        }
      },
      "folder_rules": {
        "title": "Limit replies based on contact folders",
        "include": "Only reply to contacts in these folders",
        "exclude": "Never reply to contacts in these folders",
        "null": "Don't limit replies based on folders",
        "automation_notice": "You can still send agent messages to these contacts in automation scenarios."
      }
    },
    "knowledge_access": {
      "title": "Knowledge access and behaviour",
      "description": "Control what your agent knows and what it can do.",
      "always_reply": "Always have an answer",
      "always_reply_description": "The agent will include all data available in its language model and will always provide an answer.",
      "always_reply_warning": "This may lead to irrelevant or incorrect replies. Make sure to provide clear instructions and train your agent with relevant data.",
      "limit_knowledge": "Limit to instructions",
      "limit_knowledge_description": "The agent will reply only if it can find an answer in the provided instructions and knowledge.",
      "if_no_reply": "If the agent cannot reply, trigger this automation:",
      "add_to_folder": "Add to folder",
      "default_message_title": "Send default message",
      "default_message_placeholder": "Leave empty to send nothing",
      "send_email": "Send an email to"
    },
    "knowledge_items": {
      "title": "Training and knowledge",
      "description": "Import websites and documents to train your agent.",
      "items_count_one": "{{count}} item imported",
      "items_count_other": "{{count}} items imported",
      "add_item": "Import new item",
      "add_later": "**No document on hand?** You can always add more later!",
      "delete_item_confirm": "Do you want to delete this item?",
      "no_items": "You haven't imported anything yet.",
      "empty_items": "You can import text files, documents or websites",
      "item_type_label": "What do you want to add?",
      "save_item": "Save item",
      "status": {
        "ready": "Ready",
        "processing": "Processing",
        "failed": "Failed"
      },
      "text": {
        "title": "Manual text",
        "description": "Copy and paste text content directly.",
        "text_label": "Write or paste text",
        "text_placeholder": "e.g. Our company was founded in 2020 with the mission to provide high-quality adventure tours around the world.",
        "no_text": "Text empty",
        "retrieval_label": "Include this information in all Agent requests",
        "retrieval_alert_title": "OpenAI key needed",
        "retrieval_alert_message": "This feature is currently only available if you use your own OpenAI API Key.",
        "retrieval_alert_confirm": "Set API key",
        "retrieval_notice": "Turning this on might result in longer Agent response time and increased token usage."
      },
      "url": {
        "title": "Website",
        "description": "Import content from a website URL.",
        "url_label": "Website URL",
        "url_placeholder": "e.g. https://example.com",
        "crawl_url_label": "Automatically find other pages on this website"
      },
      "file": {
        "title": "Text document",
        "description": "Upload text file or document.",
        "file_label": "Choose a file to upload",
        "file_sublabel": "Supported types: .txt, .pdf, .docx, .doc"
      }
    },
    "actions": {
      "title": "Actions and automations",
      "description": "Let your agent send links, trigger automations, etc.",
      "actions_count_one": "{{count}} action connected",
      "actions_count_other": "{{count}} actions connected",
      "add_later": "**No actions set up yet?** You can connect more later!",
      "add_action": "Add an action",
      "delete_action_confirm": "Do you want to delete this action?",
      "trigger_view_label": "When contact wants to <0>{{trigger}}</0>",
      "trigger_label": "When contact wants to...",
      "trigger_placeholder": "e.g. asks for a refund, wants to book a meeting, etc.",
      "action_label": "Do the following...",
      "choose_action": "Choose an action",
      "conversion_link": {
        "title": "Send a conversion link",
        "label": "Link",
        "count_one": "{{count}} link",
        "count_other": "{{count}} links"
      },
      "scenario": {
        "title": "Start an automation scenario",
        "label": "Scenario",
        "count_one": "{{count}} scenario",
        "count_other": "{{count}} scenarios"
      },
      "survey": {
        "title": "Send a survey",
        "label": "Survey",
        "count_one": "{{count}} survey",
        "count_other": "{{count}} surveys"
      },
      "folder": {
        "title": "Add to folder",
        "label": "Folder",
        "count_one": "{{count}} folder",
        "count_other": "{{count}} folders"
      },
      "choose_conversion_link": "Choose a conversion link",
      "choose_scenario": "Choose a scenario",
      "choose_survey": "Choose a survey",
      "choose_folder": "Choose a folder",
      "no_actions": "You haven't connected any action yet."
    },
    "detect_properties": {
      "title": "User data collection",
      "description": "Automatically detect and save contact properties.",
      "no_properties": "No contact properties detected yet.",
      "add_property": "Add a property to detect",
      "delete_confirm": "Do you want to stop detecting this property?",
      "no_description": "No description provided"
    },
    "human_intervention": {
      "title": "Human intervention",
      "description": "Hand conversations over to your team, and pause the agent while they answer.",
      "human_takeover": {
        "title": "Handled by a team member",
        "description": "When someone from your team answers a conversation manually, the agent stops replying to it so it never answers on top of them.",
        "toggle_label": "Pause the agent when a team member replies",
        "pause_label": "Pause the agent for",
        "disabled_notice": "The agent will keep replying to conversations even after a team member answers them manually.",
        "tag_on": "Paused for {{pause}}",
        "tag_off": "Never paused by team replies",
        "options": {
          "1": "1 hour",
          "4": "4 hours",
          "12": "12 hours",
          "24": "24 hours"
        }
      },
      "request_human": {
        "title": "Request a human",
        "description": "React when contacts ask to speak with a human.",
        "notice": "The AI agent will pause until a team member replies to the contact.",
        "if_contact": "If contact asks to speak to a human, trigger this automation:"
      }
    },
    "connect_catalogs": {
      "title": "Connect external catalogs",
      "description": "Connect an external knowledge base or product catalog to provide more knowledge to your Agent."
    },
    "confirmation": {
      "title": "Setup complete",
      "description": "Your agent is now configured and ready to answer DMs automatically.",
      "tagline": "You're all set!",
      "summary": "Your agent is ready to engage with your audience and help you achieve your goals. Here's a summary of your configuration:"
    },
    "chat": {
      "title": "Chat with your Agent",
      "chat_as": "Chat as",
      "tooltip": "Select one your contacts to simulate the conversation they would have with the Agent.",
      "select_a_contact": "Select a contact",
      "reset_chat": "Reset chat",
      "detected_property": "Detected contact property",
      "human_intervention": "Human intervention requested",
      "opted_out": "Contact opted out of messages",
      "triggered_actions": {
        "folder": "Added to folder",
        "scenario": "Triggered scenario"
      }
    }
  },
  "api_errors": {
    "title": "API errors",
    "search": "Search errors",
    "empty": "No API errors logged yet",
    "apis": {
      "facebook": "Meta"
    },
    "table": {
      "columns": {
        "api": "API",
        "code": "Code",
        "message": "Message",
        "created_at": "Date"
      }
    },
    "detail": {
      "title": "Error details",
      "code": "Code",
      "subcode": "Subcode",
      "message": "Message",
      "created_at": "Date",
      "request": "Request",
      "body": "Response body",
      "trace": "Trace"
    }
  },
  "automation": {
    "ai_credits": {
      "badge_one": "{{count}} credit",
      "badge_other": "{{count}} credits",
      "tooltip_one": "Uses {{count}} AI credit each time it runs",
      "tooltip_other": "Uses {{count}} AI credits each time it runs"
    },
    "calendly_links": {
      "send": "Send Calendly link",
      "send_confirm_title": "Are you sure?"
    },
    "conversion_links": {
      "delete_image": "Delete image",
      "fields": {
        "add_image": "Add an image",
        "change_image": "Change image"
      },
      "send": "Send conversion link",
      "send_confirm_title": "Are you sure?",
      "url_invalid": "URL is invalid",
      "select_link": "Select a link",
      "select_links": "Select links",
      "select_links_one": "Select 1 link",
      "select_links_other": "Select {{count}} links",
      "create_link": "Create new link"
    },
    "scenarios": {
      "action_groups": {
        "most_used": {
          "title": "Most used",
          "description": "The basic blocks of DM automations"
        },
        "messages": {
          "title": "Messages",
          "description": "Send photos, videos, posts, etc."
        },
        "actions": {
          "title": "Actions",
          "description": "Request an action or send a link"
        },
        "conversions": {
          "title": "Conversions",
          "description": "Request an action or send a link"
        },
        "comments": {
          "title": "Comments",
          "description": "Reply to or hide comments"
        },
        "contact": {
          "title": "Contacts",
          "description": "Organise or update your contact"
        },
        "logic": {
          "title": "Logic",
          "description": "Add delay or complex logic to your flow"
        },
        "ai": {
          "title": "AI",
          "description": "Ask your AI agent to handle tasks"
        },
        "integrations": {
          "title": "Integrations",
          "description": "Integrate with external services"
        }
      },
      "actions": {
        "audio_message": {
          "loading": "Loading audio message…",
          "placeholder": "Record your message",
          "title": "Send audio message",
          "description": "Record or select an audio file"
        },
        "shopify_link": {
          "placeholder": "Choose products...",
          "title": "Shopify link",
          "description": "Send a link to a product on your Shopify store"
        },
        "collect_data": {
          "description": "Automatically detect and save data from the reply based on a custom format.",
          "title": "Collect user data",
          "when_data": "When correct data is received",
          "when_incorrect": "When data is incorrect",
          "no_data": "If no data has been received after",
          "data_format_label": "Detect data format",
          "contact_property_label": "Save to contact property",
          "default_content": "Please send date of birth to continue",
          "default_incorrect_label": "We couldn't detect a valid date. Please try again.",
          "content_title": "Your initial message",
          "content_description": "Ask people to send an information",
          "incorrect_label_title": "Message if data is not detected",
          "incorrect_label_description": "Ask people to send their information again",
          "notice": "This step automatically asks again in case contacts don't send a valid data format.",
          "auto_follow_up_label": "Send follow-up if no reply",
          "follow_up_label": "Follows up after {{duration}} {{unit}}"
        },
        "collect_email": {
          "description": "Automatically detect and save contact's email address from their reply.",
          "title": "Collect email address",
          "when_email": "When email is received",
          "when_incorrect": "When email is incorrect",
          "no_email": "If email hasn't been received after",
          "default_content": "Please send your email address to continue",
          "default_incorrect_label": "We couldn't detect your email. Please try again.",
          "content_title": "Your initial message",
          "content_description": "Ask people to send their email address",
          "incorrect_label_title": "Message if email is not detected",
          "incorrect_label_description": "Ask people to send their email address again",
          "notice": "This step automatically asks again in case contacts don't send a valid email address.",
          "auto_follow_up_label": "Send follow-up if no reply",
          "follow_up_label": "Follows up after {{duration}} {{unit}}"
        },
        "collect_property": {
          "title": "Ask for information",
          "description": "Collect email, phone or any contact property and save it automatically.",
          "when_property": "When information is collected",
          "when_incorrect": "Message without the information",
          "no_property": "If information has not been received after",
          "content_title": "Your initial message",
          "content_description": "Ask people to send the information you want to collect",
          "property_label": "Contact property to collect",
          "no_description_notice": "This property has no description. Adding one helps AI detect the value more accurately.",
          "add_description": "Add description",
          "autoreply_label": "Ask again when reply doesn't match",
          "incorrect_label_placeholder": "Ask people to send the information again",
          "incorrect_label_title": "Message if information is not detected",
          "incorrect_label_description": "Ask people to send the information again",
          "max_attempts_label": "Maximum retries",
          "max_attempt_mode_expire": "Expire",
          "max_attempt_mode_continue": "Continue",
          "auto_follow_up_label": "Send follow-up if no reply",
          "follow_up_label": "Follows up after {{duration}} {{unit}}",
          "max_attempts_mode_label": "After last attempt",
          "max_attempts_continue": "Go to next automation step",
          "max_attempts_expire": "Mark step as expired",
          "skip_ai_detection_label": "Skip AI detection (save raw message)"
        },
        "comment": {
          "placeholder": "Type your comment",
          "title": "Reply to comment",
          "description": "Write a comment as a reply on your posts",
          "autowrite": "Rewrite comment automatically",
          "comment_limiting": "Avoid spam detection",
          "like_comment": "Auto-like the original comment ❤️",
          "add_variant": "Add comment variant",
          "variant_notice": "A variant will be selected at random to avoid repetition",
          "progressive_limiting": "Some comments are skipped automatically to avoid spam detection. [Learn more](https://help.inro.social/en/articles/13770654-how-inro-avoids-spam-detection#h_d938e9d657)"
        },
        "conversion_link": {
          "add_to_folder": "On click, add to folder",
          "placeholder": "Choose link...",
          "title": "Conversion link",
          "description": "Send a link with a button that tracks clicks",
          "when_click": "When link is clicked",
          "no_click": "If link hasn't been clicked after",
          "loading": "Loading content…"
        },
        "delay": {
          "seconds_one": "second",
          "seconds_other": "seconds",
          "title": "Time delay",
          "description": "Wait for a specific amount of time before continuing",
          "wait": "Wait"
        },
        "engagement_condition": {
          "if": "If contact has engaged",
          "in": "in the last",
          "title": "Engagement condition"
        },
        "folder": {
          "add": "Add contact to folder(s)",
          "remove": "Remove contact from folder(s)",
          "title": "Add or remove folder",
          "description": "Choose from your folders or create a new one",
          "excluded_folder_warning": "One of the selected folders is excluded from this scenario. Adding the contact to it will interrupt the rest of the scenario."
        },
        "contact_status": {
          "opt_in": "Opt-in contact",
          "opt_out": "Opt-out contact",
          "title": "Update contact status",
          "description": "Opt the contact in or out of messaging"
        },
        "http_request": {
          "add_header": "Add header",
          "add_param": "Add parameter",
          "body": "Body",
          "description": "Send a request to a server or a webhook.",
          "header_key": "Key",
          "header_value": "Value",
          "headers": "Headers",
          "param_key": "Key",
          "param_value": "Value",
          "encode_params": "Encode parameter values",
          "body_mode_key_value": "Key / Value",
          "body_mode_json": "JSON",
          "json_placeholder": "{ \"key\": \"value\" }",
          "title": "Send HTTP request",
          "test_request": "Test request",
          "url_placeholder": "https://example.com",
          "if_success": "If request is successful",
          "if_failure": "If request fails",
          "learn_more": "Learn more about sending data in HTTP requests"
        },
        "like": {
          "title": "Like message",
          "description": "Like the last message sent by the contact. Does not apply to comments."
        },
        "media_message": {
          "send_images": "Send several images",
          "loading": "File loading...",
          "no_file_uploaded": "No file selected",
          "title": "Send a photo or video",
          "description": "Upload a photo or a video",
          "upload_media": "Upload media",
          "select_media": "Select media",
          "use_url": "Use a URL",
          "upload_file": "Upload a file",
          "url_placeholder": "https://... or {{variable}}"
        },
        "meta_pixel": {
          "title": "Meta Conversion event",
          "description": "Send a conversion event to the Meta API for tracking and analytics",
          "get_access_token": "Get API access token",
          "select_dataset": "2. Select dataset",
          "loading_datasets": "Loading datasets...",
          "select_dataset_placeholder": "Select dataset...",
          "no_datasets": "No datasets available",
          "create_dataset": "Create dataset in Meta Business Suite",
          "select_event": "Select event",
          "select_event_placeholder": "Select event...",
          "integration_connected": "Using Meta Conversions API integration token",
          "integration_not_connected": "Connect Meta Conversions API to send events",
          "connect_integration": "Go to Integrations",
          "legacy_token_warning": "Note: Short-lived tokens will expire. Use the integration for long-lived tokens."
        },
        "share_media": {
          "title": "Share a post or reel",
          "description": "Select from your recent publications",
          "loading": "Loading content…"
        },
        "message": {
          "placeholder": "Type your message",
          "title": "Send message",
          "description": "Write a message and insert variables",
          "insert_variable": "Insert variable"
        },
        "payment_link": {
          "placeholder": "Pick an amount",
          "title": "Payment link",
          "description": "Get paid directly in DMs"
        },
        "ask_question": {
          "title": "Ask a question",
          "description": "Trigger different actions based on the contact's reply.",
          "turn_to_agent": "Add AI detection",
          "question_label": "Type your question",
          "question_placeholder": "e.g. What product are you interested in?",
          "reply_placeholder": "e.g. the red one, etc.",
          "option_placeholder": "Enter an answer",
          "prompt_placeholder": "Describe this reply to help the AI",
          "add_reply": "Add reply",
          "save_reply_to": "Save reply to contact property",
          "when_reply": "Contact replies",
          "when_reply_other": "Any other reply",
          "warning": "Be careful when adding many options as contacts are less likely to reply.",
          "no_replies": "If contact hasn't replied after",
          "delete_reply_confirm_title": "Delete this reply?",
          "delete_reply_confirm_message": "All actions linked to this reply will be deleted. This cannot be undone.",
          "when_incorrect": "Ask again when reply doesn't match",
          "incorrect_label_placeholder": "Send a message asking them to try again",
          "incorrect_reply": "Incorrect reply",
          "ai_detection": "AI detection",
          "ai_detection_description": "AI will detect which reply best matches when no exact match is found.",
          "disable_quick_replies": "Hide quick reply buttons",
          "max_attempts_label": "Maximum retries",
          "auto_follow_up_label": "Send follow-up if no reply",
          "follow_up_label": "Follows up after {{duration}} {{unit}}"
        },
        "agent_question": {
          "title": "AI Agent Question",
          "description": "Ask a question and let AI detect when replies match."
        },
        "scenario_condition": {
          "description": "Check if the condition is met.",
          "is_follower": "Contact follows you",
          "is_in_folder": "Contact is in folder",
          "folder": "Choose a folder",
          "message_contains": "Last DM or Comment contains",
          "message_contains_placeholder": "Type a keyword",
          "contact_property": "Contact property matches condition",
          "contact_property_value": "Enter value",
          "variable": "Variable matches condition",
          "variable_placeholder": "{{ variable }}",
          "time": "Time-based condition",
          "time_placeholder": "Select an option",
          "condition_options": {
            "keywords": "Specific keyword",
            "emojis": "Any emoji",
            "trigger_time": "Trigger time",
            "last_message_time": "Last message time"
          },
          "branches": {
            "option_true": "If condition is met",
            "option_false": "If condition is not met"
          },
          "title": "Scenario condition"
        },
        "wait_for_reply": {
          "description": "The scenario will pause and wait until the contact replies to proceed.",
          "title": "Wait for reply",
          "when_contact_replies": "When contact replies",
          "if_message_contains": "If message contains",
          "else_if_message_contains": "else if message contains",
          "no_reply": "If contact hasn't replied after"
        },
        "survey": {
          "loading": "Loading survey...",
          "title": "Send a survey",
          "description": "Ask one or several questions and save or export the answers",
          "placeholder": "Choose survey...",
          "when_replied": "When contact replies",
          "when_filled": "When survey is filled",
          "no_reply": "If contact hasn't replied after",
          "no_fill": "If survey hasn't been filled after"
        },
        "not_found": {
          "title": "Action not found"
        },
        "content_filter": {
          "title": "Hate and spam filter",
          "description": "The scenario will stop if the message or comment contains hate speech or spam.",
          "add_to_folder": "Add contact to folder"
        },
        "hide_comment": {
          "title": "Hide comment",
          "description": "Hide the comment from your post. You will still be able to see it in the hidden comments section."
        },
        "update_property": {
          "title": "Update contact property",
          "description": "Update the contact's email, phone or any other property.",
          "placeholder": "Property value"
        },
        "send_email": {
          "title": "Send email",
          "description": "Send an email to a specified email address.",
          "edit_email": "Edit email",
          "from": "From",
          "manage_emails": "Manage",
          "to": "To",
          "cc": "Cc",
          "bcc": "Bcc",
          "reply_to": "Reply-to",
          "subject_label": "Subject",
          "subject_placeholder": "Enter email subject",
          "email_placeholder": "adam@example.com",
          "content_label": "Email content",
          "content_placeholder": "Write your email...",
          "content_notice": "You can use Markdown syntax and insert variables.",
          "attachments": "Attachments",
          "attachments_count_one": "{{count}} attachment",
          "attachments_count_other": "{{count}} attachments",
          "add_recipient": "Add recipient"
        },
        "send_file": {
          "title": "Send file",
          "description": "Send a file as a downloadable button message",
          "upload_file": "Upload file",
          "change_file": "Change file",
          "select_file": "Select a file",
          "title_label": "Message text",
          "title_placeholder": "Enter a message...",
          "button_label": "Button label (optional)",
          "button_placeholder": "Download"
        },
        "trigger_scenario": {
          "title": "Trigger a scenario",
          "description": "Run another scenario with this contact.",
          "select_scenario": "Select scenario",
          "inactive_warning": "This scenario is inactive. It will not be triggered.",
          "current_scenario_warning": "Selecting the current scenario can trigger an infinite loop.",
          "trigger_data_label": "Trigger data",
          "trigger_data_add": "Add data",
          "trigger_data_key": "Key",
          "trigger_data_value": "Value",
          "trigger_data_empty": "Pass data to the scenario as variables."
        },
        "calendly_link": {
          "title": "Calendly link",
          "description": "Send a Calendly link to schedule a meeting.",
          "placeholder": "Choose Calendly link",
          "edit_button": "Edit button text",
          "button_placeholder": "Button text",
          "free_plan": "You need a paid Calendly account to track bookings.",
          "when_booked": "When contact books a meeting",
          "no_book": "If contact hasn't booked after"
        },
        "eventbrite_link": {
          "title": "Eventbrite link",
          "description": "Send an Eventbrite link to register for an event.",
          "placeholder": "Event ID",
          "when_booked": "When contact registers to the event",
          "no_book": "If contact hasn't registered after"
        },
        "agent_reply": {
          "title": "AI Agent auto-DM",
          "description": "Let your AI Agent reply once to the last message or comment",
          "message_length": {
            "10": "Short message (~10 words)",
            "25": "Normal message (~25 words)",
            "50": "Long message (~50 words)",
            "100": "Very long message (~100 words)"
          }
        },
        "agent_message": {
          "title": "AI Agent message",
          "description": "Let your AI Agent send one message, using the last messages or comment as context.",
          "task_placeholder": "e.g. send today's weather, ask for information, etc.",
          "task_notice": "Write clear and simple instructions for better results.",
          "message_length": {
            "10": "Short message (~10 words)",
            "25": "Normal message (~25 words)",
            "50": "Long message (~50 words)",
            "100": "Very long message (~100 words)"
          }
        },
        "agent_comment": {
          "title": "AI Agent auto-comment",
          "description": "Let your AI Agent reply to the last comment",
          "use_training_data": "Use training data in reply",
          "message_length": {
            "5": "Short comment (~5 words)",
            "10": "Normal comment (~10 words)",
            "25": "Long comment (~25 words)"
          }
        },
        "agent_handover": {
          "title": "Hand over to AI Agent",
          "description": "Hand over all incoming messages to the AI Agent until the conversation ends",
          "task_label": "Give specific instructions",
          "task_placeholder": "e.g. Give advice on products, help with booking, etc.",
          "add_task": "Add instructions",
          "auto_follow_up_label": "Follow-up automatically after each message",
          "follow_up_label": "Follows up after {{duration}} {{unit}}",
          "preview_message": "Message generated by AI Agent",
          "preview_follow_up": "Follow-up message generated by AI Agent",
          "preview_wait": "After {{duration}} {{unit}}",
          "preview_reply": "Reply from contact",
          "task_completed_branch": "Task completed",
          "expired_branch": "Handover expired"
        },
        "ai_condition": {
          "title": "AI-detected condition",
          "description": "Use AI to evaluate if a custom condition is true or false",
          "placeholder": "e.g. the contact is interested in booking a meeting",
          "warning": "AI may not always be accurate. Avoid critical decisions.",
          "option_true": "If condition is true",
          "option_false": "If condition is false"
        },
        "contact_filter_condition": {
          "title": "Contact filter",
          "description": "Branch based on whether the contact matches a set of filters.",
          "add_filter": "Add a filter",
          "logic_label": "Match",
          "no_filters": "No filters defined",
          "option_true": "Matches filters",
          "option_false": "Does not match"
        },
        "ab_testing": {
          "title": "A/B testing",
          "description": "Test different flows randomly to see which one performs better.",
          "notice": "You can trigger other scenarios",
          "option_a": "50%",
          "option_b": "50%",
          "ratio_label": "Option A ratio",
          "ratio_description": "Option A: {{option_a}}% - Option B: {{option_b}}%"
        },
        "card_template": {
          "title": "Send an action card",
          "description": "Send a card with a title, description, image and buttons.",
          "title_placeholder": "Card title",
          "subtitle_placeholder": "Card description (optional)",
          "remove_card": "Remove card",
          "when_clicked": "When button is clicked",
          "no_click": "If button hasn't been clicked after",
          "allow_multiple_clicks": "Allow multiple clicks",
          "image_url_placeholder": "Direct image URL"
        },
        "button_template": {
          "title": "Send message with button",
          "description": "Write a message with one or more clickable buttons",
          "content_placeholder": "Write your message",
          "buttons_title": "Buttons",
          "button_title_placeholder": "Button text",
          "add_url_label": "Add a URL",
          "add_button": "Add button",
          "each_button": "Each button can be clicked",
          "once": "only once",
          "multiple_times": "multiple times",
          "click_on": "Allow one click on",
          "one_button": "only one button",
          "multiple_buttons": "each button",
          "click_notice": "You can trigger actions for each non-URL button in the automation flow.",
          "when_clicked": "When button is clicked",
          "no_click": "If button has not been clicked after",
          "add_image": "Add image",
          "fetch_opengraph_image": "Automatically fetch image from caption URL",
          "fetch_opengraph_label": "Fetch image from website URL"
        },
        "keywords": {
          "add_keyword": "Add keyword",
          "keyword_placeholder": "Enter keyword",
          "add_branch": "Add branch",
          "or": "or",
          "notice": "Keywords ignore case and accents"
        },
        "eleven_labs_audio": {
          "title": "Eleven Labs audio",
          "description": "Generate an audio message from a text with Eleven Labs AI",
          "text_placeholder": "Type your message",
          "voice_placeholder": "Choose a voice",
          "generate_audio": "Generate audio message"
        },
        "high_level_upsert_contact": {
          "title": "Upsert HighLevel Contact",
          "description": "Create or update a contact in HighLevel CRM",
          "params_label": "Contact params",
          "params_empty": "No params configured",
          "params_add": "Add param",
          "params_key": "Field name",
          "params_value": "Value"
        },
        "ask_for_follow": {
          "title": "Ask for follow",
          "description": "Ask the contact to follow you before proceeding with the scenario.",
          "notice": "This step automatically asks again in case contacts don't follow you.",
          "content_title": "Your message",
          "content_description": "Ask people to follow you",
          "button_label_title": "Button text",
          "incorrect_label_title": "Message if follow is not detected",
          "incorrect_label_description": "Ask people to try again",
          "default_content": "Please follow us to continue",
          "default_button_label": "I have followed you",
          "default_incorrect_label": "We couldn't detect your follow. Please try again.",
          "when_followed": "When contact clicks and follows you",
          "no_follow": "If contact hasn't clicked and followed you after"
        },
        "like_comment": {
          "title": "Like comment",
          "description": "Like the comment that triggered this scenario.",
          "comment_text": "A comment on your post"
        }
      },
      "action_executions": {
        "details": {
          "request": "Request",
          "response": "Response",
          "rerun": "Run again",
          "http_request": "Request",
          "http_response": "Response",
          "view_details": "View details",
          "title": "Execution details"
        },
        "status": {
          "started": "Started",
          "scheduled": "Scheduled",
          "waiting": "Waiting",
          "expired": "Expired",
          "running": "Running",
          "completed": "Completed",
          "interrupted": "Interrupted",
          "failed": "Failed",
          "cancelled": "Cancelled",
          "skipped": "Skipped"
        },
        "outcome": {
          "empty_value": "Empty",
          "waiting_until": "Expires {{date}}",
          "continued_at": "Continued on {{date}}",
          "added_to": "Added to",
          "removed_from": "Removed from",
          "property_set": "{{property}} set to",
          "opted_out": "Contact opted out",
          "opted_in": "Contact opted in",
          "comment_hidden": "Comment hidden",
          "content_passed": "No hate speech or spam found",
          "value_checked": "Checked value",
          "ai_credits_exhausted": "Out of AI credits, so the condition was not evaluated.",
          "collected": "Saved to {{property}}",
          "view_run": "View run",
          "not_started": {
            "suspended": "Not started: the scenario is suspended.",
            "disconnected": "Not started: the Instagram account is disconnected.",
            "rate_limited": "Not started: the scenario already ran for this contact recently.",
            "excluded_folder": "Not started: the contact is in a folder this scenario excludes.",
            "excluded_filter": "Not started: the contact matches an exclusion filter.",
            "already_running": "Not started: this run already started it."
          },
          "agent_task": "Task",
          "replied_to": "Replied to",
          "agent_reply": "Reply",
          "handover_completed": "Task completed on {{date}}",
          "handover_expired": "Handover ended on {{date}}",
          "handover_checks_one": "Task checked once, not completed yet",
          "handover_checks_other": "Task checked {{count}} times, not completed yet"
        }
      },
      "steps": {
        "select_post": {
          "title": "Select the posts",
          "description": "Automation will only trigger on these posts",
          "which_posts": "Which posts do you want to use?",
          "select_option": "Select an option",
          "no_posts_selected": "No posts selected",
          "all_posts": "All posts",
          "all_posts_notice": "The automation will trigger on all your posts, including future ones",
          "next_post": "Next post only",
          "next_post_notice": "Automation will only trigger on the first post after",
          "next_post_reset": "Reset date",
          "caption_keywords": "Posts with keyword(s) in caption",
          "caption_keywords_label": "Enter caption keyword(s)",
          "caption_keywords_notice": "Use a #hashtag, a specific word or an emoji. Uppercase and accents are ignored. Future posts will also be included.",
          "filter_keyword_caption": "Search by hashtag, keyword or emoji...",
          "no_posts_match": "No posts match the keyword",
          "posts_match_one": "{{count}} post matches the keyword",
          "posts_match_other": "{{count}} posts match the keyword",
          "caption_url": "Posts with URL in caption",
          "caption_url_filter_label": "Only if URL contains",
          "caption_url_filter_placeholder": "e.g. awesomelink.com",
          "caption_url_filter_description": "Leave empty to match all URLs.",
          "caption_url_filter_add_button": "Add filter",
          "caption_url_notice": "You can access the URL in your automation with the variable **{{ trigger.caption_url }}**.",
          "select_posts": "Select posts manually",
          "choose_posts": "Choose one or more posts",
          "selected_posts_one": "{{count}} post selected",
          "selected_posts_other": "{{count}} posts selected",
          "search_posts": "Search posts",
          "no_posts": "No posts match your search"
        },
        "select_comments": {
          "title": "Filter the comments",
          "description": "Automation will only trigger on matching comments",
          "which_comments": "Which comments should trigger the automation?",
          "select_option": "Select an option",
          "no_comments_selected": "No comments selected",
          "all_comments": "All comments",
          "comment_keywords": "Comments with keyword(s)",
          "ai_detected": "AI-detected intent",
          "tag_someone": "Comments that tag someone",
          "ai_intent_label": "Describe comment intent",
          "ai_intent_notice": "Be as precise and specific as possible to get better results.",
          "ai_intent_warning": "AI-detected intents may not always be accurate.",
          "keywords_title": "Enter comment keywords",
          "keywords_notice": "Uppercase and accents are ignored and you can use emojis.",
          "no_keywords_notice": "Enter at least one keyword to filter the comments.",
          "duplicate_keywords_notice": "Uppercase and accents are ignored, you can remove the duplicates.",
          "recent_comments": "Existing matching comments on posts",
          "recent_comments_notice": "You can trigger automations on past comments in the last 7 days only.",
          "no_recent_comments": "No recent comments match your keywords on the selected posts",
          "all_comments_example": "any comment",
          "all_comments_reply_example": "any reply",
          "comment_keywords_example": "comment with keyword {{keyword}}",
          "comment_keywords_reply_example": "reply with keyword {{keyword}}",
          "ai_detected_example": "comment with AI-detected intent",
          "ai_detected_reply_example": "reply with AI-detected intent",
          "hate_spam_example": "a hateful or spam comment",
          "hate_spam_reply_example": "a hateful or spam reply",
          "tag_someone_example": "love this @pierredemilly @inro.social",
          "tag_someone_reply_example": "you should see this @pierredemilly",
          "_example": "a comment",
          "allow_replies": "Also trigger on replies to comments",
          "allow_replies_notice": "Instagram only allows public replies on top-level comments, so a comment reply action will be skipped for these — a DM can still be sent to them.",
          "exclude_negative": "Exclude hateful and negative comments",
          "ai_intent_placeholder": "e.g. the comment is insulting me, is racist, etc."
        }
      },
      "comment_to_dm": {
        "title": "Comment to DM link",
        "invalid_type": "Invalid scenario type",
        "convert": "Convert to Custom",
        "convert_confirm": "Do you want to convert this Comment-to-DM scenario to a Custom scenario? This cannot be undone.",
        "comment_reply": {
          "title": "Reply to the comment",
          "description": "Automation will reply to the comment with a comment",
          "comment_label": "Write your comment",
          "placeholder": "Type your comment",
          "autowrite": "Rewrite comment automatically",
          "add_variant": "Add comment variant",
          "variant_notice": "A variant will be selected at random to avoid repetition",
          "ratio_label": "Define a reply ratio",
          "ratio_notice": "Reply randomly to only a percentage of comments to avoid being identified as spam by Instagram and your community"
        },
        "question": {
          "title": "Ask a question",
          "description": "The automation will wait until the contact replies",
          "content_label": "Your question",
          "content_placeholder": "Do you want to receive the link?",
          "button_label": "Button text",
          "button_placeholder": "Receive the link",
          "button_notice": "Contacts can click the button to reply",
          "detection_notice": "Replies will be automatically detected",
          "yes_no_title": "Why ask a question?",
          "yes_no_description": "Asking a questions makes sure that your contacts accept your request and reply to your first DM, enabling the rest of the automation.",
          "button_template": "Simple question",
          "ask_for_follow": "Ask for follow",
          "collect_property": "Ask for email",
          "modes": {
            "button_template": "Message with button",
            "message": "Simple message"
          },
          "message_label": "Your message",
          "message_placeholder": "Thanks for your comment! Want me to send you the link? Just reply to this message.",
          "message_notice": "The link is sent as soon as the contact replies"
        },
        "send_link": {
          "title": "Send a link",
          "description": "Send contacts to your website and track conversions",
          "url_label": "Enter your link",
          "url_placeholder": "e.g. https://awesomelink.com",
          "recent_links": "or select a recent link",
          "search_links": "Search links",
          "follow_up": "Follow up after {{time}}",
          "existing_link_notice": "You are currently editing an existing link.",
          "create_new_link": "Create new link",
          "tracking": {
            "title": "When button is clicked",
            "add_to_folder": "Add contact to folder",
            "send_meta_event": "Send event to Meta Pixel",
            "meta_integration_notice": "Connect Meta Conversions API in your profile to send events"
          }
        },
        "follow_up": {
          "title": "Follow up with a DM",
          "description": "Increase your conversion rates by following up automatically",
          "content_label": "Your message",
          "content_placeholder": "Have you checked the link yet?",
          "enabled_label": "Send a follow-up",
          "content_description": "This will only be sent if the contact hasn't clicked the link yet.",
          "expire_label": "Wait for...",
          "notice_title": "Why follow up?",
          "notice_description": "Many people receive your link but won't click it right away. Following up with them a few hours later will increase your conversion rates."
        },
        "review": {
          "title": "Review your automation scenario",
          "description": "Make sure everything is set up correctly",
          "test_scenario": "Test your scenario",
          "select_contact": "Select a contact",
          "activate_scenario": "Activate the scenario",
          "deactivate_scenario": "Deactivate the scenario",
          "view_insights": "View insights",
          "activate_scenario_notice": "Activate scenario to trigger on existing comments"
        },
        "preview": {
          "posts_count_one": "Triggered on {{count}} post",
          "posts_count_other": "Triggered on {{count}} posts"
        }
      },
      "comment_moderation": {
        "title": "Comment moderation",
        "invalid_type": "Invalid scenario type",
        "convert": "Convert to Custom",
        "convert_confirm": "Do you want to convert this Comment moderation scenario to a Custom scenario? This cannot be undone.",
        "comments": {
          "which_comments": "Which comments should be moderated?",
          "hate_spam": "All hateful and spam comments",
          "ai_detected": "Comments with a specific intent",
          "comment_keywords": "Comments with specific keywords",
          "hate_spam_title": "Hateful and spam detection",
          "hate_spam_notice": "AI automatically detects hateful, offensive and spam comments — no setup needed.",
          "hate_spam_warning": "AI detection may not always be accurate.",
          "ai_intent_examples": [
            "Hate speech or slurs",
            "Spam or self-promotion",
            "Threats or harassment",
            "Offensive insults or attacks"
          ]
        },
        "actions": {
          "title": "Choose what happens",
          "description": "Comments that match are hidden automatically. Add more actions if you want.",
          "default": "Default",
          "hide": {
            "title": "Hide the comment",
            "description": "The comment is hidden from your post. You can still see it in the hidden comments section."
          },
          "message": {
            "title": "Send a DM",
            "description": "Send a direct message to the person who commented.",
            "content_label": "Your message",
            "content_placeholder": "e.g. Your comment was hidden because it broke our community guidelines."
          },
          "folder": {
            "title": "Add to a folder",
            "description": "Organize the contact by adding them to one or more folders.",
            "no_folder_notice": "Select at least one folder, or turn this option off."
          }
        },
        "review": {
          "title": "Review your moderation automation",
          "description": "Make sure everything is set up correctly"
        },
        "preview": {
          "hidden": "Hidden",
          "added_to": "Added to {{folders}}"
        }
      },
      "new_scenario_dialog": {
        "title": "Create a new automation",
        "description": "Scenarios are automated workflows that run on their own. You can create them from scratch or use a template.",
        "comment_to_dm": {
          "title": "Comment to DM link",
          "description": "Automatically reply to comments with a DM and a link"
        },
        "comment_moderation": {
          "title": "Comment moderation",
          "description": "Automatically hide unwanted comments on all your posts"
        },
        "custom": {
          "title": "Custom scenario",
          "description": "Create your automation from scratch"
        },
        "choose_template": "or choose a template",
        "search_templates": "Search templates",
        "default_category": "Basic automations",
        "existing": {
          "title": "Existing automation",
          "description": "Add an existing automation from your list to this post"
        }
      },
      "add_step": "Add a step",
      "other_actions": "Other actions…",
      "add_action_suggestions": {
        "message": "Send message",
        "ask_question": "Ask a question",
        "conversion_link": "Send a link"
      },
      "move_action": {
        "title": "Move Action",
        "description": "Choose how you want to move this action:",
        "move_only": "Move only this action",
        "move_only_description": "The action will be moved without its children",
        "move_with_children": "Move action and all its children",
        "move_with_children_description": "The entire branch will be moved together"
      },
      "delete_action": {
        "title": "Delete Action",
        "description": "Choose how you want to delete this action:",
        "delete_only": "Delete only this action",
        "delete_only_description": "The action will be deleted but its children will remain",
        "delete_with_children": "Delete action and all its children",
        "delete_with_children_description": "The entire branch will be deleted"
      },
      "copy_action": {
        "title": "Copy Action",
        "description": "Choose how you want to copy this action:",
        "copy_only": "Copy only this action",
        "copy_only_description": "The action will be copied without its children",
        "copy_with_children": "Copy action and all its children",
        "copy_with_children_description": "The entire branch will be copied together"
      },
      "context_menu": {
        "copy": "Copy",
        "delete": "Delete",
        "retry_failed": "Retry all failed",
        "retry_failed_confirm": "Retry all failed executions?",
        "retry_failed_success": "Retries have been queued successfully."
      },
      "autodelay_notice": "Action automatically delayed by 5 seconds",
      "count_one": "{{count}} scenario",
      "count_other": "{{count}} scenarios",
      "create_first": "Create your first scenario",
      "create_scenario": "Create scenario",
      "current_scenario": "Current scenario",
      "delete": "Delete scenario",
      "delete_confirm": "Do you want to delete this scenario?",
      "duplicate_title": "Duplicate scenario to",
      "edit": "Edit scenario",
      "generating_tooltip": "Generating scenario…",
      "generating_status": "Your automation scenario is being generated. \nThis will only take a moment 🙂",
      "generation_status": {
        "title": "Generating your scenario",
        "phase_prompts": "Analyzing your prompt",
        "phase_triggers": "Building triggers",
        "phase_actions": "Building actions",
        "error_generic": "Something went wrong"
      },
      "suspended_status": {
        "title": "Automation suspended",
        "message_before": "This automation has been suspended for violating our ",
        "message_after": " and can no longer run, be edited, or copied.",
        "terms_of_service": "Terms of Service",
        "contact_support_before": "If you believe this is a mistake, contact ",
        "contact_support_email": "support@inroapp.com",
        "contact_support_after": " to appeal this decision."
      },
      "limit_reached": "Limit reached",
      "limit_reached_tooltip_one": "{{ count }} execution was not triggered because you have reached your monthly limit",
      "limit_reached_tooltip_other": "{{ count }} executions were not triggered because you have reached your monthly limit",
      "unsaved_changes": "Unsaved changes",
      "unsaved_changes_kept": "Your changes will be kept as a draft until you save or cancel them.",
      "limited_executions_one": "<0>{{ count }} scenario</0> was not triggered because you have reached your monthly limit",
      "limited_executions_other": "<0>{{ count }} scenarios</0> were not triggered because you have reached your monthly limit",
      "trigger_executions": "Manage limited executions",
      "disconnected_badge": "Disconnected",
      "disconnected_badge_tooltip_one": "{{ count }} execution was paused because your Instagram account was disconnected",
      "disconnected_badge_tooltip_other": "{{ count }} executions were paused because your Instagram account was disconnected",
      "disconnected_executions_one": "<0>{{ count }} execution</0> was paused because your Instagram account was disconnected",
      "disconnected_executions_other": "<0>{{ count }} executions</0> were paused because your Instagram account was disconnected",
      "reconnect_to_run": "Reconnect Instagram",
      "run_disconnected_executions": "Run paused executions",
      "live_title": "Live scenario",
      "manual_title": "Manual scenario",
      "new_scenario": "New scenario",
      "not_found": "Scenario not found",
      "title_label": "Name your scenario",
      "title_placeholder": "My awesome automation scenario",
      "no_scenarios": "You don't have any scenario yet.",
      "no_scenarios_help": "Not sure where to start? \n<0>Here are a few ways</0> to get started!",
      "pause": "Pause scenario",
      "pause_confirm": "Do you want to pause this scenario?",
      "preview_template": "Preview template",
      "rename_scenario": "Rename scenario",
      "run": "Run scenario",
      "run_confirm": "Are you sure?",
      "save": "Save scenario",
      "search_scenarios": "Search scenarios",
      "share": "Share scenario",
      "template": "Template",
      "title": "Scenarios",
      "remove_from_folder": "Remove from folder",
      "view_history": "View history",
      "view_insights": "Results",
      "suspended_badge": "Suspended",
      "suspended_badge_tooltip": "This automation was suspended for violating our Terms of Service and can no longer run",
      "empty_scenario": "Empty scenario",
      "empty_scenario_description": "Create your own scenario from scratch",
      "run_test": "Test scenario",
      "run_test_confirm": "Test the scenario with this contact?",
      "cancel_copy": "Cancel copy",
      "test_sent": "Your test was started.",
      "edit_with_agent": {
        "title": "Using an AI assistant?",
        "description": "Update this scenario from Claude or ChatGPT with our MCP server.",
        "cta": "Connect assistant"
      },
      "options": {
        "advanced_options": "Advanced options",
        "rate_limiting": {
          "title": "Limit frequency per contact",
          "toggle_label": "Limit how often this scenario can run for each contact",
          "run_scenario": "Run scenario only",
          "options": {
            "once": "once",
            "once_every": "once every"
          },
          "per_contact": "per contact"
        },
        "interrupt_on_message": {
          "title": "Interrupt on message replies",
          "interrupt_on_message": "Interrupt when you reply manually",
          "interrupt_on_reply": "Interrupt when contact replies"
        },
        "exclude_folders": {
          "title": "Exclude specific folders",
          "description": "This scenario will not run for contacts in the selected folders"
        },
        "scenario_folder": {
          "title": "Add to scenario folder",
          "select_folder": "Select folder",
          "priority_label": "Set priority number (1 is highest)",
          "create_folder": "Create new folder",
          "create_folder_label": "Folder name",
          "create_folder_placeholder": "Enter folder name",
          "priority_badge": "Priority {{priority}}"
        }
      },
      "public": {
        "made_by": "Made by",
        "powered_by": "Powered by",
        "automation_notice": "Use this flow to automate your DMs and comments on Instagram.",
        "use_automation": "Use this automation",
        "use_automation_confirm": "Copy this automation to your account?",
        "login_modal_title": "Log in to use this automation",
        "signup_modal_title": "Sign up to use this automation",
        "view_details": "View details",
        "automation_details": "Automation details"
      },
      "scenario_types": {
        "custom": "Custom",
        "comment_to_dm": "Comment-to-DM",
        "comment_moderation": "Comment moderation"
      },
      "settings": {
        "interrupt_on_message": "Interrupt when you reply manually",
        "interrupt_on_reply": "Interrupt when contact replies",
        "on_reply_scenario_label": "When contact replies, trigger this scenario:",
        "is_template": "Save as template"
      },
      "sharing": {
        "title": "Share your scenario as a template",
        "input_label": "Share this link so anyone can reuse your scenario as a template",
        "add_referral_code": "Add your referral code to the link",
        "visits": "Visited",
        "visits_notice": "The number of times your scenario was viewed by other users.",
        "used": "Used",
        "used_notice": "The number of times your scenario was copied by other users.",
        "active": "Active",
        "active_notice": "The number of current active copies of your scenario.",
        "recent_copies": "Recent uses of your scenario",
        "no_copies": "No one has used your scenario yet.",
        "metrics": "Usage metrics",
        "details": "Details & description",
        "tags_label": "Tag your template",
        "add_tag": "Add tag",
        "text_label": "Describe your template",
        "text_notice": "You can use <0>Markdown</0> syntax to format your description."
      },
      "select_scenario": "Select a scenario",
      "start": "Activate scenario",
      "start_confirm": "Do you want to activate this scenario?",
      "catch_up": {
        "counting": "Finding past comments…",
        "title": "Catch up",
        "button": "Catch up",
        "reached": "Reached **{{reached}}** of {{total}} people",
        "can_reach_one": "{{count}} person can still get it",
        "can_reach_other": "{{count}} people can still get it",
        "expires_one": "the oldest can be reached for {{count}} more day",
        "expires_other": "the oldest can be reached for {{count}} more days",
        "limited": "{{count}} fit in this month's contacts",
        "running": "Sending to **{{total}}** people",
        "running_progress": "{{done}} of {{total}} done",
        "caught_up": "All caught up: **{{count}}** people reached in the last 7 days",
        "badge_one": "{{count}} to catch up",
        "badge_other": "{{count}} to catch up",
        "badge_tooltip": "People who matched in the last 7 days and haven't got it yet",
        "summary_one": "**{{count}} person** can still be reached by your automations",
        "summary_other": "**{{count}} people** can still be reached by your automations",
        "summary_show": "Show",
        "modal": {
          "period": "Period",
          "window_help": "Instagram accepts replies to comments for 7 days.",
          "periods": {
            "day": "Last 24 hours",
            "three_days": "Last 3 days",
            "week": "Last 7 days"
          },
          "people_one": "{{count}} person",
          "people_other": "{{count}} people",
          "never_triggered": "**{{count}}** matched but never got it",
          "paused": "**{{count}}** were paused by a disconnection",
          "skipped": "**{{count}}** were skipped by the contact limit",
          "contacts_left": "Uses up to {{count}} of the {{remaining}} contacts left this month",
          "limited": "Only the {{count}} most recent fit in this month's contacts",
          "upgrade": "Upgrade",
          "confirm_one": "Send to {{count}} person",
          "confirm_other": "Send to {{count}} people"
        }
      },
      "table": {
        "columns": {
          "triggers": "Triggers",
          "conversions": "Conversions",
          "executions": "Executions",
          "updated_at": "Updated",
          "last_executed_at": "Executed",
          "status": "Status",
          "scenario_type": "Type",
          "title": "Name",
          "name": "Name"
        },
        "items": {
          "last_7_days": "{{count}} in the last 7 days",
          "no_conversion": "No conversion action",
          "total": "{{count}} in total"
        }
      },
      "trigger_node": {
        "ad_id": "Ad ID",
        "add_keyword_filter": "Add keyword",
        "automated_title": "Automated trigger",
        "initial_keyword_filter": "Filter by keywords",
        "keyword": "Trigger keyword",
        "manual_title": "Manual trigger",
        "media_username": "Content creator username",
        "on_post_by": "on a post by",
        "story_id": "Story ID",
        "when": "When",
        "with_intent": "with intent",
        "with_keyword": "with keyword",
        "keywords_notice": "Keywords ignore uppercase and accents",
        "strict_keywords_match_label": "Only match DMs that contain exactly one keyword without extra text"
      },
      "trigger_type": {
        "title": "Add a trigger",
        "categories": {
          "comments": "Comments",
          "direct_messages": "Direct Messages",
          "mentions": "Mentions",
          "contacts": "Contacts",
          "external": "External",
          "integrations": "Integrations"
        },
        "category_descriptions": {
          "comments": "Triggers from comments on your content",
          "direct_messages": "Triggers on message received or sent",
          "mentions": "Triggers when you're tagged in content",
          "contacts": "Triggers from contact creation or updates",
          "external": "Triggers from external sources",
          "integrations": "Triggers from third-party integrations"
        },
        "all": {
          "title": "All triggers"
        },
        "no_triggers": {
          "title": "No triggers"
        },
        "comment": {
          "keyword_notice": "Add this keyword or hashtag anywhere in your post's caption.",
          "label": "Someone comments on your post",
          "description": "Trigger when users comment on your posts",
          "options": {
            "any": "Any post",
            "caption_keyword": "Posts with a keyword in the caption",
            "keywords": "Comments that include a keyword",
            "specific": "A specific post"
          },
          "title": "Comments"
        },
        "live_comment": {
          "label": "Someone comments on your Live",
          "description": "Trigger during live video streams",
          "options": {
            "any": "Any comment",
            "keywords": "Comments that include a keyword"
          },
          "title": "Live Comments"
        },
        "folder": {
          "label": "Contact is added to a folder",
          "description": "Trigger when contacts are organized into folders",
          "select_folder": "Select folder",
          "title": "Folder"
        },
        "contact_create": {
          "label": "Contact is created",
          "description": "Trigger when a new contact is created",
          "title": "Contact created",
          "status_label": "Contact status",
          "status_options": {
            "all": "All contacts",
            "active": "Active contacts only"
          }
        },
        "contact_update": {
          "label": "Contact is updated",
          "description": "Trigger when a contact's attributes, folders, or custom properties change",
          "title": "Contact updated",
          "status_label": "Contact status",
          "status_options": {
            "all": "All contacts",
            "active": "Active contacts only",
            "all_notice": "You can only send DMs to active contacts"
          }
        },
        "recurring": {
          "label": "On a recurring schedule",
          "description": "Trigger automatically at regular intervals for a selected audience",
          "title": "Recurring schedule",
          "options_description": "Choose how often this scenario runs and which contacts it runs for",
          "schedule_label": "Schedule",
          "frequency_label": "How often",
          "time_label": "Time",
          "day_of_week_label": "Day of week",
          "day_of_month_label": "Day of month",
          "only_new_contacts_label": "Only new contacts matching filters",
          "only_new_contacts_badge": "New contacts only",
          "audience_label": "Audience",
          "audience_description": "Select the contacts this scenario should run for on each occurrence",
          "all_contacts": "All contacts",
          "filters_count": "{{count}} filter",
          "filters_count_plural": "{{count}} filters",
          "add_filter": "Add filter",
          "summary": {
            "daily": "Every day at {{time}}",
            "weekly": "Every {{day}} at {{time}}",
            "monthly": "Monthly on day {{day}} at {{time}}"
          },
          "frequencies": {
            "daily": "Daily",
            "weekly": "Weekly",
            "monthly": "Monthly"
          },
          "days_of_week": {
            "0": "Sunday",
            "1": "Monday",
            "2": "Tuesday",
            "3": "Wednesday",
            "4": "Thursday",
            "5": "Friday",
            "6": "Saturday"
          }
        },
        "icebreaker": {
          "label": "Someone clicks an ice breaker",
          "description": "Trigger when users select a conversation starter",
          "title": "Ice breaker",
          "select_icebreaker": "Select ice breaker",
          "no_icebreaker": "No ice breaker selected",
          "notice": "Ice breakers are shown the first time contacts open a conversation with you."
        },
        "mention_comment": {
          "label": "Someone mentions you in a comment",
          "description": "Trigger when tagged in post comments",
          "title": "Comment mentions"
        },
        "mention_media": {
          "label": "Someone mentions you in a post",
          "description": "Trigger when tagged in feed posts",
          "title": "Post mentions",
          "options_label": "Filter posts",
          "options_description": "Set up filters to trigger the scenario only on specific post mentions",
          "options": {
            "any": "Any post that mentions you",
            "keywords": "Keyword in caption or visual"
          },
          "keyword_notice": "Keyword must be **clearly visible** in the visual or in the caption."
        },
        "messages": {
          "keyword_notice": "Keywords ignore case and accents.",
          "label": "Someone sends you a DM",
          "description": "Trigger when you receive a direct message",
          "options_description": "Set up filters to trigger the scenario only on specific messages",
          "options_label": "Filter messages",
          "scope_label": "Which messages",
          "scope": {
            "any": "All messages",
            "first_message": "First message only"
          },
          "content_label": "Message content",
          "content": {
            "any": "Any content",
            "keywords": "Contains keyword(s)",
            "only_emojis": "Only emojis",
            "ai_detected": "AI-detected intent"
          },
          "content_descriptions": {
            "any": "No filter on what the message says",
            "keywords": "Only messages containing one of the keywords you define",
            "only_emojis": "Only messages made up of emojis, with no text",
            "ai_detected": "Only messages matching an intent you describe in your own words"
          },
          "preview": {
            "keywords": "message containing {{keywords}}",
            "any": "any message",
            "matched": "Triggers",
            "grouping_notice": "Messages sent close are grouped together.",
            "conversation": {
              "first": "Hi!",
              "second": "Is this still available?",
              "third": "And do you ship to Spain?",
              "reply": "Thanks for your message! 👋"
            },
            "outgoing": {
              "manual": "Hey! Loved your latest post 👋",
              "automated": "Are you taking on new collabs this month?"
            }
          },
          "title": "Incoming DMs",
          "ai_intent_label": "Describe message intent",
          "ai_intent_notice": "Be as precise and specific as possible to get better results.",
          "ai_intent_warning": "AI-detected intents may not always be accurate.",
          "ai_intent_test": "Test intent detection"
        },
        "outgoing_message": {
          "keyword_notice": "Keywords are case-sensitive. Automated messages will not trigger the scenario.",
          "label": "You send a DM to someone",
          "description": "Trigger when you send a direct message",
          "options_description": "Set up filters to trigger the scenario only on specific messages",
          "options": {
            "any": "Any message",
            "keywords": "Messages with a keyword"
          },
          "title": "Outgoing DMs",
          "sent_from": {
            "label": "Trigger on messages sent",
            "instagram": "Manually from Instagram",
            "inbox": "Manually from Inrō",
            "agent": "From AI Agent",
            "automation": "From automations",
            "api": "From API or MCP"
          }
        },
        "referral": {
          "link_notice": "Referral links open the conversation with you on Instagram. <0>Learn more</0>",
          "icebreaker": "Icebreaker",
          "icebreaker_notice": "Links only trigger if contacts message you. Set up an icebreaker to encourage them!",
          "desktop_notice": "Referral link automations are not supported on Instagram Web.",
          "label": "Someone clicks a referral link",
          "description": "Trigger when users click your referral links or scan QR codes",
          "options": {
            "any": "Any link",
            "ref_code": "Link with a specific ref"
          },
          "title": "Referral link",
          "copy_link": "Copy link",
          "qr_code_trigger": "Someone scans a QR code",
          "qr_code": "Get QR code",
          "qr_code_notice": "Ask your contacts to scan this QR code to open the conversation with you on Instagram.",
          "wildcard_hint": "Use * as a wildcard: campaign_* matches campaign_summer, campaign_winter, etc."
        },
        "referral_ad": {
          "label": "Someone replies to your ad",
          "description": "Trigger when users respond to Meta ads",
          "option_label": "Identify your ad",
          "options_label": "Match type",
          "options_description": "Choose how to identify the ad that triggers this scenario",
          "options": {
            "keywords": "Incoming question",
            "ad_id": "Specific ad ID"
          },
          "title": "Ad replies",
          "notice": "You can find this information in your Meta Ads Manager.",
          "help_title": "Need help?",
          "help_description": "The **Meta Ads Manager** can be hard to use so you might want to read this article or get in touch with us for advice.",
          "preview": {
            "title": "Message {{username}}",
            "greeting": "Hi! Please let us know how we can help you.",
            "write_message": "Write your own message",
            "example_question_1": "Where are you located?",
            "example_question_2": "Can you check the price of a product?",
            "example_question_3": "Can I make a purchase?",
            "ad_notice": "You opened this conversation through an ad. When you reply, {{username}} will be able to see your public info and which ad you clicked."
          },
          "welcome_flow": {
            "label": "Welcome message flow",
            "options_description": "Configure the message users see when they click your Instagram ad",
            "greeting_label": "Greeting message",
            "greeting_placeholder": "Enter a greeting for users who click your ad...",
            "quick_replies_label": "Quick reply buttons",
            "help_title": "What is a welcome message flow?",
            "help_description": "When someone clicks your Instagram ad and opens a DM, they'll see your greeting and quick reply buttons. After creating this flow, link it to your ad in Meta Ads Manager.",
            "greeting_missing": "No greeting message set",
            "quick_reply_placeholder": "Button label (max 20 characters)"
          }
        },
        "story_mention": {
          "label": "Someone mentions you in a story",
          "description": "Trigger when tagged in Instagram stories",
          "title": "Story mentions",
          "options_label": "Filter stories",
          "options_description": "Set up filters to trigger the scenario only on specific story mentions",
          "options": {
            "any": "Any story that mentions you",
            "keywords": "Stories with visible keyword(s)",
            "visual_ai": "Stories showing something"
          },
          "keyword_notice": "Keyword must be **clearly visible** in the whole story.",
          "visual_condition_label": "What the story shows",
          "options_descriptions": {
            "any": "No filter on the story you were mentioned in",
            "keywords": "Only stories where a keyword is legible",
            "visual_ai": "Only stories whose visual matches your description"
          }
        },
        "story_reply": {
          "label": "Someone replies to your story",
          "description": "Trigger when users reply to your stories",
          "options_label": "Filter replies",
          "options_description": "Set up filters to trigger the scenario only on specific story replies",
          "story_label": "Which story",
          "story": {
            "any": "Any story",
            "caption_keyword": "Stories with a keyword",
            "caption_url": "Stories with a URL",
            "visual_ai": "Stories showing something"
          },
          "story_descriptions": {
            "any": "No filter on which story someone replied to",
            "caption_keyword": "Only stories whose text contains a keyword",
            "caption_url": "Only stories that contain a URL as text or sticker",
            "visual_ai": "Only stories whose visual matches your description"
          },
          "content_label": "Which replies",
          "content": {
            "any": "Any reply",
            "keywords": "Contains a keyword",
            "only_emojis": "Only emojis",
            "ai_detected": "AI-detected intent"
          },
          "content_descriptions": {
            "any": "All replies to the stories selected above",
            "keywords": "Only replies containing one of the keywords you define",
            "only_emojis": "Only replies made up of a single emoji, with no text",
            "ai_detected": "Only replies matching an intent you describe in your own words"
          },
          "preview": {
            "keywords": "reply containing {{keywords}}",
            "ai_intent_missing": "Story reply with intent"
          },
          "keyword_notice": "Add the keyword anywhere as text in your story. **You must create the story in the Instagram app.**",
          "caption_url_notice": "Add the URL as text or sticker in your story. **You must create the story in the Instagram app.**",
          "caption_url_any": "Any URL",
          "title": "Story replies",
          "ai_intent_label": "Describe reply intent",
          "ai_intent_notice": "Be as precise and specific as possible to get better results.",
          "ai_intent_warning": "AI-detected intents may not always be accurate.",
          "visual_condition_label": "What the story shows"
        },
        "webhook": {
          "label": "Webhook is called",
          "description": "Trigger via external API calls and integrations",
          "notice": "You need to send a POST request with a parameter to identify your contact.",
          "placeholder": "Webhook URL available after saving",
          "title": "Webhooks"
        },
        "widget": {
          "label": "HTML Widget code is sent",
          "description": "Trigger from embedded widget on your website",
          "title": "Widget",
          "notice": "Add this HTML code to your website to trigger the scenario when someone clicks the button and sends the code.",
          "configure_widget": "Configure widget",
          "snippet_label": "HTML code to copy",
          "custom_style_label": "Enable styling with custom CSS",
          "custom_style_notice": "By checking this box, you can add custom CSS on your website to style the widget elements.",
          "header_text_label": "Custom header text",
          "header_text_placeholder": "Copy this code and send it to us on Instagram",
          "button_text_label": "Custom button text",
          "button_text_placeholder": "Send code"
        },
        "custom_event": {
          "label": "A custom event is triggered",
          "title": "Custom event",
          "select_event": "Select event"
        },
        "shopify_orders_create": {
          "label": "A Shopify order is created",
          "description": "Trigger when a new order is created in your Shopify store",
          "title": "Shopify Order Created",
          "contact_matching_notice": "Matches contacts that visited your website from an automation link. [Learn more](https://help.inro.social/en/articles/10244744-how-to-connect-a-shopify-store)"
        },
        "shopify_cart_abandoned": {
          "label": "A Shopify cart is abandoned",
          "description": "Trigger when a cart is abandoned after 24 hours in your Shopify store",
          "title": "Shopify Cart Abandoned",
          "contact_matching_notice": "Matches contacts that visited your website from an automation link. [Learn more](https://help.inro.social/en/articles/10244744-how-to-connect-a-shopify-store)"
        },
        "calendly_invitee_created": {
          "label": "A Calendly invitee is created",
          "description": "Trigger when a new invitee is created in your Calendly account",
          "title": "Calendly New Invitee"
        },
        "visual_condition": {
          "placeholder": "e.g. shows one of our sneakers",
          "notice": "Be specific: *a red sneaker on white* beats *our product*.",
          "warning": "Visual recognition can be wrong. Adjust from the results.",
          "examples": [
            "one of our products",
            "an order confirmation",
            "someone wearing our brand",
            "a restaurant photo"
          ]
        }
      },
      "triggers": {
        "add_trigger": "Add trigger",
        "no_triggers": "No triggers",
        "triggered_by_scenario": "Triggered by another automation",
        "triggered_by_agent": "Triggered by AI Agent",
        "trigger_options": "Trigger options"
      },
      "executions": {
        "not_found": "Scenario not found",
        "pending": "Scenarios pending",
        "started": "Started {{date}}",
        "status_on": "{{status}} at {{date}}",
        "no_executions": "No history found",
        "needs_attention": "Needs attention",
        "run_now": "Run",
        "cancel_confirm": "Cancel this execution? It cannot be recovered.",
        "actions_trail": "Actions of this run",
        "earlier_steps_one": "{{count}} earlier step",
        "earlier_steps_other": "{{count}} earlier steps",
        "view_conversation": "View conversation",
        "limited": "The scenario was not executed because you've reached your monthly limit.",
        "statuses": {
          "all": "All executions",
          "cancelled": "Cancelled",
          "completed": "Completed",
          "failed": "Failed",
          "interrupted": "Interrupted",
          "pending": "Pending",
          "running": "Running",
          "scheduled": "Scheduled",
          "started": "Started",
          "waiting": "Waiting",
          "expired": "Expired",
          "limited": "Limit reached",
          "disconnected": "Disconnected",
          "restricted": "Paused by Instagram",
          "skipped": "Skipped",
          "partially_completed": "Partially completed"
        },
        "title": "Automation history",
        "triggered_by": "Triggered by",
        "interrupted": "Interrupted",
        "triggered_on": "Triggered on {{date}}",
        "tests": "Tests",
        "no_tests": "No tests yet",
        "full_history": "Open full history"
      },
      "filters": {
        "status": {
          "active": "Active scenarios",
          "all": "All scenarios",
          "inactive": "Inactive scenarios"
        },
        "type": {
          "all": "All scenario types",
          "comment_to_dm": "Comment-to-DM scenarios",
          "comment_moderation": "Comment moderation scenarios",
          "custom": "Custom scenarios"
        },
        "is_template": {
          "all": "All scenarios",
          "true": "Templates",
          "false": "Non-templates"
        }
      },
      "metrics": {
        "executions_one": "execution",
        "executions_other": "executions",
        "conversions_one": "conversion",
        "conversions_other": "conversions",
        "last_7_days": "Last 7 days",
        "total": "Total",
        "conversions_help": "All clicks on conversion links are counted as conversions."
      },
      "name_label": "Name",
      "inactive_warning": "The selected scenario is inactive and will not be triggered.",
      "warnings": {
        "title": "Warnings",
        "suggestion": "Suggestion",
        "issue": "Issue",
        "issues_notice": "This automation has unresolved issues that may prevent it from working as expected.",
        "view_issues": "View issues",
        "activate_confirm": {
          "title": "This automation still has unresolved issues",
          "body": "You can activate it anyway, but these issues may prevent it from working as expected.",
          "confirm": "Activate anyway",
          "cancel": "Go back",
          "triggers_label": "Triggers",
          "steps_label": "Steps"
        }
      },
      "generation_prompt": {
        "title": "What do you want to automate?",
        "placeholder": "Send the booking link to anyone who DMs the word BOOK",
        "example": "Try: Say thank you when someone mentions me in their story",
        "example_prompt": "When someone mentions me in their story, thank them and send a discount code",
        "button": "Generate automation"
      },
      "view_scenario": "View scenario",
      "test_scenario": {
        "title": "Send yourself this code as a DM or comment to test the scenario",
        "or_select_contact": "or select a contact"
      },
      "search_no_results": "No scenarios found",
      "classification": {
        "refuse": {
          "title": "This is not possible",
          "alternatives_label": "Start one of these instead",
          "back": "Write something else",
          "patterns": {
            "rate_limited": "You have reached the AI generation limit for now. Try again later, or build this automation yourself.",
            "new_follower_unsupported": "Instagram never tells us when someone follows you. But you can start one of these instead.",
            "bulk_outbound_unsupported": "Instagram only lets you message people who messaged you first. But you can start one of these instead.",
            "engagement_unsupported": "Instagram does not report likes to us. But you can start one of these instead."
          },
          "campaign_badge": "Campaign"
        },
        "route": {
          "back": "Write something else",
          "targets": {
            "agent_trigger_rules": {
              "title": "Your AI Agent does this",
              "description": "Replying to every DM is an Agent trigger rule, not a scenario.",
              "cta": "Open Agent settings"
            },
            "agent_handover": {
              "title": "Your AI Agent does this",
              "description": "Handing a conversation to a person is an Agent handover rule.",
              "cta": "Open handover rules"
            },
            "campaigns": {
              "title": "Campaigns do this",
              "description": "Messaging a list of people at once is what Campaigns are for.",
              "cta": "Open Campaigns"
            },
            "content": {
              "title": "Publishing happens in Content",
              "description": "Create and schedule posts and stories from the Content page. Automations react to them once they are live.",
              "cta": "Open Content"
            }
          }
        },
        "decline": {
          "notice": "This is not supported, describe an Instagram automation instead."
        },
        "clarify": {
          "trigger_label": "Trigger",
          "outcome_label": "What should happen",
          "outcome_placeholder": "Ask for their email then send a link to the website",
          "submit": "Continue",
          "back": "Start over",
          "triggers": {
            "comment": "When someone comments \"{{keyword}}\" on any post",
            "dm": "When someone DMs you \"{{keyword}}\""
          },
          "url": {
            "share": {
              "question": "Add this shared automation to your account?",
              "cta": "Add it to my account"
            },
            "own": {
              "question": "This is one of your automations.",
              "duplicate": "Duplicate it",
              "open": "Open it"
            },
            "destination": {
              "question": "What should send this link?",
              "placeholder": "When someone DMs the word GUIDE"
            },
            "alternative": "Build something else"
          }
        },
        "status": {
          "title": "No automation was built",
          "prompt_label": "Your prompt",
          "edit_cta": "Edit the prompt"
        }
      },
      "mcp_preview": {
        "active": "Active",
        "inactive": "Inactive",
        "steps_one": "{{count}} step",
        "steps_other": "{{count}} steps",
        "warnings_one": "{{count}} warning",
        "warnings_other": "{{count}} warnings",
        "expand": "Expand",
        "open": "Open in Inrō",
        "error": "This scenario can't be shown here.",
        "too_large": "This scenario is too large to show here.",
        "outdated": "Reconnect Inrō to show this scenario.",
        "campaign_status": {
          "draft": "Draft",
          "running": "Sending",
          "completed": "Sent",
          "scheduled": "Scheduled",
          "limited": "Limited",
          "canceled": "Canceled",
          "disconnected": "Disconnected"
        },
        "campaign_error": "This campaign can't be shown here.",
        "campaign_too_large": "This campaign is too large to show here.",
        "campaign_outdated": "Reconnect Inrō to show this campaign.",
        "recipients": "Will send to",
        "contacts_one": "contact",
        "contacts_other": "contacts",
        "count_unavailable": "Count unavailable",
        "included": "Included",
        "excluded": "Excluded",
        "all_contacts": "All contacts",
        "selected_contacts": "Selected contacts",
        "contact_filters": "Contact filters",
        "folder": "Folder",
        "segment": "Segment"
      }
    },
    "variables": {
      "learn_more": "Learn more about variables",
      "search_placeholder": "Search variables...",
      "no_results": "No variables match your search.",
      "column_name": "Variable",
      "column_code": "Code",
      "column_description": "Description",
      "contact": {
        "title": "Contact information",
        "description": "Insert contact information in your messages and scenarios.",
        "fields": {
          "id": "Contact ID",
          "name": "Name",
          "username": "Username",
          "email": "Email",
          "phone": "Phone",
          "follower_count": "Follower count",
          "profile_picture": "Profile picture URL"
        },
        "field_descriptions": {
          "id": "Unique identifier for this contact",
          "name": "Contact's display name",
          "username": "Their Instagram handle",
          "email": "Contact's email address",
          "phone": "Contact's phone number",
          "follower_count": "Number of Instagram followers",
          "profile_picture": "Link to their profile photo"
        }
      },
      "organization": {
        "title": "Account information",
        "description": "Insert information about your account.",
        "fields": {
          "username": "Username",
          "name": "Name",
          "inro_id": "Inrō ID"
        },
        "field_descriptions": {
          "username": "Your Instagram handle",
          "name": "Your account display name",
          "inro_id": "Your unique Inrō account ID"
        }
      },
      "trigger": {
        "title": "Scenario information",
        "description": "These variables will only appear in the context of a scenario.",
        "fields": {
          "message": "Trigger message",
          "comment": "Trigger comment",
          "date": "Current date and time",
          "ref": "Referral code",
          "media_caption": "Post caption",
          "caption_url": "Post caption URL",
          "caption_keyword": "Matched caption keyword",
          "comment_keyword": "Matched comment keyword",
          "story_caption": "Story caption",
          "story_text": "Story text"
        },
        "field_descriptions": {
          "message": "The message that triggered this scenario",
          "comment": "The comment that triggered this scenario",
          "date": "Date and time when the scenario runs",
          "ref": "Referral code passed via trigger link",
          "media_caption": "Caption of the post that triggered this",
          "caption_url": "URL extracted from the post caption",
          "caption_keyword": "The keyword matched in the post caption",
          "comment_keyword": "The keyword matched in the comment",
          "story_caption": "Caption text from the replied-to story",
          "story_text": "Text extracted from the story mention visual"
        }
      },
      "conversation": {
        "title": "Conversation information",
        "description": "These variables relate to the current conversation with the contact.",
        "fields": {
          "last_message": "Last message",
          "last_message_received": "Last received message",
          "last_message_sent": "Last sent message",
          "last_message_date": "Last message date"
        },
        "field_descriptions": {
          "last_message": "The most recent message in the conversation",
          "last_message_received": "The most recent message received from the contact",
          "last_message_sent": "The most recent message sent to the contact",
          "last_message_date": "Date and time of the most recent message"
        }
      },
      "mention": {
        "title": "Mention information",
        "description": "These variables will only appear when the scenario is triggered by a mention.",
        "fields": {
          "text": "Mention text",
          "media_caption": "Post caption",
          "media_url": "Post media URL",
          "media_permalink": "Post permalink",
          "media_username": "Post author username"
        },
        "field_descriptions": {
          "text": "The text content of the mention",
          "media_caption": "Caption of the post where you were mentioned",
          "media_url": "URL of the media in the post where you were mentioned",
          "media_permalink": "Permalink to the post where you were mentioned",
          "media_username": "Username of the post author"
        }
      },
      "webhook": {
        "title": "Webhook information",
        "description": "These variables will only appear if the scenario is triggered by a webhook.",
        "fields": {
          "url": "Webhook URL",
          "body": "Webhook body"
        },
        "field_descriptions": {
          "url": "URL that sent the webhook request",
          "body": "Raw body of the webhook payload"
        }
      },
      "survey": {
        "title": "Survey reply",
        "description": "These variables will only appear after a survey is replied to.",
        "fields": {
          "date": "Survey completion date",
          "option.title": "Title of option picked",
          "option.replies": "Number of times option was picked",
          "option.percentage": "Percentage that picked the option"
        },
        "field_descriptions": {
          "date": "Date the survey response was submitted",
          "option.title": "Name of the option the contact selected",
          "option.replies": "Total number of times this option was chosen",
          "option.percentage": "Share of respondents who picked this option"
        }
      },
      "questions_survey": {
        "title": "Survey completion",
        "description": "These variables will only appear after a survey is completed. Replace '0' with the question's index.",
        "fields": {
          "date": "Survey completion date",
          "question_0.title": "Title of first question",
          "question_0.reply": "Reply first question"
        },
        "field_descriptions": {
          "date": "Date the survey was fully completed",
          "question_0.title": "The text of the first question",
          "question_0.reply": "The answer given for the first question"
        }
      },
      "shopify_order": {
        "title": "Shopify order",
        "description": "These variables are available when the scenario is triggered by a Shopify order.",
        "fields": {
          "order_number": "Order number",
          "total_price": "Total price",
          "subtotal_price": "Subtotal",
          "currency": "Currency",
          "financial_status": "Payment status",
          "created_at": "Order date",
          "order_status_url": "Order status URL",
          "customer.first_name": "Customer first name",
          "customer.last_name": "Customer last name",
          "customer.email": "Customer email"
        },
        "field_descriptions": {
          "order_number": "The unique number identifying this order (e.g. #1001)",
          "total_price": "Total amount charged including taxes and shipping",
          "subtotal_price": "Order subtotal before taxes and shipping",
          "currency": "Three-letter currency code (e.g. USD)",
          "financial_status": "Payment status of the order (e.g. paid, pending)",
          "created_at": "Date and time the order was placed",
          "order_status_url": "Order status page URL where customers can track their order",
          "customer.first_name": "Customer's first name",
          "customer.last_name": "Customer's last name",
          "customer.email": "Customer's email address"
        }
      },
      "shopify_cart": {
        "title": "Shopify abandoned cart",
        "description": "These variables are available when the scenario is triggered by an abandoned cart.",
        "fields": {
          "abandoned_checkout_url": "Cart recovery URL",
          "total_price": "Cart total",
          "subtotal_price": "Cart subtotal",
          "currency": "Currency",
          "created_at": "Cart creation date",
          "email": "Customer email"
        },
        "field_descriptions": {
          "abandoned_checkout_url": "Unique URL the customer can use to recover their cart",
          "total_price": "Total cart value including taxes and shipping",
          "subtotal_price": "Cart subtotal before taxes and shipping",
          "currency": "Three-letter currency code (e.g. USD)",
          "created_at": "Date and time the checkout was created",
          "email": "Email address of the customer who abandoned the cart"
        }
      },
      "button": {
        "title": "Card button click",
        "description": "These variables will only appear after a card template button is clicked. Reflects the last clicked button.",
        "fields": {
          "label": "Button label",
          "payload": "Button payload",
          "card_title": "Card title",
          "card_text": "Card text"
        },
        "field_descriptions": {
          "label": "The text label of the clicked button",
          "payload": "The postback payload of the clicked button",
          "card_title": "The title of the card whose button was clicked",
          "card_text": "The subtitle text of the card whose button was clicked"
        }
      }
    },
    "scenario_insights": {
      "filter_by_date": "Filter by date"
    },
    "scenario_folders": {
      "new_folder": "New folder",
      "breadcrumb_root": "Scenarios",
      "types": {
        "priority": "Priority folder",
        "normal": "Normal folder"
      },
      "type_descriptions": {
        "priority": "Only one scenario can trigger at a time.",
        "normal": "Scenarios are triggered independently."
      },
      "type_label": "Folder type",
      "name_label": "Folder name",
      "name_placeholder": "Enter folder name",
      "create_title": "Create a folder",
      "edit_title": "Edit folder",
      "delete_title": "Delete \"{{name}}\"",
      "scenario_count": "{{count}} scenario",
      "scenario_count_one": "{{count}} scenario",
      "scenario_count_other": "{{count}} scenarios",
      "delete_prompt": "This folder contains {{count}} scenario(s). What should happen to them?",
      "delete_ungroup": "Keep folder scenarios",
      "delete_ungroup_description": "Scenarios will be removed from the folder.",
      "delete_scenarios": "Delete {{count}} scenarios in folder",
      "delete_scenarios_description": "All scenarios in this folder will be permanently deleted.",
      "delete_empty_prompt": "Are you sure you want to delete this folder?",
      "drag_hint": "Drop here to add to folder",
      "priority_set_by_order": "Priority is set by drag order in the folder view",
      "all_scenarios": "All scenarios",
      "folder_empty": "This folder is empty",
      "start_confirm": "Do you want to activate all scenarios in this folder?",
      "pause_confirm": "Do you want to pause all scenarios in this folder?",
      "duplicate_confirm": "Duplicate this folder and its {{count}} scenario(s)?"
    }
  },
  "automation_logs": {
    "title": "Automations",
    "search": "Search by contact",
    "empty": "No automation ran yet",
    "deleted": "Deleted",
    "types": {
      "scenario_execution": "Scenario",
      "campaign_execution": "Campaign"
    },
    "filters": {
      "all_types": "All types",
      "all_statuses": "All statuses"
    },
    "table": {
      "columns": {
        "execution_type": "Type",
        "runnable": "Name",
        "contact": "Contact",
        "status": "Status",
        "created_at": "Date"
      }
    }
  },
  "billing": {
    "title": "Billing & usage",
    "enable_feature": {
      "title": "Feature unavailable",
      "description": "You need to upgrade your subscription to activate this feature.",
      "button": "Activate now"
    }
  },
  "calendar": {
    "calendars": {
      "connect_button": "Connect a Google account",
      "connect_description": "You can connect a Google account to view your availabilities and save all your appointments.",
      "connect_title": "Connect calendars",
      "connect_tooltip": "Connect your Google Calendar",
      "make_primary_description": "This is the calendar that will be used to save your appointments. You can change it at any time.",
      "make_primary_title": "Default calendar",
      "manage_description": "Hide and show your calendars and select the default one for your appointments.",
      "manage_title": "Manage calendars"
    },
    "details": {
      "cancel_all_propositions": "Cancel all propositions",
      "confirm_slot": "Confirm this slot",
      "edit_appointment": "Edit appointment",
      "remove_payment": "Remove payment",
      "remove_slot": "Remove this slot",
      "sent_on": "Sent on {{date}}",
      "timezone_warning": "This appointment was created with a different time zone. The times shown below are for: {{zone}}.",
      "title": "Appointment details"
    },
    "duration": {
      "label": "Duration",
      "title": "Pick event duration"
    },
    "empty_state": {
      "button": "Set up appointments",
      "description": "Start sending appointment propositions or set up your calendar settings to get started.",
      "title": "No appointments yet!"
    },
    "help": {
      "text": "You can send appointment propositions to your contacts directly inside your conversation. Pick some slots and wait for them to confirm.",
      "title": "How to use the calendar"
    },
    "message": {
      "add_to_calendar": "Add to calendar",
      "appointment_already_pending_message": "You already have a pending appointment proposition with this contact. Do you want to cancel it?",
      "appointment_already_pending_no": "No, keep it",
      "appointment_already_pending_title": "Proposition pending",
      "appointment_already_pending_yes": "Yes, cancel it",
      "awaiting": "Awaiting confirmation",
      "calendar_invite_title": "Appointment with {{name}}",
      "cancel": "Cancel appointment",
      "cancel_confirmation": "Are you sure you want to cancel this appointment?",
      "cancelled": "Cancelled",
      "cancelled_message": "This appointment proposition has been cancelled.",
      "cannot_confirm": "You can't confirm this appointment.",
      "confirm_confirmation": "Confirm appointment manually?",
      "confirm_date": "Confirm date",
      "confirm_information": "Confirm information",
      "confirmed": "Confirmed",
      "confirmed_message": "Your appointment date is confirmed!",
      "expired": "Expired",
      "expired_message": "This appointment proposition has expired.",
      "go_to_payment": "Go to payment",
      "information_notice": "Please confirm or fill the following information to confirm your appointment.",
      "label": "{{ name }} has proposed the following slots for the appointment, choose the one that works for you.",
      "label_with_payment": "{{ name }} sent you slots for your appointment. You will have to pay {{ amount }} to confirm.",
      "not_found": "Couldn't find this appointment proposition. It might have been cancelled.",
      "payment_attempted": "A payment was attempted but did not succeed. Select a slot to confirm the appointment without payment.",
      "payment_attempted_public": "A payment was attempted but did not succeed. Please try again or contact the business.",
      "payment_needed": "To confirm the appointment, you'll have to pay {{ amount }}.",
      "remove_payment": "Remove mandatory payment",
      "title": "Appointment proposition",
      "unavailable": "The time slot you selected is no longer available. Please select another one.",
      "view_details": "View appointment details",
      "view_terms": "View terms of sale"
    },
    "settings": {
      "google_calendar": {
        "add": "Add a Google account",
        "description": "You can connect a Google account to save all your new meetings in your calendar.",
        "refresh": "Refresh calendars",
        "remove": "Remove Google account",
        "title": "Google Calendar"
      },
      "phone": {
        "description": "When your client books, ask for a phone number to contact them.",
        "title": "Ask for phone number"
      },
      "reminder": {
        "description": "Your client will receive an automatic message to remind them of their appointment.",
        "disabled": "Disabled",
        "four_days_before": "4 days before",
        "morning": "At 8:00 on the day",
        "title": "Set automatic reminder",
        "yesterday": "24h before"
      },
      "terms": {
        "description": "Your clients will need to accept your terms to book an appointment.",
        "input": "Link to your terms",
        "title": "Ask to accept terms"
      },
      "time_zone": {
        "description": "Your calendar and your appointments will be displayed in this time zone.",
        "title": "Time zone"
      },
      "title": "Calendar settings"
    },
    "slots": {
      "confirm": {
        "add_payment": "Add a mandatory payment",
        "cancel_payment": "Remove payment",
        "description": "Your client will be able to choose among these slots.",
        "later_notice": "You can always send a payment link later.",
        "no_payment": "Send without payment",
        "payment_description": "The appointment will be confirmed only after the payment is made.",
        "payment_title": "Request a payment",
        "send": "Send proposition",
        "title": "Confirm slots"
      },
      "select": {
        "confirm": "Confirm slot proposition",
        "selected_one": "slot selected",
        "selected_other": "slots selected",
        "title": "Select slots to propose"
      }
    },
    "title": "Calendar"
  },
  "campaigns": {
    "contacts_count_one": "1 contact",
    "contacts_count_other": "{{count}} contacts",
    "contacts_count_capped_one": "{{count}}+ contact",
    "contacts_count_capped_other": "{{count}}+ contacts",
    "contacts_count_estimation": "Your campaign will be sent to",
    "contacts_count_unavailable": "The number of contacts couldn't be estimated",
    "count_one": "{{count}} campaign",
    "count_other": "{{count}} campaigns",
    "create_campaign": "Create campaign",
    "created_on": "Created on {{date}}",
    "delete_campaign_confirm": "Delete this campaign?",
    "destinations": {
      "all_contacts": {
        "description": "Send to all your contacts",
        "title": "All reachable contacts"
      },
      "pick_contacts": {
        "title": "Specific folder(s) or segment(s)",
        "none_selected": "Select at least one folder or segment."
      },
      "contacts_count_capped_one": "{{count}}+ contact",
      "contacts_count_capped_other": "{{count}}+ contacts",
      "contacts_count_one": "1 contact",
      "contacts_count_other": "{{count}} contacts",
      "folder": {
        "description": "All contacts added to a folder",
        "label": "Folder"
      },
      "label": "Send to",
      "segment": {
        "description": "All contacts matching your segment filters",
        "label": "Segment"
      },
      "folders": {
        "count_one": "{{count}} folder",
        "count_other": "{{count}} folders",
        "description": "All contacts in a folder",
        "title": "Folders"
      },
      "segments": {
        "count_one": "{{count}} segment",
        "count_other": "{{count}} segments",
        "description": "All contacts matching the segment's filters",
        "title": "Segments"
      }
    },
    "duplicate_title": "Duplicate campaign to",
    "edit_campaign": "Edit campaign",
    "executions": {
      "pending": "Campaigns pending",
      "limited": "The campaign was not sent to this contact as you've reached your monthly limit."
    },
    "excluding": "excl. {{count}} folders",
    "fields": {
      "folder": "Choose a folder",
      "messages": {
        "add_message": "Add message",
        "label": "Messages",
        "placeholder": "Type your message"
      },
      "scenario": "Choose a scenario",
      "segment": "Choose a segment",
      "title": "Campaign name"
    },
    "filters": {
      "status": {
        "all": "All campaigns",
        "completed": "Completed campaigns",
        "draft": "Draft campaigns",
        "running": "Running campaigns",
        "scheduled": "Scheduled campaigns",
        "sending": "Sending campaigns",
        "canceled": "Canceled campaigns"
      }
    },
    "metrics": {
      "cancelled": "Cancelled",
      "clicked": "Clicked",
      "converted": "Converted",
      "completed": "Completed",
      "failed": "Failed",
      "opened": "Opened",
      "pending": "Pending",
      "running": "Running",
      "read": "Read",
      "sent": "Sent",
      "added": "Added",
      "removed": "Removed",
      "seen": "Seen",
      "liked": "Liked",
      "replied": "Replied",
      "contacts": "Contacts",
      "updated": "Updated",
      "triggered": "Triggered",
      "skipped": "Skipped"
    },
    "exclude_folder": "Exclude a folder",
    "dont_exclude_folder": "Don't exclude any folder",
    "exclude_contacts_time": "Exclude contacts who have received a campaign in the last 72 hours",
    "include_private_replies": "Include contacts who have commented in the last 7 days",
    "limit_reached_warning": "Your campaign won't be sent right away as you have reached your monthly contact activation limit.",
    "limited_executions_one": "<0>{{ count }} contact</0> did not receive the campaign because you've reached your monthly limit.",
    "limited_executions_other": "<0>{{ count }} contacts</0> did not receive the campaign because you've reached your monthly limit.",
    "resend_limited_executions": "Resend to these contacts",
    "disconnected_executions_one": "<0>{{ count }} contact</0> did not receive the campaign because your Instagram account was disconnected.",
    "disconnected_executions_other": "<0>{{ count }} contacts</0> did not receive the campaign because your Instagram account was disconnected.",
    "resend_disconnected_executions": "Send to these contacts",
    "new_campaign": "New campaign",
    "save_as_draft": "Save as draft",
    "save_changes": "Save changes",
    "schedule_campaign": "Schedule campaign",
    "send_test": "Send a test",
    "test_prompt": {
      "title": "Test your campaign before sending it?",
      "confirm": "Send a test first",
      "later": "Cancel",
      "send_anyway": "Send without testing",
      "schedule_anyway": "Schedule anyway"
    },
    "send_test_confirm": "Send a test campaign to this contact?",
    "test_sent": "Your test campaign was sent.",
    "cancel_schedule": "Cancel schedule",
    "cancel_schedule_confirm": "Cancel the schedule?",
    "edit_schedule": "Edit date",
    "scheduled_notice": "This campaign will be sent on **{{date}}**",
    "cancel_campaign": "Cancel campaign",
    "cancel_campaign_confirm": "Do you want to cancel this campaign?",
    "duplicate_campaign": "Duplicate campaign",
    "rules_count_one": "{{count}} rule",
    "rules_count_other": "{{count}} rules",
    "search_campaigns": "Search campaigns",
    "search_no_results": "No campaigns found",
    "send_campaign": "Send campaign",
    "send_campaign_confirm": "Send this campaign?",
    "send_campaign_contact_one": "Your campaign will be sent to {{count}} contact",
    "send_campaign_contact_other": "Your campaign will be sent to {{count}} contacts",
    "send_campaign_unknown_count": "The number of contacts couldn't be estimated. Your campaign will be sent to everyone who matches.",
    "unsaved_changes": "Unsaved changes",
    "warnings": {
      "body_save": "You can save it anyway, but these issues may prevent it from working as expected.",
      "body_send": "It will be sent anyway, but these issues may prevent it from working as expected.",
      "confirm_save": "Save anyway",
      "confirm_schedule": "Schedule anyway",
      "confirm_send": "Send anyway",
      "title": "This campaign still has unresolved issues"
    },
    "sent_on": "Sent on {{date}}",
    "scheduled_on": "Scheduled on {{date}}",
    "select_campaign": "Select a campaign",
    "current_campaign": "Current campaign",
    "inactive_warning": "This campaign is inactive",
    "table": {
      "columns": {
        "title": "Title",
        "name": "Name",
        "status": "Status",
        "sent": "Sent",
        "failed": "Failed",
        "opened": "Opened",
        "created_at": "Created",
        "sent_at": "Sent",
        "metrics": "Metrics"
      }
    },
    "settings": {
      "interrupt_on_message": "Interrupt when you reply manually",
      "is_template": "Save as template"
    },
    "simple_message": "✉️ Simple message",
    "status": {
      "completed": "Completed",
      "draft": "Draft",
      "running": "Running",
      "sending": "Sending",
      "scheduled": "Scheduled",
      "limited": "Limit reached",
      "disconnected": "Disconnected",
      "canceled": "Canceled"
    },
    "title": "Campaigns",
    "no_campaigns": "No campaigns yet",
    "create_first": "Create your first campaign",
    "view_campaign": "View campaign",
    "view_results": "View results",
    "quick_campaigns": {
      "optin_campaign": {
        "title": "Opt-in Campaign",
        "description": "Send a DM to commenters from last 7 days",
        "select_contacts": {
          "title": "Select contacts",
          "description": "Choose which comments should be targeted",
          "which_comments": "Which comments do you want to target?",
          "all_comments": "All comments",
          "comment_keywords": "Comments with keywords",
          "select_posts": "Specific posts",
          "exclude_active_contacts": "Exclude active contacts",
          "keywords_label": "Keywords",
          "chart_title": "Contacts in the last 7 days",
          "matching_contacts": "Contacts who match",
          "seven_day_rule": "Why the last 7 days?",
          "exclude_active_contacts_description": "Active contacts are already in your inbox and don't need to opt-in again for new DMs."
        },
        "send_message": {
          "title": "Send optin message",
          "description": "Configure the message and what happens next",
          "modes": {
            "button": "Message with button",
            "dm": "Simple DM"
          },
          "content_label": "Message",
          "content_placeholder": "Thanks for your recent comment. Do you want more information?",
          "button_label": "Button",
          "button_placeholder": "Yes!",
          "on_click": "When the button is clicked",
          "on_yes": "When the contact replies yes",
          "on_no": "When the contact replies no",
          "reply_placeholder": "Write a reply (optional)",
          "add_to_folder": "Add to folder",
          "added_to": "Added to",
          "yes_reply_example": "Yes",
          "no_reply_example": "No"
        },
        "review": {
          "title": "Review and send",
          "description": "Review your campaign and send it",
          "schedule_notice": "Some contacts might be outside the 7-day window when your campaign sends and will not receive the message."
        }
      }
    },
    "campaign_folders": {
      "new_folder": "New folder",
      "name_label": "Folder name",
      "name_placeholder": "Enter folder name",
      "create_title": "Create a folder",
      "edit_title": "Edit folder",
      "delete_title": "Delete \"{{name}}\"",
      "campaign_count": "{{count}} campaign",
      "campaign_count_one": "{{count}} campaign",
      "campaign_count_other": "{{count}} campaigns",
      "delete_ungroup": "Keep folder campaigns",
      "delete_ungroup_description": "Campaigns will be removed from the folder.",
      "delete_campaigns": "Delete {{count}} campaigns in folder",
      "delete_campaigns_description": "All campaigns in this folder will be permanently deleted.",
      "delete_empty_prompt": "Are you sure you want to delete this folder?",
      "all_campaigns": "All campaigns",
      "folder_empty": "This folder is empty",
      "remove_from_folder": "Remove from folder",
      "duplicate_confirm": "Duplicate this folder and its {{count}} campaign(s)? "
    },
    "templates": {
      "title_label": "Campaign title",
      "title_placeholder": "Name your campaign",
      "new": "New campaign",
      "choose_template": "Choose a template",
      "empty_campaign": "Empty campaign",
      "empty_campaign_description": "Start from scratch and build your own",
      "create_campaign": "Create campaign"
    },
    "dont_send_to": "Don't send campaign to",
    "excluding_one": "{{count}} folders excluded",
    "excluding_other": "{{count}} folders excluded",
    "send_to": "Send campaign to",
    "public": {
      "made_by": "Made by",
      "powered_by": "Powered by",
      "notice": "Send this automated campaign to your contacts on Instagram.",
      "copy_campaign": "Copy this campaign",
      "copy_campaign_confirm": "Copy this campaign to your account?",
      "login_modal_title": "Log in to copy this campaign",
      "signup_modal_title": "Sign up to copy this campaign"
    },
    "share_campaign": "Share campaign",
    "sharing": {
      "title": "Share your campaign",
      "input_label": "Share this link so anyone can copy your campaign",
      "add_referral_code": "Add your referral code to the link"
    },
    "duration_info": "How long will it take to send my campaign?"
  },
  "shopify_link": {
    "message": {
      "button": "View product",
      "products_count": "+ {{count}} products"
    },
    "modal": {
      "title": "Select products to send as links",
      "source": {
        "manual": "Select manually",
        "collection": "From collection",
        "tag": "From product tag",
        "query": "From search query"
      },
      "search_collections": "Search collections",
      "no_collections": "No collections found",
      "products_count_one": "{{count}} product",
      "products_count_other": "{{count}} products",
      "tag_label": "Product tag",
      "tag_placeholder": "e.g. new, sale, summer",
      "sort_key_label": "Sort products by",
      "sort_key": {
        "RELEVANCE": "Relevance",
        "TITLE": "Title",
        "PRICE": "Price",
        "BEST_SELLING": "Best selling",
        "CREATED_AT": "Newest first",
        "UPDATED_AT": "Recently updated"
      },
      "query_label": "Search query",
      "query_placeholder": "e.g. red dresses, leather jackets",
      "link_type_label": "Link destination",
      "link_type": {
        "product": "Link to product page",
        "checkout": "Direct link to checkout"
      },
      "discount_label": "Apply a discount code",
      "discount_placeholder": "e.g. SAVE10",
      "query_notice": "This query will be used to search products in your store and send them as DM links.",
      "tag_notice": "Products with this tag will be sent as DM links.",
      "discount_notice": "This discount will be automatically applied to all DM links you send.",
      "max_products_label": "Max products",
      "max_products_placeholder": "Default: 10"
    },
    "node": {
      "link_type": {
        "product": "Link to product page",
        "checkout": "Direct to checkout"
      },
      "with_discount": "discount {{code}}"
    }
  },
  "comments": {
    "comment_reply": {
      "placeholder": "Enter your comment...",
      "title": "Reply with a comment",
      "your_reply": "Your reply"
    },
    "private_reply": {
      "placeholder": "Enter your message...",
      "title": "Reply with Direct Message",
      "view": "View reply"
    },
    "sort_options": {
      "newest": "Newest first",
      "oldest": "Oldest first",
      "most_replies": "Most replies",
      "most_likes": "Most likes"
    },
    "delete_confirm": "Delete your comment?",
    "title": "Comments",
    "trigger_automation": "Trigger automations",
    "trigger_automation_confirm": "Trigger automations on this comment?",
    "trigger_automation_success": "Automations triggered successfully",
    "triggered_scenarios_one": "Triggered 1 scenario",
    "triggered_scenarios_other": "Triggered {{count}} scenarios",
    "sync_title": "Comments syncing",
    "sync_description": "We are currently syncing your comments. This may take a few minutes."
  },
  "commerce": {
    "products": {
      "not_found": "No products found.",
      "search": "Search products"
    }
  },
  "contacts": {
    "info_unavailable": {
      "personal_account": "Only available for contacts with Business or Creator accounts",
      "no_message": "Not available until this contact has sent a message."
    },
    "all_contacts": "All contacts",
    "clear_selection": "Clear selection",
    "filters": {
      "add_filter": "Add filter",
      "any": "Any engagement",
      "categories": {
        "all": {
          "description": "All",
          "title": "All"
        },
        "any_engagement": {
          "description": "Any engagement",
          "title": "Any engagement"
        },
        "first_interaction": {
          "description": "First interaction was",
          "title": "First interaction"
        },
        "contact_created_at": {
          "description": "Contact created",
          "title": "Contact created at"
        },
        "appointment": {
          "description": "Received an appointment",
          "title": "Appointment"
        },
        "campaign": {
          "description": "Received a campaign message",
          "title": "Campaign"
        },
        "clicked_conversion_link": {
          "description": "Clicked on a conversion link",
          "title": "Clicked conversion link"
        },
        "comment": {
          "description": "Commented on a post",
          "title": "Comment"
        },
        "email": {
          "description": "Email",
          "title": "Email"
        },
        "contact_property": {
          "description": "Matches contact property",
          "title": "Contact property"
        },
        "folder": {
          "in_a_folder": "In folder",
          "not_in_folder": "Not in folder",
          "not_in_a_folder": "Not in any folder",
          "title": "Folder",
          "description": "In folder"
        },
        "follower_count": {
          "description": "Follower count",
          "title": "Follower count"
        },
        "is_follower": {
          "title": "Follow you or not"
        },
        "is_followed": {
          "title": "Followed by you or not"
        },
        "language": {
          "description": "Main language",
          "title": "Language"
        },
        "message": {
          "description": "Sent a message",
          "title": "Message"
        },
        "name": {
          "description": "Name",
          "title": "Name"
        },
        "payment": {
          "description": "Received a payment link",
          "title": "Payment link"
        },
        "phone": {
          "description": "Phone number",
          "title": "Phone"
        },
        "referral_ad": {
          "description": "Replied to an ad",
          "title": "Referral ad"
        },
        "scenario": {
          "description": "Triggered a scenario",
          "title": "Scenario"
        },
        "status": {
          "description": "Status",
          "title": "Status"
        },
        "story_mention": {
          "description": "Mentioned you in a story",
          "title": "Story mention"
        },
        "story_reply": {
          "description": "Replied to a story",
          "title": "Story reply"
        },
        "username": {
          "description": "Username",
          "title": "Username"
        },
        "media_mention": {
          "description": "Mentioned you in a post caption",
          "title": "Mention in caption"
        },
        "survey": {
          "description": "Completed a survey",
          "title": "Survey"
        }
      },
      "comment": "Commented on a post",
      "logic": {
        "and": "and",
        "or": "or",
        "users_who": "Contacts",
        "and_label": "Match all filters",
        "or_label": "Match any filter"
      },
      "message": "Sent a message",
      "options": {
        "campaigns": {
          "any": "Any campaign"
        },
        "scope": {
          "post": {
            "any": "Any post",
            "specific": "Specific post"
          },
          "story": {
            "any": "Any story",
            "specific": "Specific story"
          },
          "campaign": {
            "any": "Any campaign",
            "specific": "Specific campaign"
          },
          "scenario": {
            "any": "Any scenario",
            "specific": "Specific scenario"
          },
          "link": {
            "any": "Any link",
            "specific": "Specific link"
          }
        },
        "date_operators": {
          "is_equal": "on",
          "is_not_equal": "not on",
          "is_before": "before",
          "is_after": "after",
          "is_on_or_before": "on or before",
          "is_on_or_after": "on or after",
          "is_between": "between",
          "is_present": "at any time",
          "is_not_present": "never"
        },
        "date_modes": {
          "exact_date": "exact date",
          "today": "today",
          "yesterday": "yesterday",
          "days_ago": "days ago"
        },
        "date_summary": {
          "exact_date": {
            "is_equal": "on {{date}}",
            "is_not_equal": "not on {{date}}",
            "is_before": "before {{date}}",
            "is_after": "after {{date}}",
            "is_on_or_before": "on or before {{date}}",
            "is_on_or_after": "on or after {{date}}"
          },
          "today": {
            "is_equal": "today",
            "is_not_equal": "not today",
            "is_before": "before today",
            "is_after": "after today",
            "is_on_or_before": "on or before today",
            "is_on_or_after": "on or after today"
          },
          "yesterday": {
            "is_equal": "yesterday",
            "is_not_equal": "not yesterday",
            "is_before": "before yesterday",
            "is_after": "after yesterday",
            "is_on_or_before": "on or before yesterday",
            "is_on_or_after": "on or after yesterday"
          },
          "days_ago": {
            "is_equal_one": "on {{count}} day ago",
            "is_equal_other": "on {{count}} days ago",
            "is_not_equal_one": "not on {{count}} day ago",
            "is_not_equal_other": "not on {{count}} days ago",
            "is_before_one": "before {{count}} day ago",
            "is_before_other": "before {{count}} days ago",
            "is_after_one": "after {{count}} day ago",
            "is_after_other": "after {{count}} days ago",
            "is_on_or_before_one": "on or before {{count}} day ago",
            "is_on_or_before_other": "on or before {{count}} days ago",
            "is_on_or_after_one": "on or after {{count}} day ago",
            "is_on_or_after_other": "on or after {{count}} days ago"
          },
          "between": "between {{from}} and {{to}}"
        },
        "is_follower": {
          "is_false": "They don't follow you",
          "is_true": "They follow you"
        },
        "is_followed": {
          "is_false": "You don't follow them",
          "is_true": "You follow them"
        },
        "message": {
          "containing": "containing",
          "any_text": "any text"
        },
        "operators": {
          "placeholder": "Select an operator",
          "contains": "contains",
          "does_not_contain": "does not contain",
          "is_above": "is above",
          "is_below": "is below",
          "is_between": "is between",
          "between_and": "and",
          "is_equal": "is equal to",
          "is_not_equal": "is not equal to",
          "is_false": "is false",
          "is_not_present": "is not present",
          "is_present": "is present",
          "is_true": "is true",
          "is_before": "is before",
          "is_after": "is after"
        },
        "scenarios": {
          "any": "Any scenario"
        },
        "survey": {
          "received_and_completed": "Received and completed",
          "received_not_completed": "Received but not completed",
          "never_received": "Never received"
        }
      },
      "referral_ad": "Replied to an ad",
      "remove_filter": "Remove filter",
      "remove_filters": "Remove all filters",
      "scenario": "Triggered a scenario",
      "scenarios": {
        "any_scenario": "Any scenario"
      },
      "story_mention": "Mentioned you in a story",
      "story_reply": "Replied to a story",
      "title": "Filters",
      "generate_prompt_placeholder": "Describe how you want to filter",
      "generate_button": "Generate filters with AI",
      "contacts_count_one": "1 contact matches",
      "contacts_count_other": "{{count}} contacts match",
      "contacts_count_capped_one": "{{count}}+ contact matches",
      "contacts_count_capped_other": "{{count}}+ contacts match",
      "no_filters": "No filters",
      "edit_filters": "Edit filters",
      "add_filters": "Add filters"
    },
    "no_contacts": "No contacts found",
    "contacts_count_one": "1 contact",
    "contacts_count_other": "{{count}} contacts",
    "contacts_count_capped_one": "{{count}}+ contact",
    "contacts_count_capped_other": "{{count}}+ contacts",
    "search_contacts": "Search contacts",
    "load_failed": "Couldn't load contacts.",
    "segments": {
      "create_segment": "Create segment",
      "delete_segment": "Delete segment",
      "delete_segment_confirm": "Delete segment {{segment}}?",
      "rename_segment": "Rename segment",
      "duplicate_segment": "Duplicate segment",
      "copy_title": "{{title}} (copy)",
      "edit_segment": "Edit segment",
      "title": "Segments",
      "view_contacts": "View segment contacts"
    },
    "title": "Contacts",
    "view_conversation": "View conversation",
    "edit": "Edit contact",
    "save": "Save contact",
    "delete_contact": "Delete contact",
    "delete_contact_confirm": "Delete this contact and all its conversations?",
    "delete_contact_notice": "This can't be undone. Automation and campaign history is kept, without the contact.",
    "export_contacts": "Export contacts",
    "no_language": "No language detected",
    "notes": {
      "write_notes": "Write your notes..."
    },
    "columns": {
      "agent_active": "Used by AI agent",
      "username": "Instagram Username",
      "name": "Instagram Name",
      "status": "Status",
      "email": "Email",
      "email_description": "The contact's email address for communication",
      "phone": "Phone",
      "phone_description": "The contact's phone number",
      "folders": "Folders",
      "follower": "Follower",
      "followed": "Followed",
      "language": "Language",
      "follower_count": "# followers",
      "weekly_growth": "Weekly growth",
      "bio": "Bio",
      "last_message": "Last message",
      "last_interaction": "Last interaction"
    },
    "properties": {
      "title": "Contact properties",
      "new_property": "New property",
      "edit_property": "Edit property",
      "delete_property": "Delete property",
      "select_property": "Select a property",
      "title_label": "Property name",
      "title_placeholder": "e.g. Birthday, Country, etc.",
      "description_label": "Property description",
      "description_placeholder": "e.g. Client's birthday, user's country of residence, etc.",
      "no_description": "No description provided",
      "type_label": "Property type",
      "type_text": "Text",
      "type_number": "Number",
      "type_date": "Date",
      "type_email": "Email",
      "type_phone": "Phone",
      "type_url": "URL",
      "type_boolean": "Boolean",
      "type_list": "List",
      "type_single_choice": "Single choice",
      "type_multiple_choice": "Multiple choice",
      "choices_label": "Options",
      "choices_placeholder": "Add an option...",
      "delete_confirm": "Delete this property?",
      "no_property_selected": "Select property",
      "agent_active_label": "Accessible by AI agent",
      "auto_detect_label": "Detect automatically",
      "auto_detect_paused": "No AI credits left. Detection is paused.",
      "built_in": "Built-in",
      "built_in_note": "Built-in property. Its title and type cannot change.",
      "agent_badge": "AI agent",
      "agent_active_hint": "The agent uses it to answer your contacts' questions.",
      "auto_detect_cost": "Uses 1 AI credit per detection that finds a value.",
      "backfill_confirm": "Detect",
      "backfill_later": "Not now",
      "backfill_running": "Detecting…",
      "backfill_progress": "{{done}} of {{total}}",
      "backfill_found_one": "{{count}} value found",
      "backfill_found_other": "{{count}} values found",
      "backfill_found_none": "Done. No values found.",
      "backfill_stopped": "Stopped at {{done}} of {{total}}.",
      "backfill_stopped_credits": "Stopped at {{done}} of {{total}}: no AI credits left.",
      "see_credits": "See credits",
      "empty": "Properties keep what contacts tell you, like budget or interests.",
      "no_value": "No value",
      "detected_automatically": "Detected automatically",
      "more_values": "+{{count}}",
      "auto_detect_description": "Fills in empty values from each contact's profile and conversations.",
      "auto_detect_badge": "Auto-detect",
      "backfill_button": "Detect for existing contacts",
      "backfill_prompt_other": "Detect {{property}} for {{count}} existing contacts?",
      "backfill_prompt_one": "Detect {{property}} for {{count}} existing contact?",
      "backfill_none": "No existing contacts to detect.",
      "slug_label": "Variable slug",
      "slug_placeholder": "Property variable name, e.g. birthday",
      "slug_hint": "Use this in automation variables:",
      "add_choice_button": "Add option"
    },
    "tabs": {
      "conversation": "Conversation",
      "automation": "Automation",
      "campaigns": "Campaigns",
      "comments": "Comments",
      "mentions": "Mentions",
      "notes": "Notes",
      "properties": "Properties",
      "history": "History"
    },
    "drawer": {
      "view_contacts": "View contacts",
      "contacts_count_one": "1 contact",
      "contacts_count_other": "{{count}} contacts",
      "contacts_count_capped_one": "{{count}}+ contact",
      "contacts_count_capped_other": "{{count}}+ contacts",
      "export_contacts": "Export contacts",
      "search_contacts": "Search contacts",
      "page": "Page {{page}} of {{total}}",
      "no_contacts": "No contacts found",
      "add_to_folder": "Add to folder",
      "add_to_folder_success_one": "Added 1 contact to folder {{ folder }}",
      "add_to_folder_success_other": "Added {{ count }} contacts to folder {{ folder }}"
    },
    "status": {
      "edit_status": "Edit contact status",
      "edit_optout": "Opt out contact from receiving messages",
      "edit_spam": "Add contact to spam list",
      "edit_active": "Opt in contact to receive messages",
      "remove_spam": "Remove contact from spam list",
      "remove_optout": "Remove contact from opt-out list",
      "pending": {
        "label": "Pending",
        "description": "Contact status is still being evaluated."
      },
      "active": {
        "label": "Active",
        "description": "Contact is active and can receive messages."
      },
      "inactive": {
        "label": "Inactive",
        "description": "Contact cannot receive your messages. They will become active if they send you a message. This can be because they blocked you, commented on your post more than 7 days ago or have not accepted your message request."
      },
      "commented": {
        "label": "Commented",
        "description": "Contact has commented on your post. You can send them a DM request in the following 7 days."
      },
      "live_commented": {
        "label": "Live Commented",
        "description": "Contact has commented on a live video. You can send them a DM request in the following 24 hours."
      },
      "requested": {
        "label": "Requested",
        "description": "Contact has received your message request and has not accepted it yet."
      },
      "optout": {
        "label": "Opt-out",
        "description": "Contact has opted out of receiving your messages."
      },
      "deleted": {
        "label": "Deleted",
        "description": "Instagram account deleted."
      },
      "spam": {
        "label": "Spam",
        "description": "You have added this contact to your spam list."
      }
    },
    "history": {
      "no_history": "No contact history"
    }
  },
  "content": {
    "title": "Instagram content",
    "sync_title": "Content syncing",
    "sync_description": "We're fetching your latest posts, reels and stories from Instagram. This may take a few minutes.",
    "medias": {
      "title": "Posts & reels",
      "ad_badge": "Ad",
      "media_id": "Post ID",
      "pick_media": "Pick a post/reel",
      "refresh": "Refresh content",
      "comments_one": "1 comment",
      "comments_other": "{{count}} comments",
      "likes_one": "1 like",
      "likes_other": "{{count}} likes",
      "view_on_instagram": "View on Instagram",
      "no_caption": "No caption",
      "no_comments": "No comments yet",
      "published": "Published: {{date}}",
      "last_synced": "Last synced: {{date}}",
      "automations_one": "1 automation",
      "automations_other": "{{count}} automations",
      "create_automation": "Create Comment-to-DM automation",
      "create_automation_confirm": "Create automation for comments on this post?",
      "create_media": "Create new post",
      "cannot_publish": {
        "title": "Publishing not available",
        "description": "We need additional API permissions to enable publishing and scheduling features.",
        "reconnect": "Reauthorize access"
      },
      "tabs": {
        "comments": "Comments",
        "automation": "Automation",
        "insights": "Insights",
        "ai_agent": "AI Agent"
      },
      "insights": {
        "reach": "Reach",
        "views": "Views",
        "likes": "Likes",
        "comments": "Comments",
        "saved": "Saved",
        "shares": "Shares",
        "total_interactions": "Total interactions",
        "profile_visits": "Profile visits",
        "profile_activity": "Profile actions",
        "follows": "New followers",
        "ig_reels_avg_watch_time": "Average watch time",
        "ig_reels_video_view_total_time": "Total watch time",
        "seconds": "{{value}}s",
        "refreshing": "Fetching the latest numbers from Instagram…",
        "unavailable": "Instagram is not reporting insights for this post.",
        "empty": "No insights yet for this post."
      },
      "status": {
        "all": "All posts",
        "posted": "Published",
        "scheduled": "Scheduled",
        "draft": "Draft",
        "pending": "Processing"
      },
      "automation": {
        "title": "Why add an automation?",
        "description": "Reply to comments and send messages to turn passive commenters into active contacts and grow your audience.",
        "add_automation_label": "Add an automation",
        "add_automation_sublabel": "Reply to comments on this post",
        "grid_unavailable": "Comment automations aren't available for grid posts. Add one to each post once it's published.",
        "last_triggered_at": "Last triggered {{date}}"
      },
      "publishing": {
        "title": "New post",
        "description": "Edit your post until you're ready to publish it on Instagram.",
        "steps": {
          "select_media": "Select media",
          "set_caption": "Caption & tags",
          "automation": "Comment automation",
          "options": "Options",
          "review": "Review"
        },
        "cover": {
          "label": "Edit cover",
          "pick_frame": "Pick from video",
          "drop": "Drop to use as cover",
          "upload_error": "Couldn't upload this file. Use a JPEG or PNG image.",
          "remove": "Remove cover",
          "frame_title": "Pick a frame",
          "frame_label": "Frame",
          "use_frame": "Use this frame",
          "ai": {
            "open": "Edit with AI",
            "title": "Edit frame with AI",
            "prompt_label": "What should change?",
            "prompt_placeholder": "Add some text to this image",
            "generate": "Generate",
            "regenerate": "Generate again",
            "cost": "Uses {{number}} AI credits",
            "generating": "Generating… this can take up to a minute.",
            "use": "Use as cover",
            "original": "Original frame",
            "version": "Version {{number}}",
            "get_credits": "Get more credits",
            "errors": {
              "ai_credits_exhausted": "You're out of AI credits.",
              "rate_limited": "You've reached today's limit for AI covers. Try again tomorrow.",
              "moderation_blocked": "This request was declined. Try rewording it.",
              "frame_unavailable": "This frame couldn't be read. Pick another one.",
              "failed": "The cover couldn't be generated. Try again."
            }
          },
          "upload_image": "Upload image"
        },
        "caption": {
          "label": "Caption",
          "placeholder": "e.g. check out our latest news and discover our amazing new stuff",
          "notice": "You can use @mentions and #hashtags in your caption."
        },
        "alt_text": {
          "label": "Alternative text",
          "placeholder": "Describe the content of your image for visually impaired people and screen readers."
        },
        "location": {
          "label": "Location",
          "sublabel": "Adding a location can help your post get discovered.",
          "placeholder": "e.g. New York, Eiffel Tower, etc."
        },
        "media_types": {
          "image": "Image",
          "reels": "Reel",
          "carousel": "Carousel",
          "grid": "Grid"
        },
        "grid": {
          "preview": "Profile grid preview",
          "layout": "Layout",
          "posts": "{{number}} posts",
          "sublabel": "Cropped to fit. Posts go live one by one, about 20 seconds apart."
        },
        "tag_users": {
          "title": "Tag people in your post",
          "image_hint": "Click on the image to tag a new person",
          "new_tag": "Add a person",
          "search_user": "Search a person",
          "search_user_notice": "Enter the username of the account without the @",
          "search_placeholder": "e.g. inro.social",
          "notice": "Profile picture and name are only shown for **Business and Pro accounts**."
        },
        "save_draft": "Save draft",
        "audio_name": {
          "label": "Rename your audio",
          "placeholder": "Original audio",
          "sublabel": "This appears on your Reel in Instagram"
        },
        "not_published_yet": "Post not published yet",
        "options": {
          "share_to_feed": "Feed and Reels",
          "reels_only": "Reels only",
          "share_to_feed_label": "Post visibility"
        },
        "collaborators": {
          "title": "Collab posting",
          "new_collaborator": "Add an account",
          "empty_title": "What's collab posting?",
          "empty_description": "You can invite other accounts to collaborate on your posts. It will appear on their feed and be shown to their followers."
        },
        "ai_disclosure": {
          "label": "AI-generated content",
          "description": "Turn this on if the image or video was generated with AI.",
          "notice": "Instagram requires the label on photorealistic AI content. It has to be set **before publishing** — once the post is live the label can only be removed, never added."
        },
        "review": {
          "title": "You're all set 🎉",
          "description": "You can publish your post now or schedule it for later.",
          "publish_now": "Publish now",
          "schedule_for_later": "Schedule for later",
          "publish_confirm": "Do you want to publish this post to Instagram?",
          "schedule_title": "Schedule post",
          "edit_schedule": "Edit scheduled date",
          "scheduled_title": "Post scheduled",
          "scheduled_description": "The post will be published on **{{date}}**. You can still edit it before it's published.",
          "cancel_schedule_confirm": "Cancel scheduling?",
          "cancel_schedule": "Cancel scheduling",
          "delete_draft": "Delete draft",
          "delete_draft_confirm": "Delete this draft?",
          "publish_channels": "Publish to other channels",
          "publish_facebook": "Publish to Facebook Page",
          "publish_tiktok": "Publish to TikTok"
        },
        "carousel": {
          "label": "Carousel slides",
          "sublabel": "Between {{min}} and {{max}} images or videos. Drag to reorder — the first slide is the one people see in the feed.",
          "add_items": "Select slides",
          "edit_items": "Edit slides",
          "too_few_title": "Add one more slide",
          "too_few_description": "Instagram needs at least {{min}} slides for a carousel.",
          "slide_position": "Slide {{position}} of {{total}}. Use the left and right arrow keys to move it."
        },
        "paid_partnership": {
          "label": "Paid partnership",
          "description": "Show the \"Paid partnership\" label required when a brand pays for this post.",
          "sponsors": "Tag the brands you worked with (up to {{max}}).",
          "add_sponsor": "Add a brand partner",
          "picker_title": "Tag a brand partner",
          "notice": "The label is added when the post is published and **can't be added afterwards**. The brand also has to allow you to tag it in its branded content settings.",
          "preview": "Paid partnership",
          "preview_with": "Paid partnership with {{sponsor}}",
          "unresolved_sponsor": "**{{username}}** can't be credited: no public Business or Creator account was found for it. Instagram only lets you tag professional accounts as brand partners."
        },
        "trial_reel": {
          "label": "Trial reel",
          "description": "Show the reel to non-followers first, so you can test it before it reaches your audience.",
          "graduation_label": "When should it reach your followers?",
          "strategies": {
            "manual": {
              "label": "When I decide",
              "description": "The reel stays a trial until you share it from the Instagram app."
            },
            "performance": {
              "label": "If it performs well",
              "description": "Instagram shares it with your followers automatically when it does well with non-followers."
            }
          },
          "notice": "A trial reel can't be turned back into a normal reel once it's published."
        },
        "agent_context": {
          "label": "Additional information for the AI Agent",
          "notice": "The Agent will use this information when answering comments on this post.",
          "placeholder": "Useful information about the subject of the post: price, location, availability, etc."
        },
        "reel_ui": {
          "follow": "Follow",
          "with_one": "{{username}} and {{other}}",
          "with_others": "{{username}} and {{count}} others",
          "hide": "Hide Instagram overlay",
          "show": "Show Instagram overlay"
        },
        "autosave": {
          "saving": "Saving…",
          "failed": "Couldn't save"
        }
      },
      "select_media": "Select media",
      "select_media_one": "Select 1 media",
      "select_media_other": "Select {{count}} medias",
      "upload_media": "Upload media",
      "load_error": "Cannot load media",
      "stories": {
        "title": "New story",
        "description": "Edit your story until you're ready to publish it on Instagram.",
        "media": {
          "label": "Media",
          "sublabel": "Upload an image or video for your story."
        }
      }
    },
    "stories": {
      "title": "Stories",
      "pick_story": "Pick a story",
      "story_id": "Story ID",
      "story_expired": "Story expired",
      "replies_count_one": "{{count}} reply",
      "replies_count_other": "{{count}} replies",
      "replies": {
        "view_conversation": "View conversation",
        "no_replies": "No replies found.",
        "sort_options": {
          "newest": "Newest first",
          "oldest": "Oldest first"
        }
      },
      "insights": {
        "impressions": "Impressions",
        "reach": "Reach",
        "taps_forward": "Taps forward",
        "taps_back": "Taps back",
        "exits": "Exits",
        "replies": "Replies"
      },
      "create_story": "Create new story"
    },
    "images": {
      "select_image": "Select image",
      "select_images": "Select images",
      "select_images_one": "Select 1 image",
      "select_images_other": "Select {{count}} images",
      "upload_image": "Upload image"
    },
    "videos": {
      "select_video": "Select video",
      "select_videos": "Select videos",
      "select_videos_one": "Select 1 video",
      "select_videos_other": "Select {{count}} videos",
      "upload_video": "Upload video"
    }
  },
  "conversion_links": {
    "select": "Select a link",
    "close_confirm": "Close without saving?",
    "count_one": "{{count}} link",
    "count_other": "{{count}} links",
    "create": "Create link",
    "default_button_title": "Visit website",
    "delete_confirm": "Delete this link? All conversion data will be lost.",
    "delete_image": "Delete image",
    "edit_url_params": "Edit URL parameters",
    "table": {
      "columns": {
        "title": "Title",
        "used_in": "Sent by",
        "metrics": "Metrics",
        "last_sent_at": "Last sent",
        "created_at": "Created on"
      },
      "scenarios_count_one": "1 scenario",
      "scenarios_count_other": "{{count}} scenarios",
      "campaigns_count_one": "1 campaign",
      "campaigns_count_other": "{{count}} campaigns",
      "used_by_agent": "AI Agent can send this link"
    },
    "fields": {
      "add_image": "Add image",
      "button_title": "Button",
      "button_title_description": "Write a clear and short call to action",
      "change_image": "Change image",
      "image": "Link image",
      "subtitle": "Description",
      "title": "Title",
      "url": "https://awesomelink.com"
    },
    "metrics": {
      "clicked": "Clicked",
      "sent": "Sent"
    },
    "no_conversion_links": "No links found",
    "params": {
      "add": "Add parameter",
      "add_utm": "Add UTM tracking",
      "key": "Name",
      "value": "Value"
    },
    "preview": {
      "missing": "Conversion link is missing",
      "title": "Preview message"
    },
    "title": "Conversion links",
    "search": "Search links",
    "agent_active_label": "AI Agent can send this link",
    "agent_active_tooltip": "The AI Agent can send this link if it's relevant",
    "agent_context_placeholder": "When is this link relevant? e.g. Link to book a meeting, to our website, etc."
  },
  "dashboard": {
    "appsumo_deal": {
      "title": "Get lifetime access on AppSumo",
      "description": "Pay once, automate Instagram for good. Available until December 18.",
      "cta": "Get the deal",
      "note": "Can't be applied to Pro accounts."
    },
    "appsumo_review": {
      "title": "Leave a review, get more",
      "description": "+{{slots}} account, +{{contacts}} contacts and +{{ai_credits}} AI credits a month.",
      "cta": "Leave a review"
    },
    "appsumo_upgrade": {
      "title": "Need more room?",
      "description": "Move up a tier on AppSumo for more accounts and larger allowances."
    },
    "lifetime_ai_credits": {
      "title": "You've been upgraded!",
      "description_one": "Your lifetime deal now includes {{formattedCount}} AI credit a month.",
      "description_other": "Your lifetime deal now includes {{formattedCount}} AI credits a month.",
      "description_generic": "Your lifetime deal now includes monthly AI credits.",
      "cta": "View my plan"
    },
    "lifetime_cap": {
      "badge": {
        "warning": "Running low",
        "full": "Cap reached"
      },
      "count": "{{used}} of {{max}}",
      "cta": "See your usage",
      "contacts": {
        "warning_title": "You have used 80% of this month's contacts",
        "full_title": "You have used all of this month's contacts",
        "hard": "Shared across every account on your code. New contacts stop being activated until the month renews — switch to auto-billing to keep going.",
        "auto_billing": "Shared across every account on your code. Extra contacts are billed at the end of the month."
      },
      "ai_credits": {
        "warning_title": "You have used 80% of this month's AI credits",
        "full_title": "You have used all of this month's AI credits",
        "hard": "Shared across every account on your code. AI steps are skipped until the month renews — switch to auto-billing or your own OpenAI key to keep them running.",
        "auto_billing": "Shared across every account on your code. Extra credits are billed at the end of the month."
      }
    },
    "title": "Dashboard",
    "metrics": "Metrics",
    "active_contacts": "Available contacts",
    "engaged_contacts": "Engaged contacts",
    "replied_share": "{{rate}}% to whom you have reacted or replied",
    "activated_contacts": "Activated contacts",
    "conversions": "Conversions",
    "tooltips": {
      "active_contacts": "Contacts who can receive your messages. They have either sent you a message or replied to your message request.",
      "engaged_contacts": "Contacts who have sent you a DM, reacted to your story, mentioned you in a story or commented on your content.",
      "activated_contacts": "Contacts a scenario, campaign, AI agent or API acted on, e.g. by sending a DM.",
      "conversions": "Contacts who clicked on a conversion link or replied to a survey in a campaign or scenario."
    },
    "scenarios": {
      "title": "Active automations ({{count}})",
      "empty": "You don't have any active automation scenarios.",
      "empty_cta": "Create a scenario",
      "name": "Name",
      "executions": "Executions",
      "conversions": "Conversions",
      "last_executed": "Last executed {{date}}",
      "view_all": "View all scenarios"
    },
    "campaigns": {
      "title": "Recent campaigns",
      "empty": "You don't have any campaigns yet.",
      "empty_cta": "Create a campaign",
      "name": "Name",
      "sent": "Sent",
      "opened": "Opened",
      "conversions": "Conversions",
      "sent_at": "Sent on {{date}}",
      "view_all": "View all campaigns"
    },
    "create_automation": {
      "title": "Start automating your Instagram",
      "description": "Create an automation scenario to engage with your contacts and grow your account",
      "cta": "Create an automation"
    },
    "join_discord": {
      "title": "Join our Discord community",
      "description": "Get help, share your automations and learn from other users.",
      "cta": "Join Discord"
    },
    "referral_link": {
      "title": "Invite and refer a friend",
      "description": "Send your referral link and get commissions for 3 months!",
      "cta": "Share link"
    },
    "setup_agent": {
      "title": "Activate your AI agent",
      "description": "Train the agent to manage your conversations automatically and save time.",
      "cta": "Set up agent"
    },
    "onboarding": {
      "title": "Get started with Inrō",
      "description": "Complete these steps to start growing your Instagram account",
      "create_folder": "Create a folder for contacts",
      "create_automation": "Create your first automation",
      "setup_agent": "Set up your AI Agent",
      "talk_instagram": "Talk to us on Instagram",
      "follow_inro": "Follow us on Instagram"
    },
    "setup_mcp": {
      "title": "Using Claude or ChatGPT?",
      "description": "Connect our MCP server to manage Instagram from your AI assistants!",
      "cta": "Connect assistant"
    },
    "instagram_notifications": {
      "title": "Don't miss notifications!",
      "description": "Message us so we can send you important information and alerts",
      "cta": "Get DM notifications"
    },
    "instagram_restriction": {
      "title": {
        "sending_blocked": "Instagram paused your messages",
        "links_blocked": "Instagram paused messages with links",
        "rate_limited": "Instagram is slowing your messages"
      },
      "since": "Since {{date}}",
      "resume": {
        "all": "Automations and messages resume on their own once it lifts.",
        "links": "Messages with a link resume on their own once it lifts."
      },
      "learn_more": "Learn more",
      "error_message": "Instagram error message"
    },
    "disconnected_scenarios": {
      "title": "Paused automations",
      "description_one": "<0>{{ count }} automation</0> were triggered but could not run while your Instagram account was disconnected.",
      "description_other": "<0>{{ count }} automations</0> were triggered but could not run while your Instagram account was disconnected.",
      "cta": "View automations",
      "modal_title": "Paused automations",
      "modal_description": "These automations could not run while your Instagram account was disconnected.",
      "paused_count_one": "{{count}} paused",
      "paused_count_other": "{{count}} paused",
      "all_done": "All paused executions have been handled"
    },
    "icebreakers": {
      "title": "Your ice breakers lead nowhere",
      "description": "An automation can reply, file the contact, or ask a few questions.",
      "empty": {
        "title": "Set up your ice breakers",
        "description": "Ice breakers appear when contacts first message you, and handle frequent requests.",
        "cta": "Set up ice breakers"
      }
    },
    "upgrade_to_pro": {
      "title": "What Pro adds",
      "details": "See pricing",
      "benefits": {
        "automations": "Unlimited automations and campaigns",
        "ai": "An AI Agent that replies in your voice",
        "triggers": "Every trigger and action",
        "integrations": "Shopify, Calendly, API, MCP"
      },
      "limit": {
        "badge": "Limit reached",
        "activations": {
          "count": "{{used}} / {{max}} contacts",
          "title": "Activated contacts limit reached",
          "description": "You've reached your maximum monthly active contacts. Go unlimited with Pro."
        },
        "scenarios": {
          "count": "{{used}} / {{max}} scenarios",
          "title": "Active automations limit reached",
          "description": "You've reached the limit of active scenarios. Go unlimited with Pro."
        },
        "campaigns": {
          "count": "{{used}} / {{max}} campaigns",
          "title": "You've sent all your campaigns",
          "description": "You can send more next month. Go unlimited with Pro."
        }
      }
    }
  },
  "fields": {
    "meta_access_token": {
      "placeholder": "API Token not generated",
      "get_token": "Get token"
    }
  },
  "errors": {
    "default": "An error occurred. Please try again later."
  },
  "automation_folder_exclusion": {
    "title": "Exclude specific contacts",
    "description": "Contacts matching these filters will be excluded from all automations."
  },
  "icebreakers": {
    "title": "Manage ice breakers",
    "description": "Ice breakers appear in Instagram the first time contacts message you.",
    "module_description": "Ice breakers appear in Instagram the first time contacts message you.",
    "learn_more": "Learn more about ice breakers",
    "new_icebreaker_placeholder": "Type your ice breaker",
    "new_icebreaker_label": "New ice breaker",
    "suggested_intents": "Common questions you get",
    "question_label": "Ice breaker question",
    "delete": "Delete this ice breaker",
    "delete_confirm": "Delete this ice breaker?",
    "delete_automated_warning_one": "One automation fires on it. Deleting it leaves that scenario with nothing to trigger it.",
    "delete_automated_warning_other": "{{count}} automations fire on it. Deleting it leaves them with nothing to trigger them.",
    "no_icebreakers": "No ice breakers yet. Add one and it shows up on your profile right away.",
    "add_automation": "Add an automation",
    "matched_on_title": "Matched by wording",
    "matched_on_title_tooltip": "This automation matches the wording, not the ice breaker itself. Rewriting the question stops it firing.",
    "preview": {
      "tap_to_send": "Tap to send a question suggested by {{username}}.",
      "nothing_suggested": "No questions suggested — the conversation starts empty."
    },
    "limit_reached": "You've reached the limit of 4 ice breakers",
    "health": {
      "empty": "No ice breakers yet.",
      "dead_one": "1 ice breaker gets no reply.",
      "dead_other": "{{count}} ice breakers get no reply.",
      "all_answered": "All ice breakers answered"
    },
    "row": {
      "nothing_answers": "Nothing answers this",
      "agent_answers": "Your agent answers this"
    },
    "recipes": {
      "title": "Add automation to icebreaker",
      "message": {
        "title": "Reply with a message",
        "description": "Answer instantly, in your own words.",
        "reply": "Thanks for your question"
      },
      "folder": {
        "title": "File them in a folder",
        "description": "Sends nothing. You still answer by hand, but the contact is sorted and reachable later.",
        "reply": "Added to folder"
      },
      "questions": {
        "title": "Ask a few questions",
        "description": "Qualify the contact before you spend time on the reply.",
        "reply": "What do you need?",
        "quick_reply_1": "Option A",
        "quick_reply_2": "Option B"
      }
    }
  },
  "inbox": {
    "actions": {
      "actions": "Actions",
      "conversations_selected_one": "One conversation selected",
      "conversations_selected_other": "{{count}} conversations selected",
      "like_last_message": "❤️ Like last message",
      "like_last_message_confirm_one": "You'll be liking ❤️ the last message of this conversation.",
      "like_last_message_confirm_other": "You'll be sending likes ❤️ to the last message of {{count}} conversations.",
      "liking_one": "Liking message…",
      "liking_other": "Liking {{count}} messages…",
      "mark_as": "Mark as…",
      "mark_as_read": "Mark as read",
      "mark_as_read_confirm_one": "You'll be marking this conversation as read.",
      "mark_as_read_confirm_other": "You'll be marking {{count}} conversations as read.",
      "mark_as_replied": "Mark as replied",
      "mark_as_replied_confirm_one": "You'll be marking this conversation as replied.",
      "mark_as_replied_confirm_other": "You'll be marking {{count}} conversations as replied.",
      "mark_as_unread": "Mark as unread",
      "move_to": "Move to",
      "move_to_spam": "Move to spam",
      "move_to_spam_confirm_one": "You'll be moving this conversation to the spam folder",
      "move_to_spam_confirm_other": "You'll be moving {{count}} conversations to the spam folder",
      "reply": "Reply",
      "send_confirm_one": "Send message?",
      "send_confirm_other": "Send message to {{count}} conversations?",
      "send_message": "Send message",
      "send_message_emoji": "✉️ Send message",
      "sending_one": "Sending one message…",
      "sending_other": "Sending {{count}} messages…",
      "sending_title_one": "You'll be sending this message to one conversation",
      "sending_title_other": "You'll be sending this message to {{count}} conversations"
    },
    "all_loaded": "All conversations loaded!",
    "audio_messages": {
      "modal": {
        "record_audio": "or record your audio message",
        "title": "Send audio message",
        "upload_file": "Upload audio file"
      }
    },
    "caught_up": "You're all caught up!",
    "clear": "Clear selection",
    "conversation": {
      "automation_history": "Automation history",
      "automated": {
        "sent_by_automation": "Message sent by automation",
        "sent_by_scenario": "Message sent by automation **{{scenario}}**",
        "sent_by_campaign": "Message sent by campaign **{{campaign}}**",
        "sent_by_agent": "Message sent by **AI Agent**",
        "sent_by_api": "Message sent via **API**"
      },
      "add_to_folder": "Add to folder",
      "agent_handled_info": {
        "title": "Handled by AI Agent",
        "description": "This conversation is currently being handled by your AI agent.",
        "remove_agent": "Remove Agent from conversation",
        "remove_agent_confirm": "Do you want to remove the AI agent from this conversation?"
      },
      "human_handled_info": {
        "title": "You are handling this conversation",
        "description": "Your AI agent is paused here because a team member replied manually. It will start replying again automatically, or you can hand it back now.",
        "resume_agent": "Hand back to AI Agent"
      },
      "handler_status": {
        "handled_by_agent": "Handled by AI Agent",
        "handled_by_runnable": "Handled by Automation",
        "handled_by_human": "Handled by a team member",
        "human_requested": "Human Requested"
      },
      "go_to_instagram": "Go to Instagram to send DMs to inactive contact",
      "change_folder": "Change folder(s)",
      "compose": "Compose message…",
      "copy": "Copy",
      "icebreaker_clicked": "Ice breaker clicked",
      "invalid_file_type": "This file type is not supported.",
      "like": "Like",
      "loading_messages": "Loading messages...",
      "mark": "Mark",
      "mark_as": "Mark as",
      "marked_as_replied": "You marked this conversation as replied",
      "max_file_size": {
        "image": "Cannot send images above 8MB.",
        "video": "Cannot send videos above 25MB.",
        "audio": "Cannot send audio files above 25MB."
      },
      "me": "Me",
      "message_group_tooltip": "Messages sent close in time are grouped together for automations.",
      "move": "Move",
      "move_to_folder": "Move to folder",
      "move_to_spam": "Move to spam",
      "move_to_spam_confirm": "Move {{object}} to spam?",
      "not_found": "Conversation not found",
      "notes": "Notes",
      "referral_link": "Referral link",
      "remove_from_folder": "Remove from folder",
      "remove_from_spam": "Remove from spam",
      "remove_from_spam_confirm": "Remove conversation from spam?",
      "replying_to": "Replying to comment",
      "request_payment": "Request payment",
      "run_scenario": "Run scenario",
      "save_message": "Save message",
      "schedule_appointment": "Schedule appointment",
      "schedule_message": "Schedule message",
      "seen": "Seen",
      "send": "Send",
      "send_failed": "Message not sent",
      "send_shopify_link": "Send Shopify link",
      "spam": "Spam",
      "trigger_automation": "Trigger automation",
      "trigger_automation_confirm": "Do you want to trigger scenarios for this message? Only scenarios with a matching trigger will be executed.",
      "trigger_automation_regroup": "Trigger automation (regroup)",
      "unlike": "Unlike",
      "unsave_message": "Unsave message",
      "unsupported_message": "Message cannot be shown",
      "unsupported_message_view": "View it on Instagram",
      "message_request_info": {
        "title": "Message requests",
        "description": "Some of your messages are not being sent because your first message is waiting in the contact's requests. You can update your automations to wait for a reply."
      },
      "thread_ownership": {
        "title": "Conversation locked",
        "description": "Another application has requested exclusive control over this conversation.",
        "description_date": "Another application has requested exclusive control over this conversation until **{{date}}**.",
        "description_estimated_date": "Another application has requested exclusive control over this conversation. It should be released by approximately **{{date}}**."
      }
    },
    "details": {
      "client_details": "Client details",
      "email": "Email",
      "email_address": "Email address",
      "email_placeholder": "example@mail.com",
      "empty_notes": "Write notes about this contact...",
      "engagement": "Engagement",
      "engagement_appointments_one": "{{ count }} appointment",
      "engagement_appointments_other": "{{ count }} appointments",
      "engagement_date": "Since you started using Inrō on {{date}}",
      "engagement_dm_one": "{{ count }} DM received",
      "engagement_dm_other": "{{ count }} DM received",
      "engagement_follow": "@{{ name }} follows you",
      "engagement_last_appointment": "Last on {{ date }}",
      "engagement_no_follow": "@{{ name }} doesn't follow you",
      "engagement_payments_one": "{{ count }} payment received",
      "engagement_payments_other": "{{ count }} payments received",
      "engagement_story_one": "Reacted to {{ count }} story",
      "engagement_story_other": "Reacted to {{ count }} stories",
      "information": "Contact information",
      "media": "All shared media",
      "media_empty": "We could not find any media in this conversation",
      "name": "Name",
      "no_saved_messages": "You haven't saved any message in this conversation yet.",
      "notes": "Notes",
      "phone": "Phone",
      "phone_number": "Phone number",
      "phone_placeholder": "+xx x xx xx xx xx",
      "real_name": "Name",
      "real_name_placeholder": "Contact name",
      "saved_messages": "Saved messages",
      "unsave_message": "Do you want to remove this message from saved messages?"
    },
    "empty": "It's empty!",
    "filters": {
      "campaign_running": "Currently handled by a campaign",
      "filter_by": "Filter by",
      "followers": "Followers only",
      "handled_by_agent": "Currently handled by agents",
      "human_requested": "Human requested",
      "mentions": "Unreplied story mentions",
      "messages": "They replied last",
      "most_engaged": "Most engaged",
      "no_folder": "Not in a folder",
      "not_handled_by_automation": "Not handled by automation",
      "payment_pending": "Payment pending",
      "pending": "Awaiting payment or appointment",
      "replied_message": "You replied last",
      "scenario_running": "Currently handled by a scenario",
      "shopify_customers": "Shopify customers",
      "show_all": "Show all",
      "story_replies": "Story replies",
      "unconfirmed_appointment": "Unconfirmed appointment",
      "unread": "Unread messages",
      "unread_empty": "Congrats, you have read all your messages.",
      "unreplied_reactions": "Unreplied story reactions",
      "upcoming_appointment": "Upcoming appointment"
    },
    "folders": {
      "activate_notifications": "Activate folder notifications",
      "add_to_folder": "Add to folder",
      "remove_from_folder": "Remove from folder",
      "all": "All",
      "all_folders": "All folders",
      "create": "Create folder",
      "create_new": "Create new folder",
      "create_tooltip": "Create your first folder",
      "delete": "Delete folder",
      "delete_confirm": "Delete folder {{folder}}?",
      "delete_confirm_notice": "Don't worry, your conversations will be removed from this folder but will remain in your inbox.",
      "edit": "Edit folder",
      "folder_exists": "Folder already exists",
      "manage": "Manage folders",
      "manage_notifications": "Manage folder notifications",
      "manage_notifications_empty": "Create folders to organize your conversations and define the notification frequency for each folder: in real time, daily recap or weekly recap.",
      "move_to_one": "Move conversation to folder",
      "move_to_other": "Move {{count}} conversations to folder",
      "notifications_daily": "🗞️ Daily recap",
      "notifications_description": "Did you know you can manage notifications for each folder separately?",
      "notifications_none": "No notifications",
      "notifications_real_time": "⚡ Real time",
      "notifications_weekly": "📬 Weekly recap",
      "pick": "Pick a folder",
      "placeholder": "Clients, guests, friends…",
      "name": "Folder name",
      "intro": "Group contacts to filter your inbox, target campaigns and trigger automations.",
      "suggestions": {
        "leads": "Leads",
        "customers": "Customers",
        "collaborations": "Collaborations",
        "vip": "VIP",
        "orders": "Orders"
      },
      "remove_from": "Remove from {{folder}}",
      "save_order": "Save folder order",
      "title_one": "{{count}} folder",
      "title_other": "{{count}} folders"
    },
    "inbox_empty": "No conversations found",
    "no_messages": "You don't have any conversation in this folder.\nSwipe on any conversation to move it.",
    "pending_messages_text": "We're sending your messages one by one to protect your Instagram account.",
    "pending_messages_title_one": "{{count}} message remaining",
    "pending_messages_title_other": "{{count}} messages remaining",
    "message_error": "Message error",
    "message_pending": "Message waiting to send",
    "permissions": {
      "allow_access": "Allow access to your messages",
      "allow_access_text": "You need to authorize access to your messages in your Instagram settings.",
      "authorisation_missing": "Authorization missing",
      "authorisation_missing_text": "We lost access to your Instagram inbox. Please log in again to reauthorize Inrō.",
      "authorise": "Authorize with Facebook",
      "connecting": "Connecting your inbox…",
      "facebook_error_text": "We couldn't connect to your Instagram account. We have been notified and will look into it. Try again later or fix the error below.",
      "facebook_error_title": "Connection error",
      "instagram_success_text": "While we get your DMs, you can start creating your folders and moving your conversations.",
      "instagram_success_title": "✨ Instagram inbox connected! ✨",
      "many_pages": {
        "text": "It seems you have selected multiple facebook pages. Please select the one you wish to connect.",
        "title": "Multiple Facebook pages selected"
      },
      "no_instagram": {
        "text": "To connect your inbox, you need to have a Instagram Business account. Make sure it matches the Facebook page you chose.",
        "title": "No Instagram account"
      },
      "no_page": {
        "text": "To connect your inbox, you need to have a Facebook page linked to your Instagram Business account.",
        "title": "No Facebook page detected"
      },
      "account_not_found": {
        "title": "Instagram account not found",
        "text": "We couldn't find this Instagram account. Please make sure you have authorized the right Facebook page and Instagram account."
      },
      "instagram_already_connected": {
        "title": "Instagram account already connected",
        "description": "The Instagram account <0>{{username}}</0> is already connected to another account. Please select another account or contact support if this was not you.",
        "description_current": "**{{username}}** is the Instagram account you are already using.",
        "description_member": "You have already connected **{{username}}**.",
        "switch_cta": "Switch to {{username}}",
        "recover_text": "If that other account is yours, we can email a login link to everyone on it.",
        "recover_cta": "Recover my account",
        "error": "We couldn't send the email. Please try again or contact support."
      },
      "invalid_account_connected": {
        "title": "Invalid account connected",
        "description": "The Instagram account you connected is different from the one entered below. Please try again with the account below."
      },
      "reauthorise": "Reauthorize",
      "retry": "Retry connection",
      "settings_changed": "I changed my settings",
      "show_me_how": "Where is this setting?",
      "why_facebook": "Why do I need Facebook?",
      "wrong_login_method": {
        "title": "Wrong login method",
        "description": "This account is linked through {{method}}. Reconnect using the same login method."
      }
    },
    "promo_codes": {
      "notice": "Code **{{code}}** active until **{{date}}**"
    },
    "recent_searches": "Recent searches",
    "search_empty": "No conversations found",
    "search_empty_text": "Type anything to search your inbox.",
    "search_messages": "Search in messages",
    "segments": {
      "ambassadors": "Ambassadors",
      "potential_clients": "Potential clients"
    },
    "select": "Select",
    "select_all": "Select all",
    "select_max": "Select max",
    "selected": "selected",
    "selection_limit": "You cannot select more than {{count}} conversations at once.",
    "settings": {
      "export_cta": "Download as Excel file",
      "export_subtitle": "Only includes contacts that have saved their personal information for a payment or an appointment.",
      "export_title": "Export contact information",
      "folder_notifications": "Folder notifications",
      "manage_folders": "Manage folders",
      "mark_all_read": "Mark all as read",
      "spam_messages": "Spam list",
      "sync_inbox": "Sync inbox",
      "title": "Inbox settings"
    },
    "sync_in_progress": "Inbox sync in progress",
    "sync_in_progress_text": "We're saving your conversations and contacts.",
    "timeoff": {
      "activate": "Activate Time-off",
      "calendar_title": "Pick the last day of your Time-off",
      "description": "This will be replied to the first message anyone sends you while Time-off is active.",
      "keep_active": "Keep active",
      "last_day": "Last day: {{day}}",
      "notice": "Auto-reply is active",
      "notice_until": "Auto-reply is active until {{date}}",
      "placeholder": "Type your message",
      "save": "Save changes",
      "set_end_date": "Set end date",
      "title": "Time-off"
    },
    "title": "Messages"
  },
  "email_preferences": {
    "title": "Email preferences",
    "subtitle": "Choose which emails **{{email}}** receives.",
    "saved": "Preferences saved",
    "save_failed": "We could not save that. Please try again.",
    "unsubscribe_all": "Unsubscribe from all of these",
    "arrived_from": "You arrived here from a *{{category}}* email.",
    "transactional_notice": "You will still receive emails we have to send you: invoices, payment problems, password resets, exports you asked for and security notices about your account.",
    "categories": {
      "account_alerts": {
        "label": "Account alerts",
        "description": "Your Instagram account disconnecting, automations paused or suspended, and other problems that stop your account working."
      },
      "product_news": {
        "label": "New features and news",
        "description": "New features, product updates and occasional tips to get more out of your account."
      },
      "weekly_reports": {
        "label": "Weekly report",
        "description": "A weekly summary of your conversations, automations and results."
      },
      "campaign_reports": {
        "label": "Campaign and task reports",
        "description": "Results of each campaign and background task, such as a property detection, once it has finished."
      }
    }
  },
  "languages": {
    "aa": "Afar",
    "ab": "Abkhazian",
    "ae": "Avestan",
    "af": "Afrikaans",
    "ak": "Akan",
    "am": "Amharic",
    "an": "Aragonese",
    "ar": "Arabic",
    "as": "Assamese",
    "av": "Avaric",
    "ay": "Aymara",
    "az": "Azerbaijani",
    "ba": "Bashkir",
    "be": "Belarusian",
    "bg": "Bulgarian",
    "bh": "Bihari languages",
    "bi": "Bislama",
    "bm": "Bambara",
    "bn": "Bengali",
    "bo": "Tibetan",
    "br": "Breton",
    "bs": "Bosnian",
    "ca": "Catalan",
    "ce": "Chechen",
    "ch": "Chamorro",
    "co": "Corsican",
    "cr": "Cree",
    "cs": "Czech",
    "cu": "Church Slavic",
    "cv": "Chuvash",
    "cy": "Welsh",
    "da": "Danish",
    "de": "German",
    "dv": "Maldivian",
    "dz": "Dzongkha",
    "ee": "Ewe",
    "el": "Greek",
    "en": "English",
    "eo": "Esperanto",
    "es": "Spanish",
    "et": "Estonian",
    "eu": "Basque",
    "fa": "Persian",
    "ff": "Fulah",
    "fi": "Finnish",
    "fj": "Fijian",
    "fo": "Faroese",
    "fr": "French",
    "fy": "Western Frisian",
    "ga": "Irish",
    "gd": "Gaelic",
    "gl": "Galician",
    "gn": "Guarani",
    "gu": "Gujarati",
    "gv": "Manx",
    "ha": "Hausa",
    "he": "Hebrew",
    "hi": "Hindi",
    "ho": "Hiri Motu",
    "hr": "Croatian",
    "ht": "Haitian",
    "hu": "Hungarian",
    "hy": "Armenian",
    "hz": "Herero",
    "ia": "Interlingua",
    "id": "Indonesian",
    "ie": "Interlingue",
    "ig": "Igbo",
    "ii": "Sichuan Yi",
    "ik": "Inupiaq",
    "io": "Ido",
    "is": "Icelandic",
    "it": "Italian",
    "iu": "Inuktitut",
    "ja": "Japanese",
    "jv": "Javanese",
    "ka": "Georgian",
    "kg": "Kongo",
    "ki": "Kikuyu",
    "kj": "Kuanyama",
    "kk": "Kazakh",
    "kl": "Kalaallisut",
    "km": "Central Khmer",
    "kn": "Kannada",
    "ko": "Korean",
    "kr": "Kanuri",
    "ks": "Kashmiri",
    "ku": "Kurdish",
    "kv": "Komi",
    "kw": "Cornish",
    "ky": "Kirghiz",
    "la": "Latin",
    "lb": "Luxembourgish",
    "lg": "Ganda",
    "li": "Limburgan",
    "ln": "Lingala",
    "lo": "Lao",
    "lt": "Lithuanian",
    "lu": "Luba-Katanga",
    "lv": "Latvian",
    "mg": "Malagasy",
    "mh": "Marshallese",
    "mi": "Maori",
    "mk": "Macedonian",
    "ml": "Malayalam",
    "mn": "Mongolian",
    "mr": "Marathi",
    "ms": "Malay",
    "mt": "Maltese",
    "my": "Burmese",
    "na": "Nauru",
    "nb": "Norwegian",
    "nd": "North Ndebele",
    "ne": "Nepali",
    "ng": "Ndonga",
    "nl": "Dutch",
    "nn": "Norwegian",
    "no": "Norwegian",
    "nr": "South Ndebele",
    "nv": "Navajo",
    "ny": "Chichewa",
    "oc": "Occitan",
    "oj": "Ojibwa",
    "om": "Oromo",
    "or": "Oriya",
    "os": "Ossetic",
    "pa": "Panjabi",
    "pi": "Pali",
    "pl": "Polish",
    "ps": "Pushto",
    "pt": "Portuguese",
    "qu": "Quechua",
    "rm": "Romansh",
    "rn": "Rundi",
    "ro": "Romanian",
    "ru": "Russian",
    "rw": "Kinyarwanda",
    "sa": "Sanskrit",
    "sc": "Sardinian",
    "sd": "Sindhi",
    "se": "Northern Sami",
    "sg": "Sango",
    "si": "Sinhala",
    "sk": "Slovak",
    "sl": "Slovenian",
    "sm": "Samoan",
    "sn": "Shona",
    "so": "Somali",
    "sq": "Albanian",
    "sr": "Serbian",
    "ss": "Swati",
    "st": "Sotho, Southern",
    "su": "Sundanese",
    "sv": "Swedish",
    "sw": "Swahili",
    "ta": "Tamil",
    "te": "Telugu",
    "tg": "Tajik",
    "th": "Thai",
    "ti": "Tigrinya",
    "tk": "Turkmen",
    "tl": "Tagalog",
    "tn": "Tswana",
    "to": "Tonga",
    "tr": "Turkish",
    "ts": "Tsonga",
    "tt": "Tatar",
    "tw": "Twi",
    "ty": "Tahitian",
    "ug": "Uighur",
    "uk": "Ukrainian",
    "ur": "Urdu",
    "uz": "Uzbek",
    "ve": "Venda",
    "vi": "Vietnamese",
    "vo": "Volapük",
    "wa": "Walloon",
    "wo": "Wolof",
    "xh": "Xhosa",
    "yi": "Yiddish",
    "yo": "Yoruba",
    "za": "Zhuang",
    "zh": "Chinese",
    "zu": "Zulu"
  },
  "landing_page": {
    "sign_up_tab": "Sign up",
    "sign_in_tab": "Log in"
  },
  "locale": "en",
  "maintenance": {
    "title": "We'll be back soon!",
    "message": "We're performing some maintenance at the moment. We apologize for the inconvenience.",
    "read_articles": "In the meantime, you can read some of our <0>latest articles</0> below or join our <1>Discord community</1>."
  },
  "mentions": {
    "reply": {
      "placeholder": "Enter your comment...",
      "title": "Reply with a comment",
      "your_reply": "Your reply"
    },
    "title": "Mentions"
  },
  "navigation": {
    "dashboard": "Dashboard",
    "automation": "Automation",
    "agent": "AI Agent",
    "calendar": "Calendar",
    "campaigns": "Campaigns",
    "contacts": "Contacts",
    "content": "Content",
    "conversion_links": "Conversion links",
    "folders": "Folders",
    "inbox": "Inbox",
    "intents": "Intents",
    "payments": "Payments",
    "profile": "Profile",
    "scenarios": "Scenarios",
    "surveys": "Surveys",
    "segments": "Segments",
    "signout": "Sign Out",
    "request_access": "Request access",
    "beta": "Beta",
    "medias": "Posts & Reels",
    "stories": "Stories",
    "menu": "Menu",
    "help": "Get help"
  },
  "notifications": {
    "activate": "Activate",
    "alert_title": "Activate notifications",
    "business_description": "Notifications for payments and appointments.",
    "business_title": "Business",
    "folders": {
      "alert_text": "You can activate notifications for this folder only",
      "name": "Manage folder notifications"
    },
    "folders_description": "Notifications for your different folders.",
    "folders_title": "Folder",
    "inbox_description": "Notifications for incoming messages.",
    "inbox_title": "Inbox",
    "later": "Later",
    "messages": {
      "alert_text": "Receive notifications for incoming DMs based on your preferences",
      "all": {
        "description": "Bring it on! Every like, message, reaction, mention, etc.",
        "label": "All messages"
      },
      "disabled": {
        "cta": "I don't want notifications",
        "description": "Disable all incoming message notifications.",
        "label": "No notifications"
      },
      "first": {
        "description": "Contacts who send you a message for the first time.",
        "label": "First messages only"
      },
      "less": {
        "description": "Everything except likes, story reactions and mentions.",
        "label": "Less messages"
      },
      "preferences": "Select your notifications preferences",
      "tagline": "Clear out the noise"
    },
    "permissions_missing_action": "Go to settings",
    "permissions_missing_text": "You must authorize Inrō to send you notifications",
    "permissions_missing_title": "Permissions missing",
    "preview_label": "Notification preview",
    "preview_time": "2m ago",
    "title": "Notifications",
    "weekly": {
      "alert_text": "Receive a weekly notification with a summary of your inbox statistics",
      "name": "Weekly statistics",
      "preview_text": "You have received 240 messages, 120 story reactions and answered 130 messages. 80 messages awaiting.",
      "preview_title": "130 messages answered "
    }
  },
  "onboarding": {
    "navigation": {
      "title": "Your onboarding",
      "connect_instagram": {
        "title": "Connect Instagram",
        "description": "Link your account",
        "time": "2 min"
      },
      "about_you": {
        "title": "About you",
        "description": "Tell us more about yourself",
        "time": "1 min"
      },
      "goal": {
        "title": "Your goals",
        "description": "How do you plan to use Inrō?",
        "time": "1 min"
      },
      "select_plan": {
        "title": "Start your free trial",
        "description": "Congrats! You're all set!",
        "time": "1 min"
      }
    },
    "connect_instagram": {
      "title": "Connect your Instagram account",
      "description": "Synchronize your inbox and content to start automating your DMs and comments.",
      "connect_title": "Connect your accounts",
      "facebook_page_text": "Your Instagram account must be linked to a **Facebook page**.",
      "connect_facebook_check": "I have a **Facebook page** linked to my account",
      "connect_text": "To connect your inbox, you need an **Instagram Business or Creator** account.",
      "connect_facebook_cta": "Authenticate with Facebook",
      "connect_instagram_cta": "Authenticate with Instagram",
      "facebook_method": "I want to authenticate through Facebook",
      "instagram_method": "I want to authenticate through Instagram",
      "username_label": "What's your Instagram username?",
      "username_placeholder": "best_account_ever",
      "username_button": "Find Instagram account",
      "username_error": "There is no Business or Creator account with this username.",
      "username_error_button": "How to switch to a Business account",
      "disclaimer": "Inrō is a verified <0>Meta Tech Provider</0>. We are committed to protecting your data and privacy and abide by Meta's <1>Developer Platform Terms</1>.",
      "learn_more": "Learn more about Meta Tech Providers",
      "show_me_how": "Show me how to connect my account",
      "not_your_account": "Not your account?",
      "send_link": "Send a link to the account's owner",
      "link_label": "Link to connect the Instagram account",
      "link_notice": "Send this link to the owner of the Instagram account so they can connect it themselves. They will not have access to your Inrō account.",
      "link_description": "They will need their Instagram credentials",
      "public_connect": {
        "title": "Connect your Instagram account",
        "description": "You have been invited to connect the following Instagram account to Inrō.",
        "cta": "Connect Instagram",
        "reconnect_title": "⚠️ Reconnect your Instagram account",
        "reconnect_description": "We lost connection with your account, please reconnect it to continue using Inrō.",
        "reconnect_cta": "Reconnect Instagram",
        "success_title": "🎉 Instagram account connected",
        "success_description": "The following Instagram account has been connected to Inrō.",
        "template_disclaimer": "This automation will be activated on your account"
      },
      "help": {
        "title": "Videos to guide you",
        "link": "How to link your Facebook page",
        "switch": "How to switch to Business?"
      }
    },
    "features": {
      "title": "Powerful features to grow your Instagram",
      "comment_automation": "Auto-reply to comments",
      "dm_automation": "Automate your inbox",
      "dm_campaigns": "Send DM campaigns",
      "ai_agent": "Build an AI Agent",
      "and_also": "And also:",
      "collect_emails": "Collect emails",
      "ask_for_follow": "Ask for a follow",
      "audio_messages": "Send audio messages",
      "ecommerce_website": "Connect ecommerce website",
      "send_surveys": "Send surveys",
      "collect_payments": "Collect payments",
      "and_more": "And many more!",
      "cta": "Discover all features"
    },
    "about_you": {
      "title": "Tell us more about yourself",
      "description": "Help us to improve Inrō by providing details about you and your company, this will help us to prioritize which features our team should focus on.",
      "your_profile": "Your profile",
      "app_website": "App or Website",
      "brand": "Ecommerce Brand",
      "content_creator": "Content Creator",
      "small_business": "Small Business",
      "other": "Other",
      "company_size": "Company size",
      "your_industry": "Your industry",
      "industry_placeholder": "Select industry",
      "fashion": "Fashion",
      "beauty": "Beauty & Cosmetics",
      "tech": "Tech & Software",
      "food": "Food & Beverage",
      "music": "Music & Entertainment",
      "hear_about": "How did you hear about Inrō?",
      "hear_about_placeholder": "Select an option",
      "website": "Company website",
      "instagram": "A post or ad on Instagram",
      "search": "A result on a search engine",
      "article": "I read about it in an article",
      "word_of_mouth": "Someone told me about it",
      "product_hunt": "On Product Hunt",
      "ai_search": "From an AI search (ChatGPT, etc.)",
      "other_source": "Other",
      "linkedin": "A post or ad on LinkedIn",
      "youtube": "A YouTube video or ad"
    },
    "goals": {
      "title": "Tell us more about yourself!",
      "description": "We'll use this to recommend the best path to getting started and personalize your experience.",
      "icp_title": "What describes you best?",
      "goals_title": "What are your main goals?",
      "hear_about_title": "How did you hear about us?",
      "cta": "Connect your Instagram account",
      "bubbles": {
        "grow_followers": "Grow your followers",
        "collect_emails": "Collect emails",
        "capture_new_leads": "Capture new leads",
        "boost_engagement": "Boost engagement",
        "drive_sales": "Drive sales",
        "launch_product": "Launch a product",
        "collect_feedback": "Collect feedback",
        "content_creator": "Content creator",
        "ecommerce": "Ecommerce brand",
        "local_business": "Local business",
        "agency": "Agency",
        "coach": "Coach",
        "artist": "Artist",
        "other": "Other",
        "instagram": "Instagram ad or post",
        "search": "Search engine",
        "article": "Blog article",
        "word_of_mouth": "Word-of-mouth",
        "ai_search": "AI search (ChatGPT, etc.)",
        "linkedin": "LinkedIn post or ad",
        "youtube": "YouTube video or ad",
        "other_source": "Other"
      }
    },
    "select_plan": {
      "title": "Congrats! You're all set!",
      "description": "Select your plan and start automating your Instagram account.",
      "discount_applied": "Your discount code <0>{{code}}</0> will be applied automatically at checkout.",
      "free_plan": {
        "title": "Free plan",
        "description": "Use simple features to manage your audience.",
        "price": "0€",
        "credit_card": "No credit card required",
        "contacts_limit": "100 contacts per month",
        "automation_limit": "3 active automations",
        "no_mcp_server": "MCP server and API not included",
        "cta": "Select free plan"
      },
      "pro_plan": {
        "title": "Pro plan",
        "description": "Test all features for {{count}} days for free before upgrading.",
        "price": "Free trial",
        "cta": "Start my pro trial",
        "per_month": "12.99€/month after {{count}} days",
        "cancel_anytime": "You can cancel at anytime",
        "contacts_limit": "500+ contacts per month",
        "price_scales": "Price scales with usage",
        "automation_limit": "Unlimited automations",
        "ai_features": "AI Agent features",
        "mcp_server": "MCP server and API access",
        "description_no_trial": "Get started with all Pro features today.",
        "price_no_trial": "Pro plan",
        "cta_no_trial": "Susbcribe now",
        "per_month_no_trial": "12.99€/month",
        "link": "Show monthly price"
      },
      "enterprise_plan": {
        "title": "Enterprise plan",
        "description": "Unlock the full potential of Inrō for your business.",
        "price": "Let's talk",
        "cta": "Contact sales",
        "pay_monthly": "Pay monthly or yearly",
        "save_yearly": "Save 10% with yearly payment",
        "contacts_limit": "Unlimited activated contacts",
        "automation_limit": "Unlimited automations",
        "payments": "Accept payments in DMs",
        "ai_agent": "AI Agent early access"
      },
      "annual_plan": {
        "title": "Annual plan",
        "description": "Best value for growing brands ready to scale with automation.",
        "price": "1249€",
        "full_price": "1800€",
        "cta": "Select annual plan",
        "pay_annually": "Pay annually",
        "transfer_method": "Bank transfer available",
        "contacts_limit": "10k contacts per month",
        "every_month": "Resets every month",
        "automation_limit": "Unlimited automations",
        "mcp_server": "MCP server and API access",
        "link": "Save money with annual plan"
      },
      "description_no_trial": "Subscribe and enjoy all the features of Inrō Pro."
    },
    "checklist": {
      "title": "Welcome aboard!",
      "subtitle": "Follow these steps to get started.",
      "dm_inro": "Send us a DM on Instagram",
      "create_folder": "Create a folder in your inbox",
      "move_to_folder": "Move a contact to a folder",
      "create_scenario": "Create an automation scenario",
      "trigger_scenario": "Trigger your scenario",
      "send_campaign": "Send your first campaign"
    }
  },
  "payments": {
    "actions": {
      "archive_one": "Archive payment",
      "archive_other": "Archive payments",
      "archive_confirm": "Archived payments are hidden from the inbox. This action cannot be undone.",
      "cancel_one": "Cancel payment",
      "cancel_other": "Cancel payments",
      "edit_one": "Edit payment",
      "edit_other": "Edit payments",
      "loading": "Updating payments...",
      "mark_as_paid": "Mark as paid",
      "payments_selected_one": "One payment selected",
      "payments_selected_other": "{{ count }} payments selected",
      "refund": "Refund payment",
      "revert_to_pending": "Revert to pending",
      "set_up": "Set up payments",
      "title": "Payment link",
      "view_details": "View payment details",
      "refresh": "Refresh payment status"
    },
    "balance": {
      "available_now": "Available now",
      "available_soon": "Available soon",
      "awaiting": "Awaiting payment",
      "dashboard": "View Stripe dashboard",
      "estimated_date": "Est.",
      "setup": "Setup bank details",
      "setup_notice": "Once you start receiving payments you will find your account balance here.",
      "title": "Balance",
      "transfer": "Transfer to a bank account",
      "transfer_info": "Transfers are made automatically every 1-2 business days.",
      "transfers": "Transfers history"
    },
    "details": {
      "amount_received": "Amount received",
      "amount_requested": "Amount requested",
      "approximate_notice": "Approximate amount, exact payment processing fees depend on the client's payment method.",
      "goToConversation": "Go to conversation",
      "payment_processing_fees": "Payment processing fees",
      "service_fees": "Service fees",
      "status": "Payment status",
      "title": "Payment details"
    },
    "image": {
      "amount": "Amount",
      "service_fees": "Service fees",
      "subtitle": "@{{name}} sent a payment link",
      "title": "Payment required",
      "total_amount": "Total amount"
    },
    "labels": {
      "any": "Any payment",
      "attempted": "Payment attempted",
      "awaiting": "Awaiting payment",
      "cancelled": "Payment cancelled",
      "error": "Payment error",
      "failed": "Payment failed",
      "manual": "Marked as paid",
      "paid": "Payment confirmed",
      "pending": "Awaiting payment",
      "refunded": "Payment refunded",
      "sent": "Payment link sent"
    },
    "modal": {
      "description": "What is this payment for?",
      "description_placeholder": "e.g. Deposit for your order",
      "send": "Send payment link",
      "service_fees": "{{total}} after fees",
      "set_up_cta": "Set up account",
      "set_up_notice": "Before you can receive payments, you need to set up your payment account.",
      "title": "Generate your payment link"
    },
    "no_contact": "No contact",
    "no_filtered_payments": "No payments found",
    "no_filtered_payments_description": "We couldn't find any payment matching your search criteria.",
    "no_payments": "No payments",
    "no_payments_cta": "Set up payments",
    "no_payments_description": "Start sending payments or set up your payment settings to get started.",
    "page": {
      "accept_terms": "<0>I accept the </0><1>terms of sale</1>.",
      "can_close": "You can now close this window.",
      "confirm": "Confirm payment",
      "download_invoice": "Download invoice",
      "in_progress": "Payment in progress..",
      "invoice_address": "Billing address",
      "invoice_company": "Company name",
      "invoice_extra": "Extra info (VAT number, etc.)",
      "invoice_form_title": "Invoice details",
      "payment_failed": "Payment error, please try again.",
      "request_invoice": "Request invoice",
      "stripe_notice": "Payment secured by Stripe",
      "thank_you": "Thank you for your payment."
    },
    "paid": "{{amount}} paid",
    "paid_on": "Paid on {{date}}",
    "settings": {
      "details": {
        "address": "Address",
        "address_details": "Additional details",
        "address_details_placeholder": "Floor, building, etc.",
        "city": "City",
        "company_name": "Company name",
        "country": "Country",
        "description": "Save your personal or company information",
        "dob": "Date of birth",
        "first_name": "First name",
        "invalid_phone": "Invalid phone number",
        "last_name": "Last name",
        "locked": "You cannot update your information once you have started setting up your Stripe account. Please contact us if you need to change it.",
        "phone": "Phone number",
        "postal_code": "Postal code",
        "title": "Account information",
        "type_company": "Company",
        "type_individual": "Individual"
      },
      "general": {
        "added": "Added to total",
        "country_description": "Select the country where you operate and the currency you want to receive payments in.",
        "country_not_supported": "This country is not supported yet",
        "country_title": "Country & currency",
        "currency": "Currency",
        "currency_placeholder": "Pick a currency",
        "description": "Country, currency & payment link settings",
        "included": "Included in total",
        "locked": "You cannot update your country once you have started setting up your Stripe account. Please contact us if you need to change it.",
        "payment_card_background": "Payment card background",
        "payment_card_background_info": "Personalize your payment card background to your brand identity.",
        "service_fees": "Service fees",
        "service_fees_added": "The {{fee}}% fees are added to the amount you request: you send {{send}}, your client pays {{pay}}, you receive {{receive}}. ",
        "service_fees_included": "The {{fee}}% fees are included in the amount you request: you send {{send}}, your client pays {{pay}}, you receive {{receive}}. ",
        "title": "General settings",
        "upload": "Upload your own background",
        "validation_error": "Please select a country and a currency"
      },
      "notice": {
        "cta": "Show me how",
        "text": "Start sending payments to your clients.",
        "video_text": "Send a payment link to your clients directly in their DMs."
      },
      "stripe": {
        "description": "Set up a free Stripe account in 2 minutes",
        "title": "Set up Stripe account"
      },
      "terms": {
        "description": "Your clients will need to accept your terms to pay.",
        "input": "Link to your terms",
        "title": "Ask to accept terms"
      },
      "title": "Payment settings"
    },
    "filters": {
      "status": {
        "all": "All payments",
        "awaiting": "Awaiting",
        "paid": "Paid",
        "manual": "Marked as paid",
        "cancelled": "Cancelled"
      }
    },
    "show_all": "Show all payments",
    "table": {
      "columns": {
        "contact": "Contact",
        "amount": "Amount",
        "status": "Status",
        "created_at": "Date"
      }
    },
    "title": "Payments",
    "tabs": {
      "active": "Active",
      "archived": "Archived"
    }
  },
  "integrations": {
    "confirm_disconnect": "Are you sure you want to disconnect this account?",
    "calendly": {
      "connect": "Connect Calendly account",
      "external_label": "View account",
      "subtitle": "Connect a Calendly account to send appointment links to your contacts.",
      "title": "Calendly account",
      "free_plan": "This is a free Calendly account. Some features will not be available unless you switch to a paid plan."
    },
    "eventbrite": {
      "connect": "Connect Eventbrite account",
      "subtitle": "Connect an Eventbrite account to send event links to your contacts.",
      "title": "Eventbrite account",
      "token": "Private token"
    },
    "meta_conversions_api": {
      "connect": "Connect Meta Conversions API",
      "subtitle": "Connect to Meta Conversions API to send conversion events.",
      "title": "Meta Conversions API",
      "connected": "Connected",
      "connected_on": "Connected on {{date}}",
      "not_connected": "Not connected",
      "expires_at": "Token expires: {{date}}",
      "expired": "Your access token has expired. Please reconnect.",
      "expiring_soon": "Your access token will expire soon. Please refresh."
    },
    "make": {
      "title": "Make",
      "subtitle": "Connect with 8000+ apps through our Make integration.",
      "connect": "Connect Make account",
      "connected_on": "Connected on {{date}}"
    },
    "private_api": {
      "title": "Private API",
      "subtitle": "Use our private API to connect your own tools and services.",
      "token": "API token",
      "view_documentation": "View documentation",
      "regenerate": "Regenerate",
      "regenerate_confirm_title": "Regenerate API token?",
      "regenerate_confirm_message": "Your current token will stop working immediately. Any tools or services using it will need to be updated with the new token.",
      "regenerated": "API token regenerated!"
    },
    "mcp_server": {
      "title": "MCP Server",
      "subtitle": "Connect your Agents to our MCP server to give them access to Instagram features.",
      "notice": "Authenticate via Oauth or use your Private API token",
      "pro_only": "Available on the Pro plan.",
      "steps_title": "How to connect",
      "step_1": "Copy the MCP server URL above.",
      "step_2": "In your agent, add a new connector and paste the URL.",
      "step_3": "Authenticate with Inrō when prompted to give your agent access.",
      "documentation": "View full documentation",
      "agents": {
        "claude": "Claude",
        "chatgpt": "ChatGPT",
        "other": "Other agents"
      },
      "claude": {
        "cta": "Open in Claude directory",
        "step_1": "Sign in to Claude and open the Connectors directory.",
        "step_2": "Find the Inrō connector and Add it to your account.",
        "step_3": "Authenticate with Inrō and authorize access to Claude."
      }
    },
    "openai": {
      "title": "OpenAI API Key",
      "subtitle": "Use a custom OpenAI API key for all AI features in your account.",
      "key_set": "A custom API key is configured.",
      "placeholder": "sk-...",
      "save": "Save key",
      "saved": "API key saved",
      "remove": "Remove key",
      "removed": "API key removed",
      "model_label": "Model",
      "model_fast": "Fast (GPT-6 Luna)",
      "model_balanced": "Balanced (GPT-6.1 Terra)",
      "model_smart": "Smart (GPT-6.1 Sol)",
      "reasoning_label": "Reasoning",
      "reasoning_low": "Low",
      "reasoning_medium": "Medium",
      "reasoning_high": "High",
      "default": "Default: {{value}}",
      "settings_saved": "Settings saved",
      "high_reasoning_warning": "High reasoning makes replies slower and uses more of your OpenAI credit."
    },
    "shopify": {
      "connect": "Connect Shopify account",
      "external_label": "View shop",
      "subtitle": "Connect a Shopify store to send checkout links to your contacts",
      "title": "Shopify account",
      "token": "Custom app API token",
      "get_token": "How to get your API token?",
      "save": "Save details",
      "or": "or",
      "oauth_connect": "Connect Shopify store",
      "install": {
        "title": "Connect your Shopify store",
        "description": "Complete the setup in a new tab, then come back here when done.",
        "cta": "Open setup",
        "already_done": "Already done?",
        "refresh": "Refresh this page"
      },
      "choose_organization": {
        "title": "Choose an account",
        "subtitle": "Select which Instagram account to connect with your Shopify store.",
        "connect": "Connect"
      },
      "missing_scopes": "Some API scopes are missing from the integration.",
      "inject_script": "Install tracking script",
      "script_missing": "Tracking script is not installed on your store."
    },
    "stripe": {
      "title": "Stripe account",
      "subtitle": "Connect a Stripe account to send payment links to your contacts.",
      "public_key": "Publishable API key",
      "private_key": "Restricted API key",
      "private_key_saved": "Key saved — paste a new one to replace it",
      "get_api_key": "How to get your API keys?",
      "invalid_key": "Stripe rejected this key. Paste it again in full.",
      "missing_permissions": "Payments will fail with this key. Add these permissions to it in Stripe:",
      "permissions": {
        "payment_intents_write": "PaymentIntents: Write",
        "charges_read": "Charges: Read",
        "payment_methods_read": "PaymentMethods: Read",
        "refunds_write": "Refunds: Write",
        "balance_transactions_read": "Balance transaction sources: Read"
      }
    },
    "eleven_labs": {
      "title": "Eleven Labs",
      "subtitle": "Connect with Eleven Labs to generate voice messages automatically.",
      "token": "Custom app API token",
      "get_token": "How to get your API token?",
      "save": "Save details"
    },
    "high_level": {
      "title": "HighLevel CRM",
      "subtitle": "Connect HighLevel CRM to automatically create or update contacts.",
      "api_key": "API key",
      "location_id": "Location ID",
      "save": "Save details",
      "connected": "Connected",
      "not_connected": "HighLevel is not connected",
      "invalid_api_key": "Invalid API key",
      "location_not_found": "Location ID not found",
      "invalid_credentials": "Invalid API key or Location ID"
    },
    "custom_domain": {
      "description": "Your domain name will be used for all links sent in your scenarios and campaigns, e.g. conversion links, payment links, surveys, etc.",
      "input_label": "Custom domain name",
      "input_placeholder": "e.g. instagram.mydomain.com",
      "input_notice": "Only subdomains are supported, not root domains.",
      "refresh_status": "Refresh status",
      "refresh_notice": "It can take up to 24h for your domain to be verified.",
      "success_notice": "Your custom domain is now set up and ready to use.",
      "dns_instructions": "Add the following DNS records to your domain provider:",
      "type": "Type",
      "cname": "CNAME",
      "host": "Host",
      "value": "Value"
    },
    "email_signatures": {
      "title": "Email addresses",
      "subtitle": "Authorize email addresses to use in your scenarios and campaigns.",
      "add_signature": "Add a new email",
      "edit_signature": "Edit email",
      "delete_signature": "Delete this email?",
      "name_label": "Name",
      "name_placeholder": "Alicia from MyCompany",
      "email_label": "Email address",
      "email_placeholder": "alicia@example.com",
      "email_notice": "Public domain emails (gmail, yahoo, etc.) are not supported.",
      "add_signature_error": "There was an error adding this email address.",
      "confirmed": "Confirmed",
      "pending": "Pending",
      "resend_confirmation": "Resend confirmation email",
      "confirmation_sent": "Check your inbox!"
    }
  },
  "opt_out": {
    "title": "Opt-out detection",
    "description": "Automatically detect contacts who want to opt-out from your messages.",
    "activate_prompt": "Enable opt-out detection?",
    "deactivate_prompt": "Disable opt-out detection?",
    "menu_message": "Persistent menu item",
    "menu_message_placeholder": "Type your menu item",
    "confirmation_message": "Send a confirmation message",
    "confirmation_message_placeholder": "We won't send you any more messages...",
    "keywords": "Keywords detection",
    "new_keyword": "New keyword",
    "ai_detection": "AI detection",
    "ai_detection_description": "The AI agent will detect opt-out messages and automatically unsubscribe the contact."
  },
  "persistent_menu": {
    "title": "Manage persistent menu",
    "description": "Contacts open it from ☰ in the conversation, over whatever you have said to each other.",
    "module_description": "A menu contacts open from ☰ in your Instagram conversation.",
    "learn_more": "Learn more about persistent menu",
    "new_item_placeholder": "Type your menu entry",
    "new_item_label": "New menu entry",
    "add_new_item": "Add a menu entry",
    "add_item": "Add to menu",
    "when_click": "When tapped",
    "send_dm": "Send a message",
    "open_link": "Open a link",
    "label_label": "Menu entry label",
    "url_label": "Link URL",
    "open_url": "Open this link",
    "sends_message": "Sends you a message",
    "opts_out": "Opts the contact out of your messages",
    "delete": "Remove this entry from the menu",
    "delete_confirm": "Remove this entry from the menu?",
    "empty": "No menu yet. Add an entry and ☰ opens it right away.",
    "limit_reached": "The menu is full — Instagram shows 5 entries at most",
    "optout_tooltip": "This entry belongs to the opt-out setting. Rename or remove it there.",
    "preview": {
      "business_chat": "Business chat",
      "tap_to_send": "Tap to send",
      "empty": "Nothing in the menu — ☰ opens only Instagram's own options."
    }
  },
  "platform_logs": {
    "title": "Platform logs"
  },
  "profile": {
    "account": "Accounts",
    "account_holder": "Account holder",
    "account_updated": "Your account has been updated",
    "balance": "Balance",
    "bank_details": "Bank details",
    "business": "Business",
    "cancel_delete": "Cancel account deletion",
    "connected_accounts": "Connected accounts",
    "contact_us": "Contact us",
    "create_organization": "Create a new account",
    "custom_domain": "Domain & emails",
    "delete_action": "Delete account",
    "delete_confirm": "All your data will be permanently deleted and you will not be able to log in anymore. Your conversations in Instagram will not be affected.",
    "delete_notice": "Your account will be deleted permanently on {{time}}.",
    "delete_confirmation": "Your account will be deleted in 24h. You can cancel at any time.",
    "delete_organization": "Delete account",
    "delete_organization_confirm": "Are you sure you want to delete this account? All its data will be deleted. This action is irreversible.",
    "download_payments": "Download as Excel file",
    "instagram_account": {
      "confirm_disconnect": "After 24 hours, all Instagram conversations and contacts will be deleted from Inrō unless your reconnect your account. Are you sure?",
      "connect": "Connect Instagram account",
      "connected_with_account": "Connected with account",
      "reconnect_account": "Reconnect account",
      "reconnect_modal": {
        "title": "Reconnect your account",
        "description": "Make sure to sign in with the same account to avoid any issue.",
        "facebook_user": "Facebook user",
        "facebook_page": "Facebook page",
        "instagram_account": "Instagram account"
      },
      "reconnect_error": {
        "title": "Reconnection failed",
        "invalid_account": "You signed in with a different Instagram account. Nothing changed. Try again with @{{username}}.",
        "account_not_found": "No Instagram account was found for that login. Nothing changed. Try again with the Facebook page linked to @{{username}}.",
        "no_page": "No Facebook page was selected. Nothing changed. Try again and select the page linked to @{{username}}.",
        "multiple_pages": "More than one Facebook page was selected. Nothing changed. Try again with only the page linked to @{{username}}.",
        "already_connected": "This Instagram account is already connected elsewhere. Nothing changed.",
        "wrong_login_method": "This account was connected with another login method. Nothing changed. Use the same method as before.",
        "default": "We couldn't reconnect your account. Nothing changed. Try again."
      },
      "metrics_report": {
        "title": "Advanced account report",
        "generate": "Generate report",
        "download": "Download report",
        "regenerate": "Regenerate report",
        "generated_on": "Generated on {{date}}",
        "generating": "Generating. We'll email it to you when it's ready."
      },
      "unlink": {
        "button": "Disconnect Instagram account",
        "title": "Disconnect @{{username}}?",
        "warning": "This permanently deletes the data below and takes you back to onboarding. This cannot be undone.",
        "counts": {
          "contacts": "Contacts",
          "conversations": "Conversations",
          "messages": "Messages",
          "comments": "Comments",
          "mentions": "Mentions",
          "medias": "Posts",
          "stories": "Stories",
          "executions": "Automation history"
        },
        "kept": "Your automations, campaigns and folders are kept.",
        "type_username": "Type **{{username}}** to confirm",
        "confirm": "Disconnect and delete data"
      },
      "view_details": "View details",
      "connection_issue": "Instagram connection issue",
      "reconnect_hint": "Reconnect to fix permission or sync issues. Sign in with the same account.",
      "details": {
        "status": "Status",
        "connected_since": "Connected since"
      },
      "followers": "Followers",
      "following": "Following",
      "subtitle": "Connect your Instagram inbox to manage and send messages.",
      "title": "Instagram account",
      "missing_permissions": {
        "title": "Permissions missing",
        "description": "We are missing some permissions on your Instagram account. Some features may not work as intended.",
        "short_description": "Some features may not work as intended.",
        "fix": "Fix permissions",
        "reconnect": "Reconnect to fix permissions",
        "public_profile": "Public Profile - Access your account's public information",
        "instagram_basic": "Basic Profile - Access your account's information an recent posts",
        "instagram_manage_messages": "Direct Messages - Send DMs to your contacts",
        "instagram_manage_comments": "Comments - Reply to comments on your posts",
        "instagram_manage_insights": "Stories - Get insights on your recent stories",
        "instagram_business_manage_messages": "Direct Messages - Send DMs to your contacts",
        "instagram_business_manage_insights": "Insights - Get insights on your posts and stories",
        "pages_show_list": "Pages - List your connected Facebook pages",
        "pages_messaging": "Private Replies - Send a DM to users who comment on your posts",
        "pages_manage_metadata": "Direct Messages - Synchronise your DMs in real time",
        "pages_read_engagement": "Story replies - Detect replies to your stories"
      },
      "authorization_error": {
        "title": "Authorization error",
        "description": "There is an issue with your Instagram account.",
        "view_issue": "View issue"
      },
      "status": {
        "connected": "Connected",
        "disconnected": "Disconnected",
        "error": "Connection error",
        "restricted": "Restricted",
        "limited": "Limited",
        "never_connected": "Not connected",
        "not_connected": "Not connected",
        "connection_error": "Connection error"
      },
      "connect_new_account": "Connect another account"
    },
    "contact_support": "Contact support",
    "integrations": "Integrations",
    "api": "API & MCP",
    "leave_organization": "Leave account",
    "leave_organization_confirm": "Are you sure you want to leave this account? You will lose access to all its data.",
    "log_out": "Log out",
    "manage_subscription": "Manage subscription",
    "organization_name": "Account name",
    "team_members": {
      "add_user": "Add user",
      "add_user_dialog": "Invite user to account",
      "owner": "Owner",
      "member": "Member",
      "make_owner": "Make owner",
      "make_owner_confirm": "Make this user the owner? You will become a regular member.",
      "remove_user": "Remove user",
      "remove_user_confirm": "Remove this user from account?",
      "title": "Team members",
      "hide_inbox": "Prevent access to inbox"
    },
    "payment_settings": "Payment settings",
    "payments": "Payments",
    "payments_history": "Payments history",
    "personal_account": "My account",
    "platform_logs": "Platform logs",
    "privacy_policy": "Privacy Policy",
    "referral_program": "Referral program",
    "share": "Share with friends",
    "switch_organization": "Switch account",
    "terms_of_service": "Terms of Service",
    "title": "Profile",
    "connect_new_account": "Connect new account",
    "switcher": {
      "title": "Switch account"
    },
    "privacy": {
      "title": "Privacy settings",
      "subtitle": "Choose which optional data we collect.",
      "refresh_title": "Refresh the page",
      "refresh_message": "The page needs to refresh to apply this change.",
      "refresh_confirm": "Refresh"
    }
  },
  "organization_suspended": {
    "title": "Account suspended",
    "message_before": "This account has been suspended for violating our ",
    "message_after": ". Your Instagram account has been disconnected and your automations can no longer run.",
    "terms_of_service": "Terms of Service",
    "contact_support_before": "If you believe this is a mistake, contact ",
    "contact_support_email": "support@inroapp.com",
    "contact_support_after": " to appeal this decision.",
    "switch_organization": "Switch account",
    "log_out": "Log out"
  },
  "promo_codes": {
    "activate": "Activate a promo code",
    "activated_codes": "Activated codes",
    "active_until": "Active until {{date}}",
    "custom_fee": "{{fee}}% service fees",
    "error": "Invalid code",
    "expired_on": "Expired on {{date}}",
    "label": "Enter code",
    "starts_on": "Starts on {{date}}",
    "success": "Code activated",
    "title": "Promo codes"
  },
  "referral_program": {
    "referral_code": "Referral code",
    "referral_code_description": "Give this code for other users to enter during sign up.",
    "referral_link_description": "Or share this link for the referral to be automatically applied.",
    "referred_accounts": "Referred users",
    "referred_accounts_description_one": "{{ count }} user has signed up with your referral code.",
    "referred_accounts_description_other": "{{ count }} users have signed up with your referral code."
  },
  "request_access": {
    "title": "Feature in Beta",
    "button": "Request access",
    "requested": "Access requested, we'll get back to you soon.",
    "ai_agent": "The AI Agent can answer frequently asked questions based on the data you train it with. This feature is in beta and available to a limited number of users. Request access to use it.",
    "ai_rewrite": "Your reply can be automatically rewritten by our AI to avoir repetition. This feature is in beta and available to a limited number of users. Request access to use it."
  },
  "shared": {
    "load_failed": "Couldn't load this. Try again.",
    "load_timeout": "This is taking too long. Try again.",
    "rerun_executions": {
      "title_limited": "Run pending executions",
      "title_disconnected": "Run paused executions",
      "description_limited": "These executions were paused because you reached your monthly contact activation limit. Choose which ones to run — the rest will be cancelled.",
      "description_disconnected": "These executions were paused while your Instagram account was disconnected. Choose which ones to run — the rest will be cancelled.",
      "windows": {
        "all": "All history",
        "week": "Last 7 days",
        "day": "Last 24 hours"
      },
      "executions_count_one": "{{count}} execution",
      "executions_count_other": "{{count}} executions",
      "run_button_one": "Run {{count}} execution",
      "run_button_other": "Run {{count}} executions",
      "recap_run_one": "<0>{{count}} execution</0> will run now ({{window}})",
      "recap_run_other": "<0>{{count}} executions</0> will run now ({{window}})",
      "recap_cancel_one": "<0>{{count}} older execution</0> will be cancelled",
      "recap_cancel_other": "<0>{{count}} older executions</0> will be cancelled",
      "recap_warning": "Cancelled executions cannot be recovered, but the warning will disappear.",
      "cancel_all_limited": "Cancel all limited executions",
      "cancel_all_confirm_limited": "Cancel all limited executions? They cannot be recovered.",
      "confirm_button": "Run {{run}} and cancel {{cancel}}"
    },
    "tasks_in_progress": "{{count}} tasks in progress",
    "ai": "AI",
    "active": "Active",
    "add": "Add",
    "add_emoji": "Add emoji",
    "add_keyword": "Add keyword",
    "add_image": "Add image",
    "added_to": "Added to",
    "removed_from": "Removed from",
    "change_image": "Change image",
    "remove_image": "Remove image",
    "add_video": "Add video",
    "change_video": "Change video",
    "remove_video": "Remove video",
    "add_media": "Add media",
    "change_media": "Change media",
    "remove_media": "Remove media",
    "all_day": "All day",
    "all": "All",
    "and": "and",
    "appt": "Appt",
    "back": "Back",
    "cancel": "Cancel",
    "cancel_changes": "Cancel changes",
    "cannot_load_media": "Cannot load media",
    "caption": "Caption",
    "clear": "Clear",
    "clear_selection": "Clear selection",
    "clicked": "Clicked",
    "close": "Close",
    "coming_soon": "Coming soon",
    "coming_soon_text": "This feature is not available yet. We're actively working on it and will update you when it's ready!",
    "completed": "Completed",
    "confirm": "Confirm",
    "connected": "Connected",
    "contacts": "Contacts",
    "contacts_one": "contact",
    "contacts_other": "contacts",
    "contacts_count_one": "1 contact",
    "contacts_count_other": "{{count}} contacts",
    "continue": "Continue",
    "converted": "Converted",
    "copied": "Copied to clipboard!",
    "copy": "Copy",
    "country_placeholder": "Pick a country",
    "create": "Create",
    "current_step": "Step {{step}} of {{total}}",
    "steps": "Steps",
    "date": "Date",
    "debug_mode_on": "Debug mode on",
    "debug_mode_off": "Debug mode off",
    "default": "Default",
    "delete": "Delete",
    "delete_confirm": "Confirm deletion?",
    "details": "Details",
    "disabled": "Disabled",
    "discard_changes": "Discard changes",
    "disconnect": "Disconnect",
    "do_it_later": "I'll do it later",
    "duplicate": "Duplicate",
    "edit": "Edit",
    "edit_selection": "Edit selection",
    "email": "Email",
    "else": "Else",
    "excluding": "Excluding",
    "select": "Select",
    "errors": {
      "contact_us": "Please contact us if the error persists.",
      "description": "Don't worry, we got notified and will fix this ASAP.",
      "refresh": "Refresh the page",
      "title": "😬 Something went wrong..."
    },
    "expired": "Expired",
    "filter_by": "Filter by",
    "filters_count_one": "1 filter",
    "filters_count_other": "{{count}} filters",
    "folders_count_one": "1 folder",
    "folders_count_other": "{{count}} folders",
    "free": "Free",
    "get_started": "Get started",
    "hide": "Hide",
    "unhide": "Unhide",
    "history": "History",
    "more_actions": "More actions",
    "inactive": "Inactive",
    "insights": "Insights",
    "icon": "Icon",
    "in_progress": "In progress",
    "last_saved": "Last saved {{date}}",
    "last_updated": "Last updated {{date}}",
    "learn_more": "Learn more",
    "lets_go": "Let's go!",
    "load_more": "Load more",
    "make_default": "Set as default",
    "media": "Media",
    "media_link": "Media link",
    "media_type": "Media type",
    "next": "Next",
    "no": "No",
    "no_data": "No data",
    "not_clicked": "Not clicked",
    "not_connected": "Not connected",
    "not_found_text": "The page you're looking for doesn't exist or has been moved.",
    "not_found_title": "Page not found",
    "not_started": "Not started",
    "not_answered": "Not answered",
    "number": "Number",
    "ok": "Ok",
    "optional": "Optional",
    "optout": "Opt-out",
    "or": "or",
    "other": "Other",
    "password": "Password",
    "password_confirmation": "Confirm password",
    "password_confirmation_placeholder": "Re-enter your new password",
    "password_placeholder": "Enter a new password",
    "password_too_short": "Password must be at least 6 characters long",
    "passwords_dont_match": "Passwords don't match",
    "phone": "Phone",
    "preview": "Preview",
    "previous": "Previous",
    "pro": "Pro",
    "read_more": "Read more",
    "reconnect": "Reconnect",
    "remove": "Remove",
    "replied": "Replied",
    "reply": "Reply",
    "request_review_action": "Leave a review",
    "request_review_text": "If yes, please take a minute to leave us a review on the App Store. It really helps us!",
    "request_review_title": "🫶 Are you enjoying Inrō?",
    "required": "Required",
    "rename": "Rename",
    "results": "Results",
    "retry": "Retry",
    "same_organization": "Same account",
    "save": "Save",
    "search": "Search",
    "select_option": "Select an option",
    "selected_one": "1 selected",
    "selected_other": "{{count}} selected",
    "send": "Send",
    "settings": "Settings",
    "share": "Share",
    "show_less": "Show less",
    "show_more": "Show more",
    "skip": "Skip",
    "template": "Template",
    "test": "Test",
    "text": "Text",
    "total": "Total",
    "total_count_one": "1 total",
    "total_count_other": "{{count}} total",
    "type_here": "Type here",
    "understood": "Understood!",
    "unsaved_changes": "You have unsaved changes. Do you want to discard them?",
    "use_setting": "Use setting",
    "view": "View",
    "view_all": "View all",
    "view_documentation": "View documentation",
    "view_media": "View media",
    "warning": "Warning",
    "yes": "Yes",
    "datepicker": {
      "separator": "to"
    },
    "read_article": "Read the article",
    "wait": "Wait",
    "from": "From",
    "new": "New",
    "edit_folder": "Edit folder",
    "delete_folder": "Delete folder",
    "create_folder": "Create folder",
    "duplicate_folder": "Duplicate folder",
    "keep_editing": "Keep editing",
    "save_changes_question": "Save your changes?",
    "unsaved_stay_draft": "Otherwise they stay as a draft.",
    "save_and_close": "Save and close",
    "close_keep_draft": "Close, keep draft",
    "error_loading": "Something went wrong while loading.",
    "error_saving": "Something went wrong while saving. Please try again.",
    "selected": "Selected",
    "open": "Open",
    "switch": "Switch",
    "mute": "Mute",
    "unmute": "Unmute"
  },
  "login": {
    "tagline": "Welcome back, \nit's nice to see you!",
    "description": "Sign in to your account and access your audience, campaigns and automations.",
    "facebook": "Log in with Facebook",
    "google": "Log in with Google",
    "instagram": "Log in with Instagram",
    "use_email": "Log in with email",
    "create_account": "Create an account",
    "email_title": "or log in with your email",
    "email_label": "Email",
    "email_placeholder": "e.g. linda.lee@sprawl.com",
    "password_label": "Password",
    "forgot_password": "Forgot your email or password?",
    "log_in": "Log in",
    "no_account": "No account yet?",
    "sign_up_today": "Sign up today"
  },
  "redemption": {
    "unlimited": "Unlimited",
    "allowances": {
      "accounts_one": "{{value}} Instagram account",
      "accounts_other": "{{value}} Instagram accounts",
      "contacts": "{{value}} contacts a month",
      "ai_credits": "{{value}} AI credits a month"
    }
  },
  "signup": {
    "tagline": "Nice to meet you, join us today!",
    "appsumo_banner": {
      "title": "Lifetime deal on AppSumo",
      "ends_in": "Time left on the AppSumo deal",
      "cta": "Get the deal",
      "units": {
        "days": "d",
        "hours": "h",
        "minutes": "m",
        "seconds": "s"
      }
    },
    "description": "You don't have access yet? Create your account then link your Instagram account to unlock DM automation!",
    "facebook": "Sign up with Facebook",
    "google": "Sign up with Google",
    "instagram": "Sign up with Instagram",
    "email_title": "or sign up with your email",
    "email_label": "Email",
    "email_placeholder": "e.g. linda.lee@sprawl.com",
    "name_label": "Name",
    "name_placeholder": "e.g. Linda Lee",
    "name_description": "This will be used as your account name",
    "instagram_label": "Instagram account",
    "instagram_placeholder": "e.g. @sprawlinc",
    "phone_label": "Phone number",
    "phone_placeholder": "e.g. +33 6 12 34 56 78",
    "company_label": "Company",
    "company_placeholder": "e.g. Sprawl Inc.",
    "password_label": "Password",
    "password_description": "6 characters minimum",
    "redeem_code": "Redeem a key",
    "redeem_code_invalid": "This link or key is invalid or has already been used.",
    "referral_code": "Add a referral code",
    "referral_code_description": "Referral codes can give you access to exclusive features and discounts",
    "referral_code_placeholder": "e.g. c8b5f9f4a37e",
    "referral_code_not_found": "This referral code is invalid or has expired.",
    "referred_by": "Referred by",
    "create_your_account": "Create your account",
    "already_account": "Already have an account?",
    "log_in": "Log in",
    "accept_terms": "By creating an account, you agree to our <0>terms of service</0> and <1>privacy policy</1>.",
    "special_offer": {
      "title": "Special offer",
      "offer": "14-day free period",
      "description": "Create your account now and try all our pro features for free for 14 days. No credit card required."
    }
  },
  "subscription": {
    "usage_history": {
      "title": "Usage history",
      "current": "Current",
      "activations_one": "{{formatted}} contact",
      "activations_other": "{{formatted}} contacts",
      "show_more": "Show more",
      "show_less": "Show less"
    },
    "usage_limits": {
      "title": "Set a monthly limit",
      "max_contact_activations": "Max activated contacts per month",
      "max_contact_activations_description": "Automations and campaigns pause once reached. Leave empty for no limit.",
      "no_limit": "No limit",
      "save": "Save",
      "saved": "Usage limit saved"
    },
    "lifetime": {
      "subtitle": "Lifetime deal",
      "renews_on": "Allowances renew on {{date}}",
      "slots": "{{used}} of {{total}} accounts",
      "slots_unlimited_one": "{{count}} account",
      "slots_unlimited_other": "{{count}} accounts",
      "upgrade_tier": "Upgrade your tier",
      "manage_caps": "Manage your caps",
      "rail_title": "You are on {{tier}}",
      "rail_description": "Your plan does not expire. What you ran into is a monthly cap — lift it on the billing page, or move up a tier where you bought the deal.",
      "caps_title": "What happens when you reach a cap",
      "caps_subtitle": "Allowances are shared across every account on your code, and renew every month.",
      "breakdown_title": "Usage by account",
      "this_account": "This account",
      "switch_to_account": "Switch to this account",
      "connection": {
        "disconnected": "Disconnected",
        "login_error": "Reconnection needed"
      },
      "openai_key_label": "Your OpenAI API key",
      "openai_key_sublabel": "Used for every account on your code, once the monthly credits are spent.",
      "update_error_title": "Could not save",
      "update_error_description": "Your billing settings were not changed. Please try again.",
      "add_accounts_title": "Add your other accounts",
      "add_accounts_subtitle": "Accounts you own that aren't on this deal yet. Adding one puts it on the same plan and the same shared allowances — its own billing history stays where it is.",
      "add_accounts_full": "Your deal covers {{total}} accounts and they are all in use. Free a slot, or move up a tier where you bought the deal.",
      "add_account": "Add to my deal",
      "add_account_error_title": "Could not add that account",
      "add_account_error_description": "Nothing was changed. Please try again, or contact support@inroapp.com.",
      "remove_account": "Remove from my deal",
      "remove_account_confirm": "Remove {{name}} from your deal? It goes back to a free plan. What it used this month still counts against your allowances.",
      "remove_account_error_title": "Could not remove that account",
      "remove_account_error_description": "Nothing was changed. Please try again, or contact support@inroapp.com.",
      "cap_modes": {
        "hard": "Stop at the cap",
        "hard_contacts": "New contacts stop being activated until the month renews. Nothing is charged.",
        "hard_credits": "AI steps are skipped until the month renews. Nothing is charged.",
        "auto_billing": "Keep going, bill me",
        "auto_billing_contacts": "Extra contacts are billed at the end of the month.",
        "auto_billing_contacts_priced": "Extra contacts are billed at {{price}} each at the end of the month.",
        "auto_billing_credits": "Extra credits are billed at the end of the month.",
        "auto_billing_credits_priced": "Extra credits are billed at {{price}} each at the end of the month.",
        "byok": "Use my own OpenAI key",
        "byok_description": "Once the monthly credits are spent, AI steps run on your key and we charge nothing."
      },
      "tiers": {
        "appsumo_1": "Lifetime deal - Tier 1",
        "appsumo_2": "Lifetime deal - Tier 2",
        "appsumo_3": "Lifetime deal - Tier 3",
        "appsumo_4": "Lifetime deal - Tier 4",
        "appsumo_5": "Lifetime deal - Tier 5",
        "lifetime_1": "Lifetime deal - Starter",
        "lifetime_2": "Lifetime deal - Growth",
        "lifetime_3": "Lifetime deal - Scale"
      },
      "card_required": "You will be asked for a card before this is switched on.",
      "card_on_file": "Billed to the card on file.",
      "invoices_title": "Overage invoices",
      "invoice_line": "{{units}} contacts, {{credits}} credits",
      "invoice_status": {
        "invoiced": "Awaiting payment",
        "paid": "Paid"
      },
      "auto_billing_confirm": "Past your allowance, extra {{unit}} are billed at {{price}} each at the end of the month. Switch this on?",
      "units": {
        "contacts": "contacts",
        "credits": "credits"
      },
      "running_title": "This month",
      "running_line": "{{units}} extra contacts, {{credits}} extra credits",
      "running_subtitle": "Not yet billed",
      "switch_account": "Switch account"
    },
    "ai_credits": {
      "title": "AI credits",
      "remaining_one": "{{count}} credit left",
      "remaining_other": "{{count}} credits left",
      "renews_on": "Renews on {{date}}",
      "exhausted_title": "You are out of AI credits",
      "exhausted_description": "AI steps are skipped until your credits renew. Switch on auto-billing or your own OpenAI key to keep them running.",
      "overage_one": "{{count}} extra credit billed this month",
      "overage_other": "{{count}} extra credits billed this month",
      "byok_active": "Running on your own OpenAI key"
    },
    "activated_contacts": "Activated contacts",
    "cancel_trial": "Cancel trial now",
    "cancelled": "Cancelled",
    "days_left_one": "1 day left",
    "days_left_other": "{{count}} days left",
    "free_plan": "Free plan",
    "free_trial": "Free trial",
    "manage_plan": "Manage billing",
    "customer": "Billing account",
    "pro_plan": "Pro plan",
    "renews_on": "Renews on {{date}}",
    "ends_on": "Ends on {{date}}",
    "resets_on": "Usage resets on {{date}}",
    "start_free_trial": "Start free trial",
    "trial_until": "Free trial ends {{date}}",
    "upgrade_to_pro_confirm": "End your free trial now and switch to Pro plan?",
    "upgrade_to_pro": "Upgrade to Pro",
    "pro_notice": {
      "title": "Pro plan required",
      "description": "This feature is only available in the Pro plan. Upgrade now to unlock it.",
      "cta": "Upgrade to Pro"
    },
    "redeem_codes": {
      "title": "Redeemed keys"
    },
    "redeem_code": {
      "have_code": "Redeem a key",
      "title": "Redeem a key",
      "button": "Redeem key",
      "placeholder": "Enter your key",
      "error_title": "Invalid key",
      "error_description": "This key is invalid, already used, or can't be applied to this account.",
      "success_title": "Key redeemed",
      "success_description": "Your plan is now active."
    },
    "payment_error": {
      "title": "Payment error",
      "description": "We couldn't process your last payment. Please try again or contact us for help.",
      "cta": "Manage payment",
      "unpaid_invoice_title": "Unpaid invoice",
      "unpaid_invoice_description": "Your payment of {{amount}} failed. Pay this invoice to restore features.",
      "pay_invoice": "Pay {{amount}}",
      "unpaid_invoices_title": "Unpaid invoices",
      "unpaid_invoices_description": "You have {{count}} unpaid invoices totalling {{amount}}. Settle them to restore features.",
      "pay_invoices": "Pay invoices",
      "managed_title": "Payment issue on the plan covering this account",
      "managed_description": "The partner managing this account has an unpaid invoice. Your plan pauses if it is not settled.",
      "managed_cta": "Go to partner dashboard"
    },
    "managed_by": "Managed by {{name}}",
    "managed_description": "Your plan is included in your partner's subscription.",
    "open_partner_dashboard": "Open partner dashboard",
    "pro_upgrade": {
      "contacts_are_free": "Contacts are stored free on every plan. You only pay for the ones you activate.",
      "ledger": {
        "heading": "What changes",
        "free_column": "Free",
        "pro_column": "Pro",
        "not_included": "Not included",
        "scenarios": {
          "label": "Automations running at once",
          "free": "3",
          "pro": "No limit"
        },
        "campaigns": {
          "label": "Campaigns you can send",
          "free": "3",
          "pro": "No limit"
        },
        "triggers": {
          "label": "Triggers and actions",
          "free": "Basic set",
          "pro": "All of them"
        },
        "ai": {
          "label": "AI Agent that replies in your voice",
          "pro": "Included"
        },
        "integrations": {
          "label": "Shopify, Calendly, webhooks, API, MCP",
          "pro": "Included"
        },
        "activations": {
          "label": "Contacts you automate each month",
          "free": "100",
          "pro": "No limit"
        },
        "members": {
          "label": "People who can log in",
          "free": "1",
          "pro": "10"
        }
      },
      "rail": {
        "price": "12.99€",
        "price_period": "/ month",
        "price_note": "500 automated contacts a month. Price scales with usage.",
        "trial_cta_one": "Start 1-day trial",
        "trial_cta_other": "Start {{count}}-day trial",
        "reassurance": "Cancel any time.",
        "timeline_today_label": "Today",
        "timeline_today": "All pro features unlocked. Your card is not charged.",
        "timeline_end_label_one": "Day 1",
        "timeline_end_label_other": "Day {{count}}",
        "timeline_end": "Billing starts. Cancel at any time before.",
        "managed_title": "This account is on {{name}}'s plan",
        "managed_description": "Ask them to move it to Pro — the change happens on their dashboard, not here.",
        "invoice_title": "Settle your invoice first",
        "invoice_description": "Pro comes back the moment the payment goes through."
      },
      "feature": {
        "default": {
          "title": "You're on the free plan",
          "description": "Unlimited automations and campaigns, every trigger and action, the AI Agent, and the integrations."
        },
        "trigger": {
          "title": "Every way someone can reach you",
          "description": "Pro fires on comments, on webhooks from your own site, and on forms from your landing page."
        },
        "action": {
          "title": "More than sending a message",
          "description": "Split-test a flow, call your own API, book a Calendly slot, or hand the conversation to your AI Agent."
        },
        "ai": {
          "title": "An agent that answers your DMs",
          "description": "It learns your products, writes in your voice, and hands the conversation back to you the moment it should."
        },
        "scenarios": {
          "title": "As many automations as you need",
          "description": "You've reached your limit of active scenarios. Go unlimited with Pro."
        },
        "campaigns": {
          "title": "As many campaigns as you need",
          "description": "Send more targeted and more frequent DM campaigns without limits."
        },
        "activations": {
          "title": "No monthly contact ceiling",
          "description": "Once the free plan's limit is reached, new contacts wait until the 1st to enter your automations."
        },
        "schedule_campaigns": {
          "title": "Send at the hour you pick",
          "description": "The free plan sends immediately; Pro sends when your audience actually opens their DMs."
        },
        "integrations": {
          "title": "Connect the rest of what you run",
          "description": "Shopify orders, Calendly bookings, Meta conversions, your own webhooks, plus the API and the MCP server."
        },
        "custom_domain": {
          "title": "Your links on your own domain",
          "description": "Every link you send carries your name, and your emails come from your address."
        },
        "members": {
          "title": "Up to 10 people on the account",
          "description": "Give someone the inbox without giving them your password."
        },
        "api": {
          "title": "The API and the MCP server",
          "description": "Build on your own DMs, or point an AI client straight at your contacts, automations and campaigns."
        },
        "payments": {
          "title": "Get paid inside the DM",
          "description": "Send a payment link in the conversation and take the payment there, with no checkout page."
        },
        "folders": {
          "title": "As many folders as you need",
          "description": "Sort contacts, automations and campaigns the way you work; the free plan stops at ten."
        }
      }
    },
    "activated_contacts_suffix": "used"
  },
  "surveys": {
    "title": "Surveys",
    "count_one": "1 survey",
    "count_other": "{{count}} surveys",
    "create": "Create survey",
    "create_first": "Create your first survey",
    "no_surveys": "No surveys yet",
    "search_surveys": "Search surveys",
    "select_survey": "Select a survey",
    "current_survey": "Current survey",
    "delete_confirm": "Delete this survey? All its data will be lost.",
    "new_survey": "Create new survey",
    "edit_survey": "Edit survey",
    "table": {
      "columns": {
        "title": "Title",
        "survey_type": "Type",
        "sent": "Sent",
        "completed": "Completed",
        "created_at": "Created"
      }
    },
    "limit_replies": "Accept only one answer per contact",
    "title_placeholder": "Title of the survey",
    "subtitle_placeholder": "Subtitle of the survey",
    "default_button_label": "Button",
    "add_image": "Add an image",
    "change_image": "Change image",
    "run_test": "Test survey",
    "run_test_confirm": "Send survey to this contact?",
    "view_results": "View results",
    "options": {
      "option_placeholder": ["Blue", "Red", "Green"],
      "add_to_folder": "On reply, add to folder",
      "and_reply": "and reply",
      "reply_placeholder": "Thanks for you reply!",
      "add_option": "Add option"
    },
    "questions": {
      "no_questions": "Your form is empty. Add questions to get started!",
      "footer_text_placeholder": "Add a footer text here. You can use Markdown syntax.",
      "add_question": "Add question",
      "question_number": "Question #{{number}}",
      "title_placeholder": "What's your name?",
      "count_one": "1 question",
      "count_other": "{{count}} questions",
      "submit": "Confirm",
      "save_to_property": "Save reply to contact property",
      "success_message": "Thanks for your reply!",
      "field_types": {
        "text": "Text",
        "email": "Email",
        "url": "URL",
        "phone": "Phone",
        "number": "Number",
        "date": "Date",
        "textarea": "Long text",
        "checkbox": "Checkbox",
        "multiple_choice": "Multiple choice"
      },
      "multiple_choice": {
        "accept_multiple": "Accept multiple answers",
        "add_option": "Add option"
      },
      "edit_replies": "Edit replies",
      "tabs": {
        "message": "Message",
        "form": "Form",
        "results": "Results",
        "integrations": "Integrations"
      },
      "message": {
        "preview": "Contacts will see this message in Instagram",
        "form_title": "Customize the message",
        "title": "Message title",
        "subtitle": "Message subtitle",
        "button": "Button label"
      },
      "results": {
        "completed_at": "Completed at",
        "export": "Export results"
      }
    },
    "preview": {
      "template": "Message",
      "form": "Form",
      "results": "Results"
    },
    "replies_count_one": "1 reply",
    "replies_count_other": "{{count}} replies",
    "survey_types": {
      "title": "Select a type of survey",
      "single_choice": {
        "title": "Single question",
        "description": "Ask only one question with a multiple choice answer",
        "preview": {
          "title": "What's your favorite color?",
          "subtitle": "Choose one of the following options",
          "options": ["Blue", "Red", "Green"]
        }
      },
      "questions": {
        "title": "Multiple questions",
        "description": "Ask multiple questions with different types of answers",
        "preview": {
          "title": "Register online",
          "subtitle": "Please answer a few questions",
          "questions": ["What's your name?", "What's your email?"]
        }
      }
    },
    "metrics": {
      "sent": "Sent",
      "replied": "Replied",
      "completed": "Completed",
      "completion_rate": "Completion rate"
    },
    "button_label_placeholder": "Button label"
  },
  "time": {
    "time": "Time",
    "days": "days",
    "earlier": "Earlier",
    "hours": "hours",
    "min": "min",
    "minutes": "minutes",
    "now": "Now",
    "seconds": "seconds",
    "this_month": "This month",
    "this_week": "This week",
    "today": "Today",
    "tomorrow": "Tomorrow",
    "weekdays": ["M", "T", "W", "T", "F", "S", "S"],
    "weeks": "weeks",
    "yesterday": "Yesterday"
  },
  "welcome_modal": {
    "title": "Hi there! Need any help?",
    "body": "You can watch a short video, explore around by yourself of get in touch with the team.",
    "watch_video": "Watch intro video",
    "chat": "Chat with the team",
    "explore": "I prefer to explore by myself",
    "ltd": {
      "title": "Welcome to your lifetime deal!",
      "pitch": "Everything below is yours for good, and the monthly allowances reset every month. Start automating!",
      "unlimited": "Unlimited",
      "agency": "Your tier includes the partner dashboard and white label. Manage every client account from one place.",
      "allowances": {
        "slots": "Accounts",
        "contacts": "Contacts / month",
        "ai_credits": "AI credits / month"
      },
      "open_partner": "Open your partner dashboard",
      "review": "Want more? Leave a review"
    }
  },
  "milestone_modal": {
    "title": {
      "agent_replies": "Congratulations! Your AI agent has answered {{number}} messages",
      "automation_runs": "Congratulations! Your automations have run {{number}} times",
      "link_clicks": "Congratulations! Your links have been clicked {{number}} times",
      "campaign_recipients": "Congratulations! Your campaigns have reached {{number}} contacts",
      "contact_properties_email": "Congratulations! You've collected {{number}} emails",
      "contact_properties_phone": "Congratulations! You've collected {{number}} phone numbers",
      "contact_properties_custom": "Congratulations! You've collected “{{property}}” for {{number}} contacts",
      "review": "Get more from your plan"
    },
    "body": "If it's saving you time, a 2-minute review helps other businesses find us.",
    "benefit_body": "A review on AppSumo adds this to your plan, for good:",
    "benefit": {
      "slots_one": "+{{count}} Instagram account",
      "slots_other": "+{{count}} Instagram accounts",
      "contacts": "+{{number}} contacts / month",
      "ai_credits": "+{{number}} AI credits / month"
    },
    "review": {
      "g2": "Review us on G2",
      "appsumo": "Review us on AppSumo"
    },
    "later": "Maybe later",
    "feedback": "Something not working? Tell us",
    "close": "Close"
  },
  "consent": {
    "step_title": "Choose your privacy settings",
    "accept_all": "Accept all",
    "continue": "Continue",
    "privacy_policy": "[View the full privacy policy](https://www.inro.social/legal/privacy-policy)",
    "necessary_title": "Strictly necessary",
    "necessary_description": "Required for the app to work: sign-in, security features and the support chat.",
    "analytics_title": "Usage analytics",
    "analytics_description": "Helps us understand how you use the app so we can improve it: navigation, clicks, errors, etc.",
    "marketing_title": "Advertising measurement",
    "marketing_description": "Tells our ad partners when you sign up or subscribe, so we can measure which campaigns work.",
    "save": "Save settings"
  },
  "switcher": {
    "your_organizations": "Accounts",
    "partner_organizations": "Partner accounts",
    "shared_billing_one": "Billed together",
    "shared_billing_other": "Billed together · {{count}} accounts",
    "partners": "Partners",
    "current": "Current",
    "create": {
      "billing_label": "Where should it be billed?",
      "submit": "Create account",
      "join": {
        "lifetime": "Add to my {{tier}}",
        "lifetime_slots": "Served by the same deal, sharing its monthly allowances. {{used}} of {{total}} accounts used.",
        "lifetime_description": "Served by the same deal, sharing its monthly allowances.",
        "partner": "Add to {{name}}",
        "partner_description": "Managed and billed by {{name}}, and listed in their dashboard alongside your other account.",
        "billing_account": "Add to my billing account",
        "billing_account_description_one": "Same card and invoices as {{name}}. Each account still has its own plan.",
        "billing_account_description_other": "Same card and invoices as the {{count}} accounts already on it. Each account still has its own plan."
      },
      "separate": "Bill it separately",
      "separate_description": "Its own card, invoices and plan. Nothing is shared with the account you are in.",
      "refusals": {
        "slot_limit": "Your deal covers {{limit}} accounts and they are all in use.",
        "no_payment_method": "This partner has no payment method on file, so nothing can be billed to them yet.",
        "no_subscription": "This partner has no active plan to put a new account on."
      }
    }
  },
  "partners": {
    "nav": {
      "dashboard": "Dashboard",
      "links": "Partner links",
      "team": "Team",
      "billing": "Billing",
      "settings": "Settings",
      "partner_account": "Partner account",
      "back_to_main_app": "Back to Inrō",
      "api": "API & MCP"
    },
    "payment_method": {
      "title": "No payment method",
      "description": "Add a payment method to create accounts and managed links. Existing accounts keep running.",
      "add": "Add a payment method"
    },
    "dashboard": {
      "title": "Partner dashboard",
      "placeholder": "Dashboard metrics coming soon.",
      "contacts_activated": "Contacts activated",
      "conversions": "Conversions",
      "create_organization": "Create account",
      "create": "Create",
      "new_org_placeholder": "Account name",
      "switch_to": "Open",
      "reconnect": "Reconnect",
      "period": "Period",
      "periods": {
        "last_7_days": "Last 7 days",
        "last_30_days": "Last 30 days",
        "last_3_months": "Last 3 months"
      },
      "help": {
        "contacts_activated": "Contacts who received a DM from a scenario or a campaign over the selected period.",
        "conversions": "Contacts who clicked a conversion link or replied to a survey over the selected period.",
        "new_contacts": "Contacts added across all your accounts over the selected period."
      },
      "connect": "Connect",
      "connection": {
        "status": {
          "connected": "Connected",
          "limited": "Limited",
          "error": "Error",
          "disconnected": "Disconnected",
          "restricted": "Restricted",
          "never_connected": "Not connected"
        },
        "detail": {
          "limited": "Some permissions are missing.",
          "error": "Instagram reported a problem.",
          "disconnected": "Automations are paused.",
          "restricted": "Instagram restricts sending."
        },
        "reason": {
          "expired": "Session expired",
          "invalidated": "Session revoked",
          "deauthorized": "App access removed",
          "locked": "Locked by Meta",
          "malformed": "Invalid session"
        }
      },
      "new_contacts": "New contacts",
      "metrics": {
        "followers": "Followers",
        "contacts": "Contacts",
        "automations": "Automations"
      }
    },
    "settings": {
      "title": "Partner settings",
      "subtitle": "Manage your partner identity and branding.",
      "name": "Partner name",
      "email": "Contact email",
      "logo": "Logo",
      "upload_logo": "Upload new logo",
      "remove_logo": "Remove",
      "remove_logo_confirm": "Remove the partner logo? Signup and onboarding pages opened from your links will show the Inrō logo again.",
      "logo_hint": "PNG, JPG or SVG. Square images work best.",
      "logo_description": "Replaces the Inrō logo on the signup and onboarding pages opened from your partner links, and identifies your workspace in the account switcher.",
      "name_description": "Shown in the account switcher, and used to build your referral link code and its campaign tracking parameters.",
      "email_description": "Where we reach you about your partner account, and the billing email of the Stripe customer the accounts you create are charged through. It is not shown to the accounts you refer.",
      "saved": "Settings saved"
    },
    "api_access": {
      "title": "API & MCP",
      "subtitle": "One token for every account in your workspace.",
      "token": "Partner API token",
      "token_notice": "Anyone with this token can read and change every account in your workspace. Treat it like a password.",
      "regenerate": "Regenerate",
      "regenerated": "New token generated",
      "regenerate_confirm_title": "Regenerate the partner API token?",
      "regenerate_confirm_message": "The current token stops working immediately. Any integration or MCP connector using it will need the new one.",
      "mcp_title": "Connect an MCP client",
      "mcp_step_url": "Add a custom connector with this URL:",
      "mcp_step_auth": "Set Authentication to “None”.",
      "mcp_step_auth_why": "The client will suggest OAuth because it detects one on this domain. Don't use it: that sign-in authorizes a single Instagram account, not your whole workspace.",
      "mcp_step_header": "Add a request header with this name and value, and mark it required:",
      "api_title": "REST API",
      "api_headers": "Send your token as “Authorization: Bearer …”. Add “X-Inro-Organization” with an account id or Instagram username to say which account a request is for.",
      "documentation": "View API documentation"
    },
    "placeholder": {
      "coming_soon": "Coming soon."
    },
    "team": {
      "title": "Team",
      "subtitle": "Manage who can access the {{name}} partner workspace.",
      "invite_label": "Invite a team member",
      "invite_placeholder": "colleague@example.com",
      "invite_action": "Send invite",
      "invite_hint": "Existing Inrō users are added instantly. New users receive an email to set a password.",
      "invite_sent": "Invitation sent",
      "already_member": "This user is already a member.",
      "last_member": "You can't remove the last member of the workspace.",
      "remove": "Remove member",
      "remove_confirm": "Remove {{email}} from this partner workspace?"
    },
    "links": {
      "title": "Partner links",
      "subtitle": "Manage your signup and onboarding links.",
      "create": "New link",
      "type": "Link type",
      "types": {
        "referral": "Referral",
        "managed": "Managed"
      },
      "discount_code": "Discount code",
      "trial_days": "Trial days",
      "save": "Save",
      "copied": "Link copied to clipboard",
      "organizations": "Accounts",
      "paid": "Paying",
      "edit": "Edit link",
      "remove": "Delete link",
      "remove_confirm": "Delete the link \"{{code}}\"?",
      "empty": "No links yet.",
      "new_title": "New partner link",
      "edit_title": "Edit partner link",
      "modal_description": "Choose how new accounts reach Inrō and what they get when they sign up.",
      "type_descriptions": {
        "referral": "Sends people to the Inrō signup page. They create their own account and affiliate revenue is attributed to you.",
        "managed": "Sends the user straight to Instagram connection to authorize access to their account."
      },
      "type_locked": "A link type can't be changed after creation. Create a new link to use the other type.",
      "url": "Link to share",
      "url_description": "Anyone opening this URL is attributed to your partner account.",
      "discount_code_description": "Stripe promotion code applied automatically at checkout for accounts signing up through this link. Leave empty for no discount.",
      "discount_code_placeholder": "PARTNER20",
      "trial_days_description": "Free trial days for accounts signing up through this link (0 to {{max}}).",
      "trial_days_count": "{{count}} days",
      "default_template": "Automation template",
      "default_template_description": "Scenario duplicated into every account this link creates, and activated as soon as Instagram is connected. Pick from the scenarios in your accounts.",
      "no_default_template": "No template",
      "loading_scenarios": "Loading scenarios…",
      "untitled_scenario": "Untitled scenario",
      "scenarios_error": "Scenarios could not be loaded.",
      "hide_template_disclaimer": "Hide the template notice",
      "hide_template_disclaimer_description": "The Instagram connection page tells the user which automation template is being set up for them. Turn this on to hide that notice.",
      "managed_billing_notice": "Accounts created through a managed link are on your partner plan from the start — no trial and no coupon.",
      "organizations_title": "Accounts from this link",
      "organizations_description": "Everyone who signed up through {{code}}. These accounts bill themselves and are not yours to manage.",
      "organizations_empty": "Nobody has signed up through this link yet.",
      "organization_paid": "Paid",
      "organization_free": "Free",
      "table": {
        "link": "Link",
        "type": "Type",
        "organizations": "Accounts",
        "paid": "Paying",
        "discount_code": "Discount",
        "trial_days": "Trial",
        "created_at": "Created"
      }
    },
    "billing": {
      "title": "Billing & usage",
      "subtitle": "One plan for {{name}} and every account you manage.",
      "plan": "Partner plan",
      "renews_on": "Renews on {{date}}",
      "activated_contacts": "Activated contacts",
      "portal_title": "Stripe billing portal",
      "portal_description": "Manage your payment methods, invoices and subscription in Stripe.",
      "open_portal": "Open billing portal",
      "no_account": "No billing account is set up for this partner yet.",
      "usage_by_organization": "Usage by account",
      "usage_by_organization_description": "Activations are billed as one total across every account you manage.",
      "usage_by_organization_lifetime": "Allowances are shared across every account you manage, and renew every month.",
      "no_organizations": "You are not managing any account yet.",
      "resubscribe_title": "Nothing is billing right now",
      "resubscribe_description": "Start a new plan to bring every account you manage back online.",
      "resubscribe_cta": "Subscribe"
    },
    "plan_gate": {
      "title": "Start your partner plan",
      "subtitle": "{{name}} needs an active plan before you can manage accounts.",
      "included": {
        "accounts": "Create and manage as many client accounts as you want",
        "usage": "One usage total across every account, billed once",
        "billing": "A single invoice, no per-account subscriptions",
        "support": "Partner dashboard, managed links and the partner API"
      },
      "subscribe": "Subscribe",
      "disclaimer": "You pay the partner fee from today. The usage plan is added the first time one of your accounts connects Instagram."
    }
  },
  "request_logs": {
    "title": "API & MCP logs",
    "search": "Search logs",
    "empty": "No requests logged yet",
    "sources": {
      "grape_api": "API",
      "mcp": "MCP"
    },
    "status": {
      "ok": "OK",
      "error": "Error"
    },
    "filters": {
      "all_sources": "All sources",
      "all_statuses": "All statuses",
      "only_success": "Successful only",
      "only_errors": "Errors only"
    },
    "table": {
      "columns": {
        "source": "Source",
        "request": "Request",
        "status": "Status",
        "duration_ms": "Duration",
        "created_at": "Date"
      }
    },
    "detail": {
      "title": "Request details",
      "method": "HTTP method",
      "path": "Path",
      "route": "Route",
      "mcp_method": "MCP method",
      "tool_name": "Tool",
      "action": "Action",
      "duration": "Duration",
      "created_at": "Date",
      "ip_address": "IP address",
      "user_agent": "User agent",
      "error": "Error",
      "params": "Parameters"
    }
  },
  "media_upload": {
    "title": "Upload your file",
    "requested_by": "{{client}} asked for a file to finish what you started in your chat.",
    "requested_generic": "An app connected to your Inrō account needs a file upload",
    "for_image": "It's expecting an image.",
    "for_video": "It's expecting a video.",
    "for_audio": "It's expecting an audio file.",
    "for_file": "It's expecting a file.",
    "drop_here": "Drag a file here",
    "or": "or",
    "choose_from_library": "Choose from your library",
    "max_size": "Up to {{max}}",
    "expires_in": "This link expires {{when}}.",
    "expired_title": "This link has expired",
    "expired_message": "Upload links are single-use and short-lived. Go back to your application and request a new one.",
    "uploading": "Uploading…",
    "finishing": "Finishing up…",
    "success_title": "File received",
    "success_message": "You're done here. Go back to your app and continue your work.",
    "errors": {
      "too_large": "That file is too large. The limit is {{max}}.",
      "wrong_type": "That doesn't look like the right kind of file.",
      "failed": "The upload didn't complete. Check your connection and try again."
    }
  },
  "oauth": {
    "connect": {
      "signup_title": "Create your Inrō account",
      "signup_description": "Create your account to finish connecting {{app}} to Inrō.",
      "signup_description_generic": "Create your account to finish connecting Inrō.",
      "login_title": "Welcome back",
      "login_description": "Log in to finish connecting {{app}} to Inrō.",
      "login_description_generic": "Log in to finish connecting Inrō.",
      "app_icon_alt": "{{app}} logo",
      "logo_alt": "Inrō"
    }
  },
  "instagram_preview": {
    "active_now": "Active now",
    "followers_one": "1 follower",
    "followers_other": "{{formattedCount}} followers",
    "business_account": "Business account",
    "view_profile": "View profile",
    "message_placeholder": "Message…"
  },
  "batch_tasks": {
    "status": {
      "pending": "Starting…",
      "processing": "In progress…",
      "paused": "Paused",
      "completed": "Completed",
      "failed": "Stopped",
      "canceled": "Canceled"
    },
    "progress": "{{done}} of {{total}}",
    "analyzed": "Analyzed",
    "processed": "Processed",
    "executed": "Executed",
    "failed": "Failed",
    "detected": "Detected",
    "not_detected": "Not detected",
    "detected_waiting": "Contacts appear here as values are detected.",
    "detected_none": "No values found.",
    "stopped": "Stopped before the end.",
    "stopped_credits": "Stopped: no AI credits left. Try again once they renew.",
    "retry": "Try again",
    "show_more": "Show more",
    "open": {
      "campaign_send": "View campaign",
      "retroactive_automation": "View automation"
    },
    "sidebar_group_one": "{{count}} task",
    "sidebar_group_other": "{{count}} tasks",
    "types": {
      "campaign_send": "Campaign",
      "contact_bulk_update": "Contact update",
      "retroactive_automation": "Automation on existing",
      "contact_property_detection": "Property detection"
    }
  },
  "batch_task_logs": {
    "title": "Batch tasks",
    "empty": "No batch tasks yet",
    "filters": {
      "all_types": "All types",
      "all_statuses": "All statuses"
    },
    "table": {
      "columns": {
        "title": "Task",
        "task_type": "Type",
        "status": "Status",
        "progress": "Progress",
        "created_at": "Started"
      }
    }
  }
}
